import React, { useState, useEffect, useMemo, useCallback } from 'react';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';
import { useScenario, useEntityFilter, useTagFilter, useCurrencyFilter, useCalculationType, useStateFilter, useFilingCodeFilter } from '../components/layout/Layout';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Typography, Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert, LinearProgress, Autocomplete, IconButton, Radio, RadioGroup, FormControlLabel, FormControl, Grid, FormHelperText, InputLabel, Select, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import EnhancedDataTable from '../components/EnhancedDataTable';
import SearchableDataTable from '../components/SearchableDataTable';
import api from '../services/api';
import { adjustmentTypeToRouteMap } from '../utils/adjustmentTypeUtils';
import {
  annotateDeferredDrilldowns,
  annotatePivotDrilldowns,
  attachDrilldownLineage,
  canDrillIntoTotalCell,
  getDeferredActivityTypes
} from '../utils/calculationDrilldownUtils';
import '../styles/EntityInputs.css';

const STATE_CALCULATIONS_COLUMNS = [
{ field: 'EntityCode', headerName: 'Entity', minWidth: 120 },
{ field: 'EntityName', headerName: 'Entity Name', minWidth: 180, flex: 1 },
{ field: 'AdjustmentCode', headerName: 'Adjustment Code', minWidth: 140 },
{ field: 'AdjustmentName', headerName: 'Adjustment Name', width: 600 },
{ field: 'Tag', headerName: 'Tag', minWidth: 120 },
{ field: 'LocalCurrencyInput', headerName: 'Local Currency Input', minWidth: 160, type: 'number' },
{ field: 'USDInput', headerName: 'USD Input', minWidth: 120, type: 'number' },
{ field: 'TaxEffectedLocalAmount', headerName: 'Tax-Effected Local Amount', minWidth: 180, type: 'number' },
{ field: 'TaxEffectedUSDAmount', headerName: 'Tax-Effected USD Amount', minWidth: 180, type: 'number' },
{ field: 'AdjustmentSource', headerName: 'Adjustment Source', minWidth: 140 }];


// Column definitions for the selection dialog table - matching AdjustmentInputs style
const getSelectionDialogColumns = (handleEdit, handleDelete) => [
{
  field: 'AdjustmentCode',
  headerName: 'ADJUSTMENT CODE',
  width: 280,
  renderCell: (params) => {
    const displayValue = params.row.AdjustmentName ?
    `${params.value} - ${params.row.AdjustmentName}` :
    params.value;
    return <strong>{displayValue}</strong>;
  }
},
{
  field: 'EntityCode',
  headerName: 'ENTITY CODE',
  width: 120
},
{
  field: 'EntityName',
  headerName: 'ENTITY NAME',
  width: 150,
  renderCell: (params) =>
  <Typography variant="body2">{params.value || ''}</Typography>

},
{
  field: 'ActivityType',
  headerName: 'ACTIVITY TYPE',
  width: 160
},
{
  field: 'FilingCode',
  headerName: 'FILING CODE',
  width: 120
},
{
  field: 'Amount',
  headerName: 'AMOUNT',
  width: 130,
  valueFormatter: (params) => {
    if (params.value == null) return '';
    const value = params.value;
    const roundedValue = Math.round(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(roundedValue);
  }
},
{
  field: 'USDAmount',
  headerName: 'USD AMOUNT',
  width: 130,
  renderCell: (params) =>
  <Typography variant="body2">
        {params.value != null ? (() => {
      const roundedValue = Math.round(params.value);
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(roundedValue);
    })() : ''}
      </Typography>

},
{
  field: 'Tag',
  headerName: 'TAG',
  width: 120
},
{
  field: 'SubTag',
  headerName: 'SUB TAG',
  width: 100
},
{
  field: 'AdjustmentType',
  headerName: 'TYPE',
  width: 140
},
{
  field: 'actions',
  headerName: 'ACTIONS',
  width: 100,
  sortable: false,
  renderCell: (params) =>
  <Box>
        <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        handleEdit(params.row);
      }}
      title="Edit Adjustment">

          <img src="/assets/Icons/icon_pencil.svg" alt="Edit" style={{ width: 18, height: 18 }} />
        </IconButton>
        <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        handleDelete(params.row);
      }}
      title="Delete Adjustment">

          <img src="/assets/Icons/icon_trash.svg" alt="Delete" style={{ width: 18, height: 18 }} />
        </IconButton>
      </Box>

}];


// Deferred rollforward table headers
const DEFERRED_ROLLFORWARD_HEADERS = [
'GAAP TO STAT TEMPORARY DIFFERENCES',
'STAT TO TAX TEMPORARY DIFFERENCES',
'STATE PRE-APPORTIONMENT TEMPORARY DIFFERENCES',
'STATE PRE-APPORTIONMENT NOL',
'STATE PRE-APPORTIONMENT VALUATION ALLOWANCES',
'STATE POST-APPORTIONED TEMPORARY DIFFERENCES',
'STATE POST-APPORTIONMENT NOL',
'STATE POST-APPORTIONED VALUATION ALLOWANCE',
'STATE TAX-EFFECTED TEMPORARY DIFFERENCES',
'STATE TAX-EFFECTED VALUATION ALLOWANCE'];


// Activity types for deferred rollforward columns
const ACTIVITY_TYPES = [
'BeginningBalance',
'ReturnToProvision',
'DeferredTrueUp',
'PurchaseAccounting',
'AdjBeginningBalance',
'CurrentActivityPL',
'OCIAdjustment',
'DeferredExpAdjustment',
'BalanceSheetAdjustment',
'CustomDeferredExpAdjustment',
'CustomBalanceSheetAdjustment'];

// State-specific sections are scoped by filing code; federal sections are scoped by entity.
const STATE_SPECIFIC_SECTION_TYPES = [
'State Pre-App Perm',
'State Pre-App Temp',
'State Pre-App NOL',
'State Post-App Perm',
'State Post-App Temp',
'State Post-App NOL',
'State Tax Effected Temp',
'State Tax Adj. Return',
'State Tax Adj. Provision',
'State Tax Adj. RTP'];

const enableTotalCellDrilldown = (columns, handleCellClick) =>
columns.map((column) => {
  if (!column.renderCell || column.field === 'AdjustmentName') return column;
  const renderCell = column.renderCell;

  return {
    ...column,
    renderCell: (params) => {
      const renderedCell = renderCell(params);
      if (!canDrillIntoTotalCell(params) || !React.isValidElement(renderedCell)) {
        return renderedCell;
      }

      return React.cloneElement(renderedCell, {
        onClick: (event) => {
          event.stopPropagation();
          handleCellClick({ ...params, drilldownKeys: column.drilldownKeys });
        },
        title: 'View contributing adjustment inputs',
        sx: {
          ...(renderedCell.props.sx || {}),
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: '#e3f2fd',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }
        }
      });
    }
  };
});


const normalizeRate = (rate) => {
  const value = parseFloat(rate);
  if (!Number.isFinite(value)) return 0;
  return value > 1 ? value / 100 : value;
};

const getDeferredApportionmentRate = (stateInput, activityType) => {
  if (!stateInput) return 0;

  if (activityType === 'BeginningBalance') {
    const beginningRate = stateInput.BeginningApportionmentRate;
    if (beginningRate !== null && beginningRate !== undefined && beginningRate !== '') {
      return normalizeRate(beginningRate);
    }
  }

  return normalizeRate(stateInput.CurrentApportionmentRate);
};


// Tag and SubTag options - stored on frontend as temporary solution
const TAG_OPTIONS = [
'ORDINARY',
'DISCRETE',
'RTP',
'AUDIT',
'OCI',
'PPA',
'Deferred Only',
'Deferred Only PY',
'Balance Sheet Only',
'Balance Sheet Only PY',
'Valuation Allowance',
'Valuation Allowance PY',
'Uncertain Tax Position',
'Uncertain Tax Position PY'];


const SUBTAG_OPTIONS = [
'Import',
'Auto',
'Manual',
'Workpaper',
'Q1',
'Q2',
'Q3',
'Q4',
'2024',
'2023',
'2022'];


const DECIMAL_NUMBER_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const ROUNDED_NUMBER_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

// Utility function to format numbers with parentheses for negatives
const formatNumberWithParentheses = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return value;
  }

  const absValue = Math.abs(numValue);
  const formattedValue = DECIMAL_NUMBER_FORMATTER.format(absValue);

  return numValue < 0 ? `(${formattedValue})` : formattedValue;
};

// Helper to format numbers with commas for display
const formatNumberWithCommas = (value) => {
  if (!value) return '';
  // Remove any existing commas and non-numeric characters except decimal point and minus sign
  const numericValue = value.toString().replace(/[^\d.-]/g, '');
  // Split into integer and decimal parts
  const parts = numericValue.split('.');
  // Add commas to integer part
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  // Return formatted number
  return parts.join('.');
};

// Helper to remove commas from formatted number
const removeCommas = (value) => {
  if (!value) return '';
  return value.toString().replace(/,/g, '');
};

// Utility function to format rounded numbers with parentheses for negatives
const formatRoundedNumberWithParentheses = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return value;
  }

  const absValue = Math.abs(Math.round(numValue));
  const formattedValue = ROUNDED_NUMBER_FORMATTER.format(absValue);

  return numValue < 0 ? `(${formattedValue})` : formattedValue;
};

const defaultCalculation = {
  EntityCode: '',
  EntityName: '',
  AdjustmentCode: '',
  AdjustmentName: '',
  Tag: '',
  LocalCurrencyInput: '',
  USDInput: '',
  TaxEffectedLocalAmount: '',
  TaxEffectedUSDAmount: '',
  AdjustmentSource: ''
};

// Helper to get Activity Type options based on adjustment type
const getActivityTypeOptions = (adjustmentType) => {
  // Permanent types that can use CurrentActivityEQ
  const permanentTypes = ['Permanent', 'GS Permanent', 'TI Permanent'];

  // Group 1: Current activity types (PL only, except for permanent types)
  const group1 = [
  'Pretax',
  'Permanent',
  'Payable',
  'GS Permanent',
  'TI Permanent',
  'Tax Adj. Return',
  'Tax Adj. Provision',
  'Tax Adj. RTP'];


  // Group 2: Deferred/balance sheet activity types
  const group2 = [
  'Temporary',
  'GS Temporary',
  'TI Temporary',
  'Tax Effected Temporary',
  'Valuation Allowance',
  'Net Operating Loss',
  'Tax Effected Valuation Allowance'];


  if (!adjustmentType) return [];

  if (group1.includes(adjustmentType)) {
    // Only Permanent, GS Permanent, and TI Permanent can use CurrentActivityEQ
    if (permanentTypes.includes(adjustmentType)) {
      return ['CurrentActivityPL', 'CurrentActivityEQ'];
    }
    // All other group1 types only get CurrentActivityPL
    return ['CurrentActivityPL'];
  }

  if (group2.includes(adjustmentType)) {
    return [
    'BeginningBalance',
    'ReturnToProvision',
    'DeferredTrueUp',
    'PurchaseAccounting',
    'CurrentActivityPL',
    'OCIAdjustment',
    'DeferredExpAdjustment',
    'BalanceSheetAdjustment',
    'CustomDeferredExpAdjustment',
    'CustomBalanceSheetAdjustment'];

  }

  return [];
};

const StateCalculations = () => {
  // Check library availability on component mount
  const checkLibraryAvailability = () => {
    try {
      if (window.XLSX && window.XLSX.utils) {

        // library available
      }} catch (error) {
      console.error('Error checking XLSX methods:', error);
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    try {
      // Check if libraries are loaded
      if (typeof window.XLSX === 'undefined' || typeof window.saveAs === 'undefined') {
        console.error('Libraries missing - XLSX:', typeof window.XLSX, 'saveAs:', typeof window.saveAs);
        setNotification({
          open: true,
          message: 'Excel export is not available. Please ensure XLSX and FileSaver are loaded.',
          severity: 'warning'
        });
        return;
      }

      // Get the formatted data that matches what's displayed in the table
      let dataToExport = selectedRows.length > 0 ? rows.filter((row) => selectedRows.includes(row.id)) : rows;

      // Transform the pivot table data into a more Excel-friendly format
      const excelData = dataToExport.map((row) => {
        const excelRow = {};

        // Handle section headers and totals
        if (row.isSection) {
          excelRow['Type'] = 'SECTION HEADER';
          excelRow['Description'] = row.AdjustmentName;
          return excelRow;
        } else if (row.isSubFooter) {
          excelRow['Type'] = 'SECTION TOTAL';
          excelRow['Description'] = row.AdjustmentName;
        } else if (row.isCalculatedTotal) {
          excelRow['Type'] = 'CALCULATED TOTAL';
          excelRow['Description'] = row.AdjustmentName;
        } else {
          excelRow['Type'] = 'ADJUSTMENT';
          excelRow['Adjustment Code'] = row.AdjustmentCode || '';
          excelRow['Description'] = row.AdjustmentName || '';
        }

        // Add entity columns (dynamically based on what entities are present)
        Object.keys(row).forEach((key) => {
          // Skip internal properties and already handled fields
          if (!key.startsWith('is') && key !== 'id' && key !== 'AdjustmentCode' && key !== 'AdjustmentName') {
            // Check if this looks like an entity code (numeric or specific pattern)
            if (key.match(/^\d+$/) || key.length <= 10) {
              excelRow[key] = row[key];
            }
          }
        });

        return excelRow;
      });

      if (excelData.length === 0) {

        setNotification({ open: true, message: 'No data to export', severity: 'warning' });
        return;
      }

      const ws = window.XLSX.utils.json_to_sheet(excelData);
      const wb = window.XLSX.utils.book_new();
      window.XLSX.utils.book_append_sheet(wb, ws, 'StateCalculations');
      const wbout = window.XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      window.saveAs(blob, 'state_calculations.xlsx');
      setNotification({ open: true, message: 'Export successful', severity: 'success' });
    } catch (error) {
      console.error('=== Excel Export Debug End - ERROR ===');
      console.error('Export error details:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      setNotification({
        open: true,
        message: `Failed to export state calculations: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const { selectedScenario } = useScenario();
  const { selectedEntity } = useEntityFilter();
  const { selectedTag } = useTagFilter();
  const { selectedCurrency } = useCurrencyFilter();
  const { stateCalculationsCalculationType: calculationType } = useCalculationType();
  const { selectedState } = useStateFilter();
  const { selectedFilingCode } = useFilingCodeFilter();
  const selectedScenarioCode = selectedScenario?.scenarioCode;

  const [allRows, setAllRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityGroups, setEntityGroups] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRow, setCurrentRow] = useState(defaultCalculation);
  const [currentAdjustment, setCurrentAdjustment] = useState({
    id: '',
    AdjustmentId: '',
    EntityCode: '',
    EntityName: '',
    SubEntity: '',
    AdjustmentCode: '',
    AdjustmentName: '',
    ActivityType: '',
    Amount: '',
    USDAmount: '',
    Tag: '',
    SubTag: '',
    AdjustmentType: '',
    ScenarioCode: '',
    PointInTime: '',
    ModifiedBy: 'admin'
  });
  const [entityName, setEntityName] = useState('');
  const [adjustmentName, setAdjustmentName] = useState('');
  const [pointInTimes, setPointInTimes] = useState([]);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [selectedRows, setSelectedRows] = useState([]);
  const [masterEntities, setMasterEntities] = useState([]);
  const [masterAdjustments, setMasterAdjustments] = useState([]);
  const [entityInputs, setEntityInputs] = useState([]);
  const [stateInputs, setStateInputs] = useState([]);
  const [entityLoading, setEntityLoading] = useState(false);
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);
  const [entityInputLoading, setEntityInputLoading] = useState(false);
  const [masterStates, setMasterStates] = useState([]);
  const [stateInputLoading, setStateInputLoading] = useState(false);
  const [selectionDialogOpen, setSelectionDialogOpen] = useState(false);
  const [matchingAdjustments, setMatchingAdjustments] = useState([]);
  const [etrEntityGroup, setEtrEntityGroup] = useState('all'); // 'all', 'entity'
  const [etrStateGroup, setEtrStateGroup] = useState('state-filing'); // 'total', 'state', 'state-filing'
  const [expandedGroups, setExpandedGroups] = useState({}); // Track which groups are expanded: { 'PA': true, 'PA-E001': true, ... }

  // Deferred rollforward breakout controls
  const [deferredBreakout1, setDeferredBreakout1] = useState('entity'); // 'all', 'entity', 'group'
  const [deferredBreakout2, setDeferredBreakout2] = useState('total'); // 'total', 'state', 'filing'
  const [topRowsCollapsed, setTopRowsCollapsed] = useState(true); // collapse rows above FEDERAL TAXABLE INCOME BEFORE ADJUSTMENTS in current-provision
  const hasCalculationRows = allRows.length > 0;

  // Fetch master entities
  const fetchMasterEntities = async () => {
    setEntityLoading(true);
    try {
      const response = await api.get('/entity-models/master');
      if (response.data && response.data.data) {
        setMasterEntities(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching master entities:', error);
      setNotification({ open: true, message: 'Failed to fetch entities', severity: 'error' });
    } finally {
      setEntityLoading(false);
    }
  };

  // Fetch master states (for FilingCode → entityList mapping)
  const fetchMasterStates = async () => {
    try {
      const response = await api.get('/states/master');
      if (response.data && response.data.data) {
        setMasterStates(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching master states:', error);
    }
  };

  // Fetch entity groups
  const fetchEntityGroups = async () => {
    try {
      const response = await api.get('/entity-groups');
      if (response.data && response.data.data) {
        setEntityGroups(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching entity groups:', error);
    }
  };

  // Fetch master adjustments (for lookup purposes)
  const fetchMasterAdjustments = async () => {
    setAdjustmentLoading(true);
    try {
      const response = await api.get('/adjustments/master');
      if (response.data && response.data.data) {
        setMasterAdjustments(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching master adjustments:', error);
      setNotification({ open: true, message: 'Failed to fetch master adjustments', severity: 'error' });
    } finally {
      setAdjustmentLoading(false);
    }
  };

  // Precomputed O(1) lookup maps — rebuilt only when source arrays change, not on every render
  const entityInputMapByKey = useMemo(() => {
    const map = new Map();
    entityInputs.forEach((ei) => {
      const key = `${ei.EntityCode}__${ei.ScenarioCode}`;
      if (!map.has(key)) map.set(key, ei); // first match wins — identical to .find() behavior
    });
    return map;
  }, [entityInputs]);

  const stateInputMapByKey = useMemo(() => {
    const map = new Map();
    stateInputs.forEach((si) => {
      const key = `${si.FilingCode}__${si.EntityCode}__${si.ScenarioCode}`;
      if (!map.has(key)) map.set(key, si);
    });
    return map;
  }, [stateInputs]);

  const masterEntityCodeById = useMemo(() => {
    const map = new Map();
    masterEntities.forEach((entity) => {
      if (entity.id) map.set(entity.id, entity.EntityCode);
    });
    return map;
  }, [masterEntities]);

  // Helper function to get tax rate for an entity from EntityInput
  const getTaxRateForEntity = (entityCode, scenarioCode = null) => {
    const entityInput = scenarioCode ?
    entityInputMapByKey.get(`${entityCode}__${scenarioCode}`) :
    entityInputs.find((ei) => ei.EntityCode === entityCode);

    // Get CurrentTaxRate from EntityInput, default to 0 if not found
    const taxRate = entityInput?.CurrentTaxRate || 0;
    // Convert to decimal if it's stored as percentage (>1)
    return taxRate > 1 ? taxRate / 100 : taxRate;
  };

  // Helper function to get tax rate for a specific activity type in deferred rollforward
  const getTaxRateForActivityType = (entityCode, activityType, scenarioCode = null) => {
    const entityInput = scenarioCode ?
    entityInputMapByKey.get(`${entityCode}__${scenarioCode}`) :
    entityInputs.find((ei) => ei.EntityCode === entityCode);

    if (!entityInput) return 0;

    let taxRate = 0;

    // Apply specific tax rate based on activity type
    switch (activityType) {
      case 'BeginningBalance':
        // Beginning balance uses begin deferred tax rate
        taxRate = entityInput.BeginningDeferredTaxRate || 0;
        break;

      case 'ReturnToProvision':
        // Return to provision uses prior year tax rate, or begin deferred if no prior year
        taxRate = entityInput.PriorCurrentTaxRate || entityInput.BeginningDeferredTaxRate || 0;
        break;

      case 'DeferredTrueUp':
      case 'PurchaseAccounting':
        // Deferred true up and purchase accounting use begin deferred tax rate
        taxRate = entityInput.BeginningDeferredTaxRate || 0;
        break;

      case 'CurrentActivityPL':
        // Current activity uses current tax rate
        taxRate = entityInput.CurrentTaxRate || 0;
        break;

      case 'OCIAdjustment':
      case 'DeferredExpAdjustment':
      case 'BalanceSheetAdjustment':
      case 'CustomDeferredExpAdjustment':
      case 'CustomBalanceSheetAdjustment':
        // All other activity types use end deferred tax rate
        taxRate = entityInput.EndingDeferredTaxRate || 0;
        break;

      default:
        // Default to 0 for unknown activity types or AdjBeginningBalance (which is a total)
        taxRate = 0;
        break;
    }

    // Convert to decimal if it's stored as percentage (>1)
    return taxRate > 1 ? taxRate / 100 : taxRate;
  };

  // Get FX rate for an entity from EntityInput data based on activity type
  const getFXRateForEntity = (entityCode, activityType) => {
    if (!entityCode) return null;
    const entityInput = entityInputMapByKey.get(`${entityCode}__${selectedScenario?.scenarioCode}`);
    if (!entityInput) return null;

    let fxRate = null;

    // Determine which rate to use based on activity type
    // Beginning Balance and Return to Provision use Beginning Spot Rate
    if (activityType === 'BeginningBalance' || activityType === 'ReturnToProvision') {
      fxRate = entityInput.BeginningSpotRate;
    }
    // Current Activity PL and EQ use Average FX Rate
    else if (activityType === 'CurrentActivityPL' || activityType === 'CurrentActivityEQ') {
      fxRate = entityInput.AverageFXRate;
    }
    // All other activity types use Ending Spot Rate
    else {
      fxRate = entityInput.EndingSpotRate;
    }

    if (!fxRate) return null;

    const parsedRate = parseFloat(fxRate);
    return Number.isFinite(parsedRate) ? parsedRate : null;
  };

  // Handle local currency amount change with automatic USD conversion
  const handleAmountChange = (value) => {
    // Remove commas for calculation
    const numericValue = removeCommas(value);
    const fxRate = getFXRateForEntity(currentAdjustment.EntityCode, currentAdjustment.ActivityType);
    let usdAmount = '';

    // Skip automatic conversion for pretax adjustments
    const isPretax = currentAdjustment.AdjustmentType === 'Pretax' || currentAdjustment.AdjustmentType === 'PretaxAdjustment';

    if (numericValue && fxRate && !isPretax) {
      // USD = Local / FX Rate
      usdAmount = (parseFloat(numericValue) / fxRate).toFixed(2);
    }

    setCurrentAdjustment({
      ...currentAdjustment,
      Amount: numericValue,
      USDAmount: isPretax ? currentAdjustment.USDAmount : usdAmount
    });
  };

  // Handle USD amount change with automatic local currency conversion
  const handleUSDAmountChange = (value) => {
    // Remove commas for calculation
    const numericValue = removeCommas(value);
    const fxRate = getFXRateForEntity(currentAdjustment.EntityCode, currentAdjustment.ActivityType);
    let localAmount = '';

    // Skip automatic conversion for pretax adjustments
    const isPretax = currentAdjustment.AdjustmentType === 'Pretax' || currentAdjustment.AdjustmentType === 'PretaxAdjustment';

    if (numericValue && fxRate && !isPretax) {
      // Local = USD * FX Rate
      localAmount = (parseFloat(numericValue) * fxRate).toFixed(2);
    }

    setCurrentAdjustment({
      ...currentAdjustment,
      Amount: isPretax ? currentAdjustment.Amount : localAmount,
      USDAmount: numericValue
    });
  };

  // Fetch entity inputs (for current tax rates)
  const fetchEntityInputs = async () => {
    if (!selectedScenario) {
      return;
    }
    setEntityInputLoading(true);
    try {
      const response = await api.get('/entity-models/input', {
        params: { scenarioCode: selectedScenario.scenarioCode }
      });
      if (response.data) {
        const data = Array.isArray(response.data) ? response.data : response.data.data || [];
        setEntityInputs(data);
      }
    } catch (error) {
      console.error('Error fetching entity inputs:', error);
      setNotification({ open: true, message: 'Failed to fetch entity inputs', severity: 'error' });
    } finally {
      setEntityInputLoading(false);
    }
  };

  // Fetch state inputs (for apportionment rates)
  const fetchStateInputs = async () => {
    if (!selectedScenario) {
      return;
    }
    setStateInputLoading(true); // Set stateInputLoading to true
    try {
      const response = await api.get('/states/input', {
        params: { scenarioCode: selectedScenario.scenarioCode }
      });
      if (response.data) {
        const data = Array.isArray(response.data) ? response.data : response.data.data || [];
        setStateInputs(data);
      }
    } catch (error) {
      console.error('Error fetching state inputs:', error);
      setNotification({ open: true, message: 'Failed to fetch state inputs', severity: 'error' });
    } finally {
      setStateInputLoading(false); // Set stateInputLoading to false
    }
  };

  // Fetch state calculations data (from adjustment inputs)
  const fetchStateCalculations = async () => {
    if (!selectedScenario) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const scenarioCode = selectedScenario.scenarioCode;

      // Single batched request replaces ~30 per-type round trips.
      const response = await api.get('/adjustments/all', {
        params: { scenarioCode }
      });
      const responseData = response.data;
      const fetchedAdjustments = Array.isArray(responseData) ?
      responseData :
      responseData.data || [];

      // Build O(1) lookup maps once instead of O(n) find() per row.
      const entityByCode = new Map(masterEntities.map((e) => [e.EntityCode, e]));
      const adjustmentByCode = new Map(masterAdjustments.map((a) => [a.AdjustmentCode, a]));

      // Transform to tax calculations format with entity and adjustment name lookups
      const calculationsData = fetchedAdjustments.map((adj) => {
        const entity = entityByCode.get(adj.EntityCode);
        const adjustment = adjustmentByCode.get(adj.AdjustmentCode);

        return {
          id: `${adj.AdjustmentType}_${adj.id}`,
          originalId: adj.id, // Preserve the original backend ID for updates
          AdjustmentType: adj.AdjustmentType, // Preserve adjustment type for routing
          EntityCode: adj.EntityCode || '',
          EntityName: entity?.EntityName || '',
          AdjustmentCode: adj.AdjustmentCode || '',
          AdjustmentName: adjustment?.AdjustmentName || '',
          LocalCurrencyInput: parseFloat(adj.Amount) || 0, // LC amount from backend
          USDInput: parseFloat(adj.USDAmount) || 0, // USD amount from backend
          TaxEffectedLocalAmount: (parseFloat(adj.Amount) || 0) * getTaxRateForEntity(adj.EntityCode, adj.ScenarioCode), // Dynamic tax rate on LC
          TaxEffectedUSDAmount: (parseFloat(adj.USDAmount) || 0) * getTaxRateForEntity(adj.EntityCode, adj.ScenarioCode), // Dynamic tax rate on USD
          AdjustmentSource: adj.AdjustmentType || 'Manual',
          ScenarioCode: adj.ScenarioCode || '',
          Tag: adj.Tag || '',
          SubTag: adj.SubTag || '', // Preserve SubTag from original data
          ActivityType: adj.ActivityType || '', // Preserve ActivityType from original data
          PointInTime: adj.PointInTime || '', // Preserve PointInTime from original data
          // Preserve all original fields for backend updates
          Amount: adj.Amount,
          USDAmount: adj.USDAmount,
          SubEntity: adj.SubEntity,
          ModifiedBy: adj.ModifiedBy || 'admin',
          AdjustmentId: adj.AdjustmentId,
          FilingCode: adj.FilingCode || null
        };
      });

      setAllRows(calculationsData);
      setRows(calculationsData);
    } catch (error) {
      console.error('Error fetching state calculations:', error);
      setNotification({ open: true, message: 'Failed to fetch state calculations', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      checkLibraryAvailability();

      // Fetch master data in parallel
      await Promise.all([
      fetchMasterEntities(),
      fetchEntityGroups(),
      fetchMasterAdjustments(),
      fetchMasterStates()]
      );
    };

    initializeData();
  }, []);

  useEffect(() => {
    const loadScenarioData = async () => {
      if (!selectedScenario) {
        setLoading(false);
        return;
      }

      setLoading(true);
      await Promise.all([
      fetchEntityInputs(),
      fetchStateInputs()]
      );
    };

    loadScenarioData();
  }, [selectedScenario]);

  useEffect(() => {
    if (masterEntities.length > 0 && masterAdjustments.length > 0) {
      fetchStateCalculations();
    } else if (!selectedScenario) {
      setLoading(false);
    }
  }, [masterEntities, masterAdjustments, entityInputs, stateInputs]);

  // Filter allRows by selectedState and selectedFilingCode whenever they change
  useEffect(() => {
    let filtered = allRows;

    const stateArr = Array.isArray(selectedState) ? selectedState : [];
    const filingArr = Array.isArray(selectedFilingCode) ? selectedFilingCode : [];

    if (stateArr.length > 0) {
      // Build a set of filing codes that belong to the selected states
      const scenarioStateInputs = stateInputs.filter(
        (si) => si.ScenarioCode === selectedScenario?.scenarioCode
      );
      const validFilingCodes = new Set(
        scenarioStateInputs.
        filter((si) => stateArr.includes(si.StateCode)).
        map((si) => si.FilingCode).
        filter(Boolean)
      );
      // Keep rows that either have a FilingCode in the valid set, or have no FilingCode
      // (entity-level rows like Pretax/Permanent/Temporary don't have a filing code)
      filtered = filtered.filter((row) => !row.FilingCode || validFilingCodes.has(row.FilingCode));
    }

    if (filingArr.length > 0) {
      filtered = filtered.filter((row) => !row.FilingCode || filingArr.includes(row.FilingCode));
    }

    setRows(filtered);
  }, [allRows, selectedState, selectedFilingCode, stateInputs, selectedScenario]);

  // ETR-specific section mapping (only used when calculationType === 'effective-tax-rate')
  const etrSectionMapping = {
    'Pretax': 'PRETAX BOOK INCOME',
    'GS Permanent': 'GAAP TO STAT PERMANENT DIFFERENCES',
    'Permanent': 'STAT TO TAX PERMANENT DIFFERENCES',
    'State Pre-App Perm': 'STATE PERMANENT DIFFERENCES',
    'State Post-App Perm': 'STATE POST-APPORTIONED PERMANENT DIFFERENCES',
    'State Tax Adj. Return': 'STATE RETURN BASIS TAX ADJUSTMENTS',
    'State Tax Adj. Provision': 'STATE PROVISION BASIS TAX ADJUSTMENTS',
    'State Tax Adj. RTP': 'STATE RETURN TO PROVISION BASIS TAX ADJUSTMENTS'
  };

  const etrSectionTotalTitles = {
    'Pretax': 'GAAP PRETAX INCOME',
    'GS Permanent': 'Total GAAP TO STAT PERMANENT DIFFERENCES',
    'Permanent': 'Total STAT TO TAX PERMANENT DIFFERENCES',
    'State Pre-App Perm': 'TOTAL STATE PERMANENT DIFFERENCES',
    'State Post-App Perm': 'TOTAL STATE POST-APPORTIONED PERMANENT DIFFERENCES',
    'State Tax Adj. Return': 'TOTAL STATE RETURN BASIS TAX ADJUSTMENTS',
    'State Tax Adj. Provision': 'TOTAL STATE PROVISION BASIS TAX ADJUSTMENTS',
    'State Tax Adj. RTP': 'TOTAL STATE RETURN TO PROVISION BASIS TAX ADJUSTMENTS'
  };

  // Section mapping for adjustment types
  const sectionMapping = {
    'Pretax': 'PRETAX BOOK INCOME',
    'GS Permanent': 'GAAP TO STAT PERMANENT DIFFERENCES',
    'GS Temporary': 'GAAP TO STAT TEMPORARY DIFFERENCES',
    'Permanent': 'STAT TO TAX PERMANENT DIFFERENCES',
    'Payable': 'DEDUCTION FOR STATE AND LOCAL TAXES',
    'Temporary': 'STAT TO TAX TEMPORARY DIFFERENCES',
    'State Pre-App Perm': 'STATE PRE-APPORTIONMENT PERMANENT DIFFERENCES',
    'State Pre-App Temp': 'STATE PRE-APPORTIONMENT TEMPORARY DIFFERENCES',
    'State Pre-App NOL': 'STATE PRE-APPORTIONMENT NOL',
    'State Post-App Perm': 'STATE POST-APPORTIONED PERMANENT DIFFERENCES',
    'State Post-App Temp': 'STATE POST-APPORTIONED TEMPORARY DIFFERENCES',
    'State Post-App NOL': 'STATE POST-APPORTIONMENT NOL',
    'State Tax Effected Temp': 'STATE TAX-EFFECTED TEMPORARY DIFFERENCES',
    'State Tax Adj. Return': 'STATE RETURN BASIS TAX ADJUSTMENTS',
    'State Tax Adj. Provision': 'STATE PROVISION BASIS TAX ADJUSTMENTS',
    'State Tax Adj. RTP': 'STATE RETURN TO PROVISION BASIS TAX ADJUSTMENTS'
  };

  // Section total titles - customize each section's total label here
  const sectionTotalTitles = {
    'Pretax': 'GAAP PRETAX INCOME',
    'GS Permanent': 'Total GAAP TO STAT PERMANENT DIFFERENCES',
    'GS Temporary': 'Total GAAP TO STAT TEMPORARY DIFFERENCES',
    'Permanent': 'Total STAT TO TAX PERMANENT DIFFERENCES',
    'Payable': 'TOTAL DEDUCTION FOR STATE AND LOCAL TAXES',
    'Temporary': 'Total STAT TO TAX TEMPORARY DIFFERENCES',
    'State Pre-App Perm': 'TOTAL STATE PRE-APPORTIONMENT PERMANENT DIFFERENCES',
    'State Pre-App Temp': 'TOTAL STATE PRE-APPORTIONMENT TEMPORARY DIFFERENCES',
    'State Pre-App NOL': 'TOTAL STATE PRE-APPORTIONMENT NOL',
    'State Post-App Perm': 'TOTAL STATE POST-APPORTIONED PERMANENT DIFFERENCES',
    'State Post-App Temp': 'TOTAL STATE POST-APPORTIONED TEMPORARY DIFFERENCES',
    'State Post-App NOL': 'TOTAL STATE POST-APPORTIONMENT NOL',
    'State Tax Effected Temp': 'TOTAL STATE TAX-EFFECTED TEMPORARY DIFFERENCES',
    'State Tax Adj. Return': 'TOTAL STATE RETURN BASIS TAX ADJUSTMENTS',
    'State Tax Adj. Provision': 'TOTAL STATE PROVISION BASIS TAX ADJUSTMENTS',
    'State Tax Adj. RTP': 'TOTAL STATE RETURN TO PROVISION BASIS TAX ADJUSTMENTS'
  };

  // Create deferred rollforward table structure
  const createDeferredRollforwardData = (data, entityGrouping = 'entity', stateGrouping = 'total') => {
    const scenarioStateInputs = stateInputs.filter((si) => si.ScenarioCode === selectedScenario?.scenarioCode);
    const getStateForFiling = (filingCode) => {
      if (!filingCode) return null;
      return masterStates.find((ms) => ms.FilingCode === filingCode)?.State ||
      scenarioStateInputs.find((si) => si.FilingCode === filingCode)?.StateCode ||
      null;
    };
    const getScopedEntityStateInputs = (entityCode, subGroupKey) => {
      const stateArr = Array.isArray(selectedState) ? selectedState : [];
      const filingCodeArr = Array.isArray(selectedFilingCode) ? selectedFilingCode : [];
      let inputs = scenarioStateInputs.filter((si) => si.EntityCode === entityCode);
      if (stateGrouping === 'filing' && subGroupKey && subGroupKey !== 'total') {
        inputs = inputs.filter((si) => si.FilingCode === subGroupKey);
      } else if (stateGrouping === 'state' && subGroupKey && subGroupKey !== 'total') {
        inputs = inputs.filter((si) => getStateForFiling(si.FilingCode) === subGroupKey);
      } else {
        if (filingCodeArr.length > 0) {
          inputs = inputs.filter((si) => filingCodeArr.includes(si.FilingCode));
        }
        if (stateArr.length > 0) {
          inputs = inputs.filter((si) => stateArr.includes(si.StateCode) || stateArr.includes(getStateForFiling(si.FilingCode)));
        }
      }
      return inputs;
    };

    // Helper: split categoryData into sub-groups based on stateGrouping
    const getSubGroups = (categoryData) => {
      if (stateGrouping === 'filing') {
        const uniqueFilings = [...new Set(categoryData.map((r) => r.FilingCode).filter(Boolean))].sort();
        return uniqueFilings.map((fc) => ({ key: fc, label: fc, slice: categoryData.filter((r) => r.FilingCode === fc) }));
      }
      if (stateGrouping === 'state') {
        const uniqueStates = [...new Set(categoryData.map((r) => getStateForFiling(r.FilingCode)).filter(Boolean))].sort();
        return uniqueStates.map((sc) => ({
          key: sc,
          label: sc,
          slice: categoryData.filter((r) => getStateForFiling(r.FilingCode) === sc)
        }));
      }
      // 'total' — no sub-grouping
      return [{ key: 'total', label: '', slice: categoryData }];
    };

    const deferredRows = [];

    // Get unique entities from the data
    const uniqueEntities = [...new Set(data.map((row) => row.EntityCode))].
    filter((code) => code).
    sort();

    // Mapping of adjustment types to deferred rollforward categories
    const categoryMapping = {
      'GS Temporary': 'GAAP TO STAT TEMPORARY DIFFERENCES',
      'Temporary': 'STAT TO TAX TEMPORARY DIFFERENCES',
      'State Pre-App Temp': 'STATE PRE-APPORTIONMENT TEMPORARY DIFFERENCES',
      'State Pre-App NOL': 'STATE PRE-APPORTIONMENT NOL',
      'State Pre-App Valuation Allowance': 'STATE PRE-APPORTIONMENT VALUATION ALLOWANCES',
      'State Post-App Temp': 'STATE POST-APPORTIONED TEMPORARY DIFFERENCES',
      'State Post-App NOL': 'STATE POST-APPORTIONMENT NOL',
      'State Post-App Valuation Allowance': 'STATE POST-APPORTIONED VALUATION ALLOWANCE',
      'State Tax Effected Temp': 'STATE TAX-EFFECTED TEMPORARY DIFFERENCES',
      'State Tax Effected Valuation Allowance': 'STATE TAX-EFFECTED VALUATION ALLOWANCE'
    };

    // Entity-level section types — not filtered by state/filing, always included in every breakout group
    const ENTITY_LEVEL_TYPES = new Set(['GS Temporary', 'Temporary']);

    // ── ALL MODE: merge all entities into shared sections ──────────────────────
    // Also used for State Breakout so that state is the top-level organizer (PA, DE, etc.)
    if (entityGrouping === 'all' || stateGrouping === 'state') {
      // Outer loop: sub-groups (total / state / filing) — mirrors how entity breakout loops over entities
      const topLevelGroups = getSubGroups(data);

      topLevelGroups.forEach(({ key: sgKey, label: sgLabel, slice: sgSlice }) => {
        DEFERRED_ROLLFORWARD_HEADERS.forEach((header, headerIndex) => {
          const adjustmentType = Object.keys(categoryMapping).find((key) =>
          categoryMapping[key] === header
          );
          if (!adjustmentType) return;

          // Entity-level sections always use full data; state/filing sections use the sub-group slice
          let sourceSlice = ENTITY_LEVEL_TYPES.has(adjustmentType) ? data : sgSlice;

          // Combo mode (Entity Breakout + State Breakout): restrict entity-level sections
          // (GAAP TO STAT / STAT TO TAX temporary differences) to only those entities
          // associated with the current state's MasterStates (union of their entityLists).
          if (
          ENTITY_LEVEL_TYPES.has(adjustmentType) &&
          entityGrouping === 'entity' &&
          stateGrouping === 'state' &&
          sgKey !== 'total')
          {
            const stateEntityCodes = new Set(
              masterStates.
              filter((ms) => ms.State === sgKey).
              flatMap((ms) => (ms.entityList || []).map((e) => typeof e === 'object' ? e.EntityCode : e)).
              filter(Boolean)
            );
            sourceSlice = data.filter((row) => stateEntityCodes.has(row.EntityCode));
          }

          // Collect data for this section within this sub-group
          const categoryData = sourceSlice.filter((row) =>
          row.AdjustmentSource === adjustmentType || row.AdjustmentType === adjustmentType
          );
          if (categoryData.length === 0) return;

          // In a state breakout, entity-level temporary differences are tax-effected
          // for each state and belong to that state's grand total, so label them with
          // the state just like the explicitly state-level sections.
          const shouldPrefixSubGroup = sgLabel && (
          !ENTITY_LEVEL_TYPES.has(adjustmentType) || stateGrouping === 'state'
          );
          const sectionLabel = shouldPrefixSubGroup ? `${sgLabel} - ${header}` : header;
          const sectionHeaderRow = {
            id: `section-all-${sgKey}-${headerIndex}`,
            EntityCode: '',
            AdjustmentCode: '',
            AdjustmentName: sectionLabel,
            isSection: true
          };
          ACTIVITY_TYPES.forEach((activityType) => {
            sectionHeaderRow[activityType] = null;
            sectionHeaderRow[`${activityType}_TaxEffected`] = null;
          });
          deferredRows.push(sectionHeaderRow);

          const uniqueAdjustments = [...new Set(categoryData.map((row) => row.AdjustmentCode))].
          filter((code) => code).
          sort();

          uniqueAdjustments.forEach((adjustmentCode) => {
            const adjRows = categoryData.filter((row) => row.AdjustmentCode === adjustmentCode);
            const firstRow = adjRows[0];
            if (!firstRow) return;

            const isEntityLevel = ENTITY_LEVEL_TYPES.has(adjustmentType);
            const belongsToStateSubGroup =
            stateGrouping === 'state' && sgKey !== 'total';
            const hasSubGroup = sgKey !== 'total' && (
            !isEntityLevel || belongsToStateSubGroup
            );
            const adjustmentRow = {
              id: `all-${sgKey}-${adjustmentType}-${adjustmentCode}`,
              EntityCode: '',
              AdjustmentCode: adjustmentCode,
              AdjustmentName: firstRow.AdjustmentName || '',
              AdjustmentNamePrefix: '',
              AdjustmentType: adjustmentType,
              SubGroupKey: hasSubGroup ? sgKey : null,
              SubGroupType: hasSubGroup ? stateGrouping : null,
              isSection: false
            };

            ACTIVITY_TYPES.forEach((activityType) => {
              adjustmentRow[activityType] = 0;
              adjustmentRow[`${activityType}_PostApportioned`] = 0;
              adjustmentRow[`${activityType}_TaxEffected`] = 0;
              adjustmentRow[`${activityType}_FBOS`] = 0;
            });

            // Accumulate Tax Rate Change across each contributing adjustment using that
            // adjustment's own entity deferred/current rates (mirrors entity-breakout formula).
            let taxRateChangeAccum = 0;

            adjRows.forEach((adj) => {
              const entityCode = adj.EntityCode;
              const adjFilingCode = adj.FilingCode;
              const adjStateInput = stateInputMapByKey.get(`${adjFilingCode}__${entityCode}__${selectedScenario?.scenarioCode}`);
              const adjEntityInput = entityInputMapByKey.get(`${entityCode}__${selectedScenario?.scenarioCode}`);

              const amount = selectedCurrency === 'USD' ?
              parseFloat(adj.USDInput) || parseFloat(adj.USDAmount) || 0 :
              parseFloat(adj.LocalCurrencyInput) || parseFloat(adj.Amount) || 0;

              const apportionmentRate = getDeferredApportionmentRate(adjStateInput, adj.ActivityType);
              const rawStateCurrentRate = parseFloat(adjStateInput?.CurrentStateRate) || 0;
              const stateCurrentRate = rawStateCurrentRate > 1 ? rawStateCurrentRate / 100 : rawStateCurrentRate;
              const rawBeginDeferredRate = parseFloat(adjEntityInput?.BeginningDeferredTaxRate) || 0;
              const beginDeferredRate = rawBeginDeferredRate > 1 ? rawBeginDeferredRate / 100 : rawBeginDeferredRate;

              const isPreAppType = adjustmentType === 'State Pre-App Temp' ||
              adjustmentType === 'State Pre-App NOL' ||
              adjustmentType === 'State Pre-App Valuation Allowance' ||
              adjustmentType === 'GS Temporary' ||
              adjustmentType === 'Temporary';
              const isEntityLevelPreAppAll = adjustmentType === 'GS Temporary' || adjustmentType === 'Temporary';
              const isPostAppType = adjustmentType === 'State Post-App Temp' ||
              adjustmentType === 'State Post-App NOL' ||
              adjustmentType === 'State Post-App Valuation Allowance';
              const isStateTaxEffectedType = adjustmentType === 'State Tax Effected Temp' ||
              adjustmentType === 'State Tax Effected Valuation Allowance';

              const activityType = adj.ActivityType;
              if (!activityType || !ACTIVITY_TYPES.includes(activityType)) return;

              if (isPreAppType) {
                adjustmentRow[activityType] += amount;

                // Tax Rate Change contribution (pretax amount × entity rate difference),
                // matching the component logic used in entity-breakout mode.
                const pctRate = (r) => {const v = parseFloat(r) || 0;return v > 1 ? v / 100 : v;};
                const trcEndingDef = pctRate(adjEntityInput?.EndingDeferredTaxRate);
                const trcBeginningDef = pctRate(adjEntityInput?.BeginningDeferredTaxRate);
                const trcPriorCurrent = pctRate(adjEntityInput?.PriorCurrentTaxRate);
                const trcCurrent = pctRate(adjEntityInput?.CurrentTaxRate);
                let trcRateDiff = null;
                if (activityType === 'BeginningBalance' || activityType === 'DeferredTrueUp' || activityType === 'PurchaseAccounting') {
                  trcRateDiff = trcEndingDef - trcBeginningDef;
                } else if (activityType === 'ReturnToProvision') {
                  trcRateDiff = trcEndingDef - trcPriorCurrent;
                } else if (activityType === 'CurrentActivityPL' || activityType === 'DeferredExpAdjustment' || activityType === 'BalanceSheetAdjustment' || activityType === 'OCIAdjustment' || activityType === 'CustomDeferredExpAdjustment' || activityType === 'CustomBalanceSheetAdjustment') {
                  trcRateDiff = trcEndingDef - trcCurrent;
                }
                if (trcRateDiff !== null) {
                  taxRateChangeAccum += amount * trcRateDiff;
                }

                if (isEntityLevelPreAppAll && !(adjFilingCode && adjStateInput)) {
                  const entityStateInput = getScopedEntityStateInputs(entityCode, sgKey)[0];
                  const rate = getDeferredApportionmentRate(entityStateInput, activityType);
                  const rawSR = parseFloat(entityStateInput?.CurrentStateRate) || 0;
                  const sr = rawSR > 1 ? rawSR / 100 : rawSR;
                  const postAmount = amount * rate;
                  const taxEffectedAmount = postAmount * sr;
                  adjustmentRow[`${activityType}_PostApportioned`] += postAmount;
                  adjustmentRow[`${activityType}_TaxEffected`] += taxEffectedAmount;
                  adjustmentRow[`${activityType}_FBOS`] += taxEffectedAmount * -1 * beginDeferredRate;
                } else if (adjFilingCode && adjStateInput) {
                  // Single filing code matched
                  const postAmount = amount * apportionmentRate;
                  adjustmentRow[`${activityType}_PostApportioned`] += postAmount;
                  const taxEffectedAmount = postAmount * stateCurrentRate;
                  adjustmentRow[`${activityType}_TaxEffected`] += taxEffectedAmount;
                  adjustmentRow[`${activityType}_FBOS`] += taxEffectedAmount * -1 * beginDeferredRate;
                } else {
                  // No FilingCode or no matching state input: use the entity's state inputs in the active breakout scope.
                  const entityStateInputsFallback = getScopedEntityStateInputs(entityCode, sgKey);
                  let postAmountFb = 0;
                  let taxEffectedAmountFb = 0;
                  entityStateInputsFallback.forEach((si) => {
                    const rate = getDeferredApportionmentRate(si, activityType);
                    const rawSR = parseFloat(si.CurrentStateRate) || 0;
                    const sr = rawSR > 1 ? rawSR / 100 : rawSR;
                    const postI = amount * rate;
                    postAmountFb += postI;
                    taxEffectedAmountFb += postI * sr;
                  });
                  adjustmentRow[`${activityType}_PostApportioned`] += postAmountFb;
                  adjustmentRow[`${activityType}_TaxEffected`] += taxEffectedAmountFb;
                  adjustmentRow[`${activityType}_FBOS`] += taxEffectedAmountFb * -1 * beginDeferredRate;
                }
              } else if (isPostAppType) {
                adjustmentRow[`${activityType}_PostApportioned`] += amount;
                const taxEffectedAmount = amount * stateCurrentRate;
                adjustmentRow[`${activityType}_TaxEffected`] += taxEffectedAmount;
                adjustmentRow[`${activityType}_FBOS`] += taxEffectedAmount * -1 * beginDeferredRate;
              } else if (isStateTaxEffectedType) {
                adjustmentRow[`${activityType}_TaxEffected`] += amount;
                adjustmentRow[`${activityType}_FBOS`] += amount * -1 * beginDeferredRate;
              }
            });

            // ── Post-loop rollforward calculations (mirrors entity-breakout mode) ──
            // Adj. Beginning Balance
            adjustmentRow['AdjBeginningBalance'] =
            (adjustmentRow['BeginningBalance'] || 0) + (
            adjustmentRow['ReturnToProvision'] || 0) + (
            adjustmentRow['DeferredTrueUp'] || 0) + (
            adjustmentRow['PurchaseAccounting'] || 0);

            adjustmentRow['AdjBeginningBalance_PostApportioned'] =
            (adjustmentRow['BeginningBalance_PostApportioned'] || 0) + (
            adjustmentRow['ReturnToProvision_PostApportioned'] || 0) + (
            adjustmentRow['DeferredTrueUp_PostApportioned'] || 0) + (
            adjustmentRow['PurchaseAccounting_PostApportioned'] || 0);

            adjustmentRow['AdjBeginningBalance_TaxEffected'] =
            (adjustmentRow['BeginningBalance_TaxEffected'] || 0) + (
            adjustmentRow['ReturnToProvision_TaxEffected'] || 0) + (
            adjustmentRow['DeferredTrueUp_TaxEffected'] || 0) + (
            adjustmentRow['PurchaseAccounting_TaxEffected'] || 0);

            adjustmentRow['AdjBeginningBalance_FBOS'] =
            (adjustmentRow['BeginningBalance_FBOS'] || 0) + (
            adjustmentRow['ReturnToProvision_FBOS'] || 0) + (
            adjustmentRow['DeferredTrueUp_FBOS'] || 0) + (
            adjustmentRow['PurchaseAccounting_FBOS'] || 0);

            // CTA = 0 in all-mode (no single entity FX rates to use)
            adjustmentRow['CTAAdjustment'] = 0;
            adjustmentRow['CTAAdjustment_PostApportioned'] = 0;
            adjustmentRow['CTAAdjustment_TaxEffected'] = 0;
            adjustmentRow['CTAAdjustment_FBOS'] = 0;

            // Ending Balances
            adjustmentRow['EndingBalance'] =
            (adjustmentRow['AdjBeginningBalance'] || 0) + (
            adjustmentRow['CurrentActivityPL'] || 0) + (
            adjustmentRow['OCIAdjustment'] || 0) + (
            adjustmentRow['DeferredExpAdjustment'] || 0) + (
            adjustmentRow['BalanceSheetAdjustment'] || 0) + (
            adjustmentRow['CustomDeferredExpAdjustment'] || 0) + (
            adjustmentRow['CustomBalanceSheetAdjustment'] || 0);

            adjustmentRow['EndingBalance_PostApportioned'] =
            (adjustmentRow['AdjBeginningBalance_PostApportioned'] || 0) + (
            adjustmentRow['CurrentActivityPL_PostApportioned'] || 0) + (
            adjustmentRow['OCIAdjustment_PostApportioned'] || 0) + (
            adjustmentRow['DeferredExpAdjustment_PostApportioned'] || 0) + (
            adjustmentRow['BalanceSheetAdjustment_PostApportioned'] || 0) + (
            adjustmentRow['CustomDeferredExpAdjustment_PostApportioned'] || 0) + (
            adjustmentRow['CustomBalanceSheetAdjustment_PostApportioned'] || 0);

            adjustmentRow['EndingBalance_TaxEffected'] =
            (adjustmentRow['AdjBeginningBalance_TaxEffected'] || 0) + (
            adjustmentRow['CurrentActivityPL_TaxEffected'] || 0) + (
            adjustmentRow['OCIAdjustment_TaxEffected'] || 0) + (
            adjustmentRow['DeferredExpAdjustment_TaxEffected'] || 0) + (
            adjustmentRow['BalanceSheetAdjustment_TaxEffected'] || 0) + (
            adjustmentRow['CustomDeferredExpAdjustment_TaxEffected'] || 0) + (
            adjustmentRow['CustomBalanceSheetAdjustment_TaxEffected'] || 0);

            adjustmentRow['EndingBalance_FBOS'] =
            (adjustmentRow['AdjBeginningBalance_FBOS'] || 0) + (
            adjustmentRow['CurrentActivityPL_FBOS'] || 0) + (
            adjustmentRow['OCIAdjustment_FBOS'] || 0) + (
            adjustmentRow['DeferredExpAdjustment_FBOS'] || 0) + (
            adjustmentRow['BalanceSheetAdjustment_FBOS'] || 0) + (
            adjustmentRow['CustomDeferredExpAdjustment_FBOS'] || 0) + (
            adjustmentRow['CustomBalanceSheetAdjustment_FBOS'] || 0);

            // ── Deferred expense section columns (mirrors entity-breakout logic) ──
            // TaxRateChange computed per-adjustment using each entity's deferred/current
            // rates (accumulated in taxRateChangeAccum during the adjRows loop above).
            // TaxRateChange_FBOS is 0 to match entity-breakout, which also yields 0.
            adjustmentRow['TaxRateChange_TaxEffected'] = taxRateChangeAccum;
            adjustmentRow['TaxRateChange'] = -taxRateChangeAccum;
            adjustmentRow['TaxRateChange_FBOS'] = 0;

            // CurrentActivity = -(CurrentActivityPL tax-effected / FBOS)
            adjustmentRow['CurrentActivity'] = -(adjustmentRow['CurrentActivityPL_TaxEffected'] || 0);
            adjustmentRow['CurrentActivity_TaxEffected'] = -(adjustmentRow['CurrentActivityPL_TaxEffected'] || 0);
            adjustmentRow['CurrentActivity_FBOS'] = -(adjustmentRow['CurrentActivityPL_FBOS'] || 0);

            // WriteOff = -(DeferredExpAdjustment + CustomDeferredExpAdjustment) tax-effected / FBOS
            const allDeferredExpTe = adjustmentRow['DeferredExpAdjustment_TaxEffected'] || 0;
            const allCustomDeferredExpTe = adjustmentRow['CustomDeferredExpAdjustment_TaxEffected'] || 0;
            const allDeferredExpFbos = adjustmentRow['DeferredExpAdjustment_FBOS'] || 0;
            const allCustomDeferredExpFbos = adjustmentRow['CustomDeferredExpAdjustment_FBOS'] || 0;
            adjustmentRow['WriteOff'] = -(allDeferredExpTe + allCustomDeferredExpTe);
            adjustmentRow['WriteOff_TaxEffected'] = -(allDeferredExpTe + allCustomDeferredExpTe);
            adjustmentRow['WriteOff_FBOS'] = -(allDeferredExpFbos + allCustomDeferredExpFbos);

            // ReturnToProvisionRollforward / DeferredTrueUpRollforward
            adjustmentRow['ReturnToProvisionRollforward'] = -(adjustmentRow['ReturnToProvision_TaxEffected'] || 0);
            adjustmentRow['ReturnToProvisionRollforward_TaxEffected'] = -(adjustmentRow['ReturnToProvision_TaxEffected'] || 0);
            adjustmentRow['ReturnToProvisionRollforward_FBOS'] = -(adjustmentRow['ReturnToProvision_FBOS'] || 0);
            adjustmentRow['DeferredTrueUpRollforward'] = -(adjustmentRow['DeferredTrueUp_TaxEffected'] || 0);
            adjustmentRow['DeferredTrueUpRollforward_TaxEffected'] = -(adjustmentRow['DeferredTrueUp_TaxEffected'] || 0);
            adjustmentRow['DeferredTrueUpRollforward_FBOS'] = -(adjustmentRow['DeferredTrueUp_FBOS'] || 0);

            // ValuationAllowance + DeferredExpense
            const allIsValuationAllowanceSection = header === 'STATE PRE-APPORTIONMENT VALUATION ALLOWANCES' ||
            header === 'STATE POST-APPORTIONED VALUATION ALLOWANCE' ||
            header === 'STATE TAX-EFFECTED VALUATION ALLOWANCE';

            if (allIsValuationAllowanceSection) {
              // For VA sections: VA = -(TaxRateChange + CurrentActivity + DeferredTrueUp + ReturnToProvision)
              adjustmentRow['ValuationAllowance'] = -(
              adjustmentRow['TaxRateChange'] +
              adjustmentRow['CurrentActivity'] +
              adjustmentRow['DeferredTrueUpRollforward'] +
              adjustmentRow['ReturnToProvisionRollforward']);

              adjustmentRow['ValuationAllowance_TaxEffected'] = -(
              adjustmentRow['TaxRateChange_TaxEffected'] +
              adjustmentRow['CurrentActivity_TaxEffected'] +
              adjustmentRow['DeferredTrueUpRollforward_TaxEffected'] +
              adjustmentRow['ReturnToProvisionRollforward_TaxEffected']);

              adjustmentRow['ValuationAllowance_FBOS'] = -(
              adjustmentRow['TaxRateChange_FBOS'] +
              adjustmentRow['CurrentActivity_FBOS'] +
              adjustmentRow['DeferredTrueUpRollforward_FBOS'] +
              adjustmentRow['ReturnToProvisionRollforward_FBOS']);

              adjustmentRow['DeferredExpense'] = adjustmentRow['ValuationAllowance'];
              adjustmentRow['DeferredExpense_TaxEffected'] = adjustmentRow['ValuationAllowance_TaxEffected'];
              adjustmentRow['DeferredExpense_FBOS'] = adjustmentRow['ValuationAllowance_FBOS'];
              // Hide rollforward columns for VA sections
              adjustmentRow['WriteOff'] = null;
              adjustmentRow['WriteOff_TaxEffected'] = null;
              adjustmentRow['WriteOff_FBOS'] = null;
              adjustmentRow['TaxRateChange'] = null;
              adjustmentRow['TaxRateChange_FBOS'] = null;
              adjustmentRow['ReturnToProvisionRollforward'] = null;
              adjustmentRow['ReturnToProvisionRollforward_TaxEffected'] = null;
              adjustmentRow['ReturnToProvisionRollforward_FBOS'] = null;
              adjustmentRow['DeferredTrueUpRollforward'] = null;
              adjustmentRow['DeferredTrueUpRollforward_TaxEffected'] = null;
              adjustmentRow['DeferredTrueUpRollforward_FBOS'] = null;
              adjustmentRow['CurrentActivity'] = null;
              adjustmentRow['CurrentActivity_TaxEffected'] = null;
              adjustmentRow['CurrentActivity_FBOS'] = null;
            } else {
              adjustmentRow['ValuationAllowance'] = 0;
              adjustmentRow['ValuationAllowance_TaxEffected'] = 0;
              adjustmentRow['ValuationAllowance_FBOS'] = 0;
              adjustmentRow['DeferredExpense'] =
              adjustmentRow['TaxRateChange'] +
              adjustmentRow['ReturnToProvisionRollforward'] +
              adjustmentRow['DeferredTrueUpRollforward'] +
              adjustmentRow['CurrentActivity'] +
              adjustmentRow['WriteOff'] +
              adjustmentRow['ValuationAllowance'];
              adjustmentRow['DeferredExpense_TaxEffected'] =
              adjustmentRow['TaxRateChange_TaxEffected'] +
              adjustmentRow['ReturnToProvisionRollforward_TaxEffected'] +
              adjustmentRow['DeferredTrueUpRollforward_TaxEffected'] +
              adjustmentRow['CurrentActivity_TaxEffected'] +
              adjustmentRow['WriteOff_TaxEffected'] +
              adjustmentRow['ValuationAllowance_TaxEffected'];
              adjustmentRow['DeferredExpense_FBOS'] =
              adjustmentRow['TaxRateChange_FBOS'] +
              adjustmentRow['ReturnToProvisionRollforward_FBOS'] +
              adjustmentRow['DeferredTrueUpRollforward_FBOS'] +
              adjustmentRow['CurrentActivity_FBOS'] +
              adjustmentRow['WriteOff_FBOS'] +
              adjustmentRow['ValuationAllowance_FBOS'];
            }

            deferredRows.push(adjustmentRow);
          });

          // Section total row
          const sectionTotalRow = {
            id: `total-all-${sgKey}-${headerIndex}`,
            EntityCode: '',
            AdjustmentCode: '',
            AdjustmentName: `TOTAL ${header}`,
            isSubFooter: true
          };
          ACTIVITY_TYPES.forEach((activityType) => {
            let total = 0,postTotal = 0,taxTotal = 0,fbosTotal = 0;
            const sectionStartIndex = deferredRows.findLastIndex((row) => row.isSection);
            deferredRows.slice(sectionStartIndex + 1).forEach((row) => {
              if (!row.isSection && !row.isSubFooter) {
                total += parseFloat(row[activityType]) || 0;
                postTotal += parseFloat(row[`${activityType}_PostApportioned`]) || 0;
                taxTotal += parseFloat(row[`${activityType}_TaxEffected`]) || 0;
                fbosTotal += parseFloat(row[`${activityType}_FBOS`]) || 0;
              }
            });
            const shouldHidePreAndPost = adjustmentType === 'State Tax Effected Temp' || adjustmentType === 'State Tax Effected Valuation Allowance';
            const shouldHidePre = adjustmentType === 'State Post-App Temp' || adjustmentType === 'State Post-App NOL' || adjustmentType === 'State Post-App Valuation Allowance';
            sectionTotalRow[activityType] = shouldHidePreAndPost || shouldHidePre ? null : total;
            sectionTotalRow[`${activityType}_PostApportioned`] = shouldHidePreAndPost ? null : postTotal;
            sectionTotalRow[`${activityType}_TaxEffected`] = taxTotal;
            sectionTotalRow[`${activityType}_FBOS`] = shouldHidePreAndPost ? null : fbosTotal;
          });
          // Sum CTAAdjustment, EndingBalance, and deferred expense derived columns for this section
          {
            const shouldHidePreAndPost = adjustmentType === 'State Tax Effected Temp' || adjustmentType === 'State Tax Effected Valuation Allowance';
            const isVASection = header === 'STATE PRE-APPORTIONMENT VALUATION ALLOWANCES' ||
            header === 'STATE POST-APPORTIONED VALUATION ALLOWANCE' ||
            header === 'STATE TAX-EFFECTED VALUATION ALLOWANCE';
            const sectionStartIndex = deferredRows.findLastIndex((row) => row.isSection);
            const sectionRows = deferredRows.slice(sectionStartIndex + 1).filter((r) => !r.isSection && !r.isSubFooter);
            let ctaTotal = 0,ctaPostTotal = 0,ctaTaxTotal = 0,ctaFbosTotal = 0;
            let ebTotal = 0,ebPostTotal = 0,ebTaxTotal = 0,ebFbosTotal = 0;
            let deTotal = 0,deTaxTotal = 0,deFbosTotal = 0;
            let trTotal = 0,trTaxTotal = 0,trFbosTotal = 0;
            let caTotal = 0,caTaxTotal = 0,caFbosTotal = 0;
            let woTotal = 0,woTaxTotal = 0,woFbosTotal = 0;
            let rtpTotal = 0,rtpTaxTotal = 0,rtpFbosTotal = 0;
            let dtuTotal = 0,dtuTaxTotal = 0,dtuFbosTotal = 0;
            let vaTotal = 0,vaTaxTotal = 0,vaFbosTotal = 0;
            sectionRows.forEach((row) => {
              ctaTotal += parseFloat(row['CTAAdjustment']) || 0;
              ctaPostTotal += parseFloat(row['CTAAdjustment_PostApportioned']) || 0;
              ctaTaxTotal += parseFloat(row['CTAAdjustment_TaxEffected']) || 0;
              ctaFbosTotal += parseFloat(row['CTAAdjustment_FBOS']) || 0;
              ebTotal += parseFloat(row['EndingBalance']) || 0;
              ebPostTotal += parseFloat(row['EndingBalance_PostApportioned']) || 0;
              ebTaxTotal += parseFloat(row['EndingBalance_TaxEffected']) || 0;
              ebFbosTotal += parseFloat(row['EndingBalance_FBOS']) || 0;
              deTotal += parseFloat(row['DeferredExpense']) || 0;
              deTaxTotal += parseFloat(row['DeferredExpense_TaxEffected']) || 0;
              deFbosTotal += parseFloat(row['DeferredExpense_FBOS']) || 0;
              trTotal += parseFloat(row['TaxRateChange']) || 0;
              trTaxTotal += parseFloat(row['TaxRateChange_TaxEffected']) || 0;
              trFbosTotal += parseFloat(row['TaxRateChange_FBOS']) || 0;
              caTotal += parseFloat(row['CurrentActivity']) || 0;
              caTaxTotal += parseFloat(row['CurrentActivity_TaxEffected']) || 0;
              caFbosTotal += parseFloat(row['CurrentActivity_FBOS']) || 0;
              woTotal += parseFloat(row['WriteOff']) || 0;
              woTaxTotal += parseFloat(row['WriteOff_TaxEffected']) || 0;
              woFbosTotal += parseFloat(row['WriteOff_FBOS']) || 0;
              rtpTotal += parseFloat(row['ReturnToProvisionRollforward']) || 0;
              rtpTaxTotal += parseFloat(row['ReturnToProvisionRollforward_TaxEffected']) || 0;
              rtpFbosTotal += parseFloat(row['ReturnToProvisionRollforward_FBOS']) || 0;
              dtuTotal += parseFloat(row['DeferredTrueUpRollforward']) || 0;
              dtuTaxTotal += parseFloat(row['DeferredTrueUpRollforward_TaxEffected']) || 0;
              dtuFbosTotal += parseFloat(row['DeferredTrueUpRollforward_FBOS']) || 0;
              vaTotal += parseFloat(row['ValuationAllowance']) || 0;
              vaTaxTotal += parseFloat(row['ValuationAllowance_TaxEffected']) || 0;
              vaFbosTotal += parseFloat(row['ValuationAllowance_FBOS']) || 0;
            });
            sectionTotalRow['CTAAdjustment'] = ctaTotal;
            sectionTotalRow['CTAAdjustment_PostApportioned'] = shouldHidePreAndPost ? null : ctaPostTotal;
            sectionTotalRow['CTAAdjustment_TaxEffected'] = ctaTaxTotal;
            sectionTotalRow['CTAAdjustment_FBOS'] = shouldHidePreAndPost ? null : ctaFbosTotal;
            sectionTotalRow['EndingBalance'] = shouldHidePreAndPost ? null : ebTotal;
            sectionTotalRow['EndingBalance_PostApportioned'] = shouldHidePreAndPost ? null : ebPostTotal;
            sectionTotalRow['EndingBalance_TaxEffected'] = ebTaxTotal;
            sectionTotalRow['EndingBalance_FBOS'] = shouldHidePreAndPost ? null : ebFbosTotal;
            sectionTotalRow['DeferredExpense'] = deTotal;
            sectionTotalRow['DeferredExpense_TaxEffected'] = deTaxTotal;
            sectionTotalRow['DeferredExpense_FBOS'] = deFbosTotal;
            sectionTotalRow['TaxRateChange'] = isVASection ? null : trTotal;
            sectionTotalRow['TaxRateChange_TaxEffected'] = trTaxTotal;
            sectionTotalRow['TaxRateChange_FBOS'] = isVASection ? null : trFbosTotal;
            sectionTotalRow['CurrentActivity'] = isVASection ? null : caTotal;
            sectionTotalRow['CurrentActivity_TaxEffected'] = isVASection ? null : caTaxTotal;
            sectionTotalRow['CurrentActivity_FBOS'] = isVASection ? null : caFbosTotal;
            sectionTotalRow['WriteOff'] = isVASection ? null : woTotal;
            sectionTotalRow['WriteOff_TaxEffected'] = isVASection ? null : woTaxTotal;
            sectionTotalRow['WriteOff_FBOS'] = isVASection ? null : woFbosTotal;
            sectionTotalRow['ReturnToProvisionRollforward'] = isVASection ? null : rtpTotal;
            sectionTotalRow['ReturnToProvisionRollforward_TaxEffected'] = isVASection ? null : rtpTaxTotal;
            sectionTotalRow['ReturnToProvisionRollforward_FBOS'] = isVASection ? null : rtpFbosTotal;
            sectionTotalRow['DeferredTrueUpRollforward'] = isVASection ? null : dtuTotal;
            sectionTotalRow['DeferredTrueUpRollforward_TaxEffected'] = isVASection ? null : dtuTaxTotal;
            sectionTotalRow['DeferredTrueUpRollforward_FBOS'] = isVASection ? null : dtuFbosTotal;
            sectionTotalRow['ValuationAllowance'] = vaTotal;
            sectionTotalRow['ValuationAllowance_TaxEffected'] = vaTaxTotal;
            sectionTotalRow['ValuationAllowance_FBOS'] = vaFbosTotal;
          }
          deferredRows.push(sectionTotalRow);
        }); // end DEFERRED_ROLLFORWARD_HEADERS.forEach

        // Grand total row per sub-group
        const grandTotalRow = {
          id: `grand-total-all-${sgKey}`,
          EntityCode: '',
          AdjustmentCode: '',
          AdjustmentName: 'GRAND TOTAL TEMPORARY DIFFERENCES',
          isSubFooter: true
        };
        ACTIVITY_TYPES.forEach((activityType) => {
          let grandTotal = 0,grandPost = 0,grandTax = 0,grandFbos = 0;
          // Sum only rows belonging to this sub-group (those with matching SubGroupKey or no sub-grouping)
          deferredRows.forEach((row) => {
            if (!row.isSection && !row.isSubFooter) {
              const rowSgKey = row.SubGroupKey || 'total';
              if (rowSgKey === sgKey) {
                grandTotal += parseFloat(row[activityType]) || 0;
                grandPost += parseFloat(row[`${activityType}_PostApportioned`]) || 0;
                grandTax += parseFloat(row[`${activityType}_TaxEffected`]) || 0;
                grandFbos += parseFloat(row[`${activityType}_FBOS`]) || 0;
              }
            }
          });
          grandTotalRow[activityType] = grandTotal;
          grandTotalRow[`${activityType}_PostApportioned`] = grandPost;
          grandTotalRow[`${activityType}_TaxEffected`] = grandTax;
          grandTotalRow[`${activityType}_FBOS`] = grandFbos;
        });
        // Sum CTAAdjustment, EndingBalance, and deferred expense derived columns for grand total
        {
          let ctaTotal = 0,ctaPostTotal = 0,ctaTaxTotal = 0,ctaFbosTotal = 0;
          let ebTotal = 0,ebPostTotal = 0,ebTaxTotal = 0,ebFbosTotal = 0;
          let deTotal = 0,deTaxTotal = 0,deFbosTotal = 0;
          let trTotal = 0,trTaxTotal = 0,trFbosTotal = 0;
          let caTotal = 0,caTaxTotal = 0,caFbosTotal = 0;
          let woTotal = 0,woTaxTotal = 0,woFbosTotal = 0;
          let rtpTotal = 0,rtpTaxTotal = 0,rtpFbosTotal = 0;
          let dtuTotal = 0,dtuTaxTotal = 0,dtuFbosTotal = 0;
          let vaTotal = 0,vaTaxTotal = 0,vaFbosTotal = 0;
          deferredRows.forEach((row) => {
            if (!row.isSection && !row.isSubFooter) {
              const rowSgKey = row.SubGroupKey || 'total';
              if (rowSgKey === sgKey) {
                ctaTotal += parseFloat(row['CTAAdjustment']) || 0;
                ctaPostTotal += parseFloat(row['CTAAdjustment_PostApportioned']) || 0;
                ctaTaxTotal += parseFloat(row['CTAAdjustment_TaxEffected']) || 0;
                ctaFbosTotal += parseFloat(row['CTAAdjustment_FBOS']) || 0;
                ebTotal += parseFloat(row['EndingBalance']) || 0;
                ebPostTotal += parseFloat(row['EndingBalance_PostApportioned']) || 0;
                ebTaxTotal += parseFloat(row['EndingBalance_TaxEffected']) || 0;
                ebFbosTotal += parseFloat(row['EndingBalance_FBOS']) || 0;
                deTotal += parseFloat(row['DeferredExpense']) || 0;
                deTaxTotal += parseFloat(row['DeferredExpense_TaxEffected']) || 0;
                deFbosTotal += parseFloat(row['DeferredExpense_FBOS']) || 0;
                trTotal += parseFloat(row['TaxRateChange']) || 0;
                trTaxTotal += parseFloat(row['TaxRateChange_TaxEffected']) || 0;
                trFbosTotal += parseFloat(row['TaxRateChange_FBOS']) || 0;
                caTotal += parseFloat(row['CurrentActivity']) || 0;
                caTaxTotal += parseFloat(row['CurrentActivity_TaxEffected']) || 0;
                caFbosTotal += parseFloat(row['CurrentActivity_FBOS']) || 0;
                woTotal += parseFloat(row['WriteOff']) || 0;
                woTaxTotal += parseFloat(row['WriteOff_TaxEffected']) || 0;
                woFbosTotal += parseFloat(row['WriteOff_FBOS']) || 0;
                rtpTotal += parseFloat(row['ReturnToProvisionRollforward']) || 0;
                rtpTaxTotal += parseFloat(row['ReturnToProvisionRollforward_TaxEffected']) || 0;
                rtpFbosTotal += parseFloat(row['ReturnToProvisionRollforward_FBOS']) || 0;
                dtuTotal += parseFloat(row['DeferredTrueUpRollforward']) || 0;
                dtuTaxTotal += parseFloat(row['DeferredTrueUpRollforward_TaxEffected']) || 0;
                dtuFbosTotal += parseFloat(row['DeferredTrueUpRollforward_FBOS']) || 0;
                vaTotal += parseFloat(row['ValuationAllowance']) || 0;
                vaTaxTotal += parseFloat(row['ValuationAllowance_TaxEffected']) || 0;
                vaFbosTotal += parseFloat(row['ValuationAllowance_FBOS']) || 0;
              }
            }
          });
          grandTotalRow['CTAAdjustment'] = ctaTotal;
          grandTotalRow['CTAAdjustment_PostApportioned'] = ctaPostTotal;
          grandTotalRow['CTAAdjustment_TaxEffected'] = ctaTaxTotal;
          grandTotalRow['CTAAdjustment_FBOS'] = ctaFbosTotal;
          grandTotalRow['EndingBalance'] = ebTotal;
          grandTotalRow['EndingBalance_PostApportioned'] = ebPostTotal;
          grandTotalRow['EndingBalance_TaxEffected'] = ebTaxTotal;
          grandTotalRow['EndingBalance_FBOS'] = ebFbosTotal;
          grandTotalRow['DeferredExpense'] = deTotal;
          grandTotalRow['DeferredExpense_TaxEffected'] = deTaxTotal;
          grandTotalRow['DeferredExpense_FBOS'] = deFbosTotal;
          grandTotalRow['TaxRateChange'] = trTotal;
          grandTotalRow['TaxRateChange_TaxEffected'] = trTaxTotal;
          grandTotalRow['TaxRateChange_FBOS'] = trFbosTotal;
          grandTotalRow['CurrentActivity'] = caTotal;
          grandTotalRow['CurrentActivity_TaxEffected'] = caTaxTotal;
          grandTotalRow['CurrentActivity_FBOS'] = caFbosTotal;
          grandTotalRow['WriteOff'] = woTotal;
          grandTotalRow['WriteOff_TaxEffected'] = woTaxTotal;
          grandTotalRow['WriteOff_FBOS'] = woFbosTotal;
          grandTotalRow['ReturnToProvisionRollforward'] = rtpTotal;
          grandTotalRow['ReturnToProvisionRollforward_TaxEffected'] = rtpTaxTotal;
          grandTotalRow['ReturnToProvisionRollforward_FBOS'] = rtpFbosTotal;
          grandTotalRow['DeferredTrueUpRollforward'] = dtuTotal;
          grandTotalRow['DeferredTrueUpRollforward_TaxEffected'] = dtuTaxTotal;
          grandTotalRow['DeferredTrueUpRollforward_FBOS'] = dtuFbosTotal;
          grandTotalRow['ValuationAllowance'] = vaTotal;
          grandTotalRow['ValuationAllowance_TaxEffected'] = vaTaxTotal;
          grandTotalRow['ValuationAllowance_FBOS'] = vaFbosTotal;
        }
        deferredRows.push(grandTotalRow);
      }); // end topLevelGroups.forEach

      return { deferredRows: annotateDeferredDrilldowns(deferredRows), activityTypes: ACTIVITY_TYPES };
    }

    // ── ENTITY BREAKOUT MODE (default): one section group per entity ──────────
    // Create rows for each entity and category combination
    uniqueEntities.forEach((entityCode) => {
      // Sub-groups for this entity's data (filing breakout within each entity — state breakout is handled above)
      const entityData = data.filter((row) => row.EntityCode === entityCode);
      const entitySubGroups = getSubGroups(entityData);

      entitySubGroups.forEach(({ key: sgKey, label: sgLabel, slice: sgSlice }) => {
        DEFERRED_ROLLFORWARD_HEADERS.forEach((header, headerIndex) => {
          // Find corresponding adjustment type for this category
          const adjustmentType = Object.keys(categoryMapping).find((key) =>
          categoryMapping[key] === header
          );

          if (adjustmentType) {
            // Entity-level sections always use full entity data; state/filing sections use sub-group slice
            const sourceSlice = ENTITY_LEVEL_TYPES.has(adjustmentType) ? entityData : sgSlice;

            // Get adjustments for this category, entity, and sub-group
            const categoryData = sourceSlice.filter((row) =>
            row.AdjustmentSource === adjustmentType || row.AdjustmentType === adjustmentType
            );

            // Only create section if there is data for this category and entity
            if (categoryData.length === 0) {
              return; // Skip this section if no data
            }

            // Add section header: entity - [state/filing -] header (entity-level sections skip the state/filing prefix)
            const sectionLabel = sgLabel && !ENTITY_LEVEL_TYPES.has(adjustmentType) ?
            `${entityCode} - ${sgLabel} - ${header}` :
            `${entityCode} - ${header}`;
            const sectionHeaderRow = {
              id: `section-${entityCode}-${sgKey}-${headerIndex}`,
              EntityCode: '',
              AdjustmentCode: '',
              AdjustmentName: sectionLabel,
              isSection: true
            };
            ACTIVITY_TYPES.forEach((activityType) => {
              sectionHeaderRow[activityType] = null;
              sectionHeaderRow[`${activityType}_TaxEffected`] = null;
            });
            deferredRows.push(sectionHeaderRow);

            // Get unique adjustments in this category/sub-group
            const uniqueAdjustments = [...new Set(categoryData.map((row) => row.AdjustmentCode))].
            filter((code) => code).
            sort();

            // Add adjustment rows for this category, entity, and sub-group
            uniqueAdjustments.forEach((adjustmentCode) => {
              const adjustmentData = categoryData.find((row) =>
              row.AdjustmentCode === adjustmentCode && row.EntityCode === entityCode
              );

              if (adjustmentData) {
                const adjustmentName = adjustmentData.AdjustmentName || '';

                const isEntityLevelAdj = ENTITY_LEVEL_TYPES.has(adjustmentType);
                const adjustmentRow = {
                  id: `${adjustmentType}-${entityCode}-${sgKey}-${adjustmentCode}`,
                  EntityCode: entityCode,
                  AdjustmentCode: adjustmentCode,
                  AdjustmentName: adjustmentName,
                  AdjustmentNamePrefix: '',
                  AdjustmentType: adjustmentType, // Add adjustment type for cell rendering logic
                  SubGroupKey: !isEntityLevelAdj && sgKey !== 'total' ? sgKey : null,
                  SubGroupType: !isEntityLevelAdj && sgKey !== 'total' ? stateGrouping : null,
                  isSection: false
                };

                // Initialize all activity type columns to 0 (pre-apportioned, post-apportioned, state tax-effected, and FBOS)
                ACTIVITY_TYPES.forEach((activityType) => {
                  adjustmentRow[activityType] = 0;
                  adjustmentRow[`${activityType}_PostApportioned`] = 0;
                  adjustmentRow[`${activityType}_TaxEffected`] = 0;
                  adjustmentRow[`${activityType}_FBOS`] = 0;
                });

                // Find all adjustments with this entity/adjustment/sub-group combination and populate based on ActivityType
                // Each unique adjustment (by id or composite key) should only be counted once
                const allMatchingAdjustments = categoryData.filter((row) =>
                row.AdjustmentCode === adjustmentCode && row.EntityCode === entityCode
                );

                allMatchingAdjustments.forEach((adj) => {
                  const activityType = adj.ActivityType;
                  if (activityType && ACTIVITY_TYPES.includes(activityType)) {
                    const amount = selectedCurrency === 'USD' ?
                    parseFloat(adj.USDInput) || parseFloat(adj.USDAmount) || 0 :
                    parseFloat(adj.LocalCurrencyInput) || parseFloat(adj.Amount) || 0;

                    // Always store LC amount for CTA calculations (CTA must use LC values regardless of display currency)
                    const lcAmount = parseFloat(adj.LocalCurrencyInput) || parseFloat(adj.Amount) || 0;

                    // Look up rates for this adjustment's filing code / entity
                    const adjFilingCode = adj.FilingCode;
                    const adjStateInput = stateInputMapByKey.get(`${adjFilingCode}__${entityCode}__${selectedScenario?.scenarioCode}`);
                    const adjEntityInput = entityInputMapByKey.get(`${entityCode}__${selectedScenario?.scenarioCode}`);

                    const apportionmentRate = getDeferredApportionmentRate(adjStateInput, activityType);

                    const rawStateCurrentRate = parseFloat(adjStateInput?.CurrentStateRate) || 0;
                    const stateCurrentRate = rawStateCurrentRate > 1 ? rawStateCurrentRate / 100 : rawStateCurrentRate;

                    const rawBeginDeferredRate = parseFloat(adjEntityInput?.BeginningDeferredTaxRate) || 0;
                    const beginDeferredRate = rawBeginDeferredRate > 1 ? rawBeginDeferredRate / 100 : rawBeginDeferredRate;

                    // ── PRE-APPORTIONED (only STATE PRE-APP types) ──────────────────
                    const isPreAppType = adjustmentType === 'State Pre-App Temp' ||
                    adjustmentType === 'State Pre-App NOL' ||
                    adjustmentType === 'State Pre-App Valuation Allowance' ||
                    adjustmentType === 'GS Temporary' ||
                    adjustmentType === 'Temporary';

                    // GS Temporary / Temporary rows with no matching FilingCode still fan out
                    // across the entity's state inputs in the active breakout scope.
                    const isEntityLevelPreApp = adjustmentType === 'GS Temporary' || adjustmentType === 'Temporary';

                    const isPostAppType = adjustmentType === 'State Post-App Temp' ||
                    adjustmentType === 'State Post-App NOL' ||
                    adjustmentType === 'State Post-App Valuation Allowance';

                    const isStateTaxEffectedType = adjustmentType === 'State Tax Effected Temp' ||
                    adjustmentType === 'State Tax Effected Valuation Allowance';

                    if (isPreAppType) {
                      // PRE-APPORTIONED: raw value
                      adjustmentRow[activityType] += amount;
                      if (!adjustmentRow[`${activityType}_LC`]) adjustmentRow[`${activityType}_LC`] = 0;
                      adjustmentRow[`${activityType}_LC`] += lcAmount;

                      if (isEntityLevelPreApp && !(adjFilingCode && adjStateInput)) {
                        // POST-APPORTIONED: use the scoped entity apportionment rate,
                        // then STATE TAX-EFFECTED: postAmount × stateCurrentRate.
                        const entityStateInput = getScopedEntityStateInputs(entityCode, sgKey)[0];
                        const rate = getDeferredApportionmentRate(entityStateInput, activityType);
                        const rawSR = parseFloat(entityStateInput?.CurrentStateRate) || 0;
                        const sr = rawSR > 1 ? rawSR / 100 : rawSR;
                        const postAmount = amount * rate;
                        const postLCAmount = lcAmount * rate;
                        const taxEffectedAmount = postAmount * sr;
                        const taxEffectedLCAmount = postLCAmount * sr;
                        adjustmentRow[`${activityType}_PostApportioned`] += postAmount;
                        if (!adjustmentRow[`${activityType}_PostApportioned_LC`]) adjustmentRow[`${activityType}_PostApportioned_LC`] = 0;
                        adjustmentRow[`${activityType}_PostApportioned_LC`] += postLCAmount;
                        adjustmentRow[`${activityType}_TaxEffected`] += taxEffectedAmount;
                        if (!adjustmentRow[`${activityType}_TaxEffected_LC`]) adjustmentRow[`${activityType}_TaxEffected_LC`] = 0;
                        adjustmentRow[`${activityType}_TaxEffected_LC`] += taxEffectedLCAmount;
                        // FBOS uses entity begin deferred rate (from EntityInput)
                        const fbosAmount = taxEffectedAmount * -1 * beginDeferredRate;
                        adjustmentRow[`${activityType}_FBOS`] += fbosAmount;
                        if (!adjustmentRow[`${activityType}_FBOS_LC`]) adjustmentRow[`${activityType}_FBOS_LC`] = 0;
                        adjustmentRow[`${activityType}_FBOS_LC`] += taxEffectedLCAmount * -1 * beginDeferredRate;
                      } else if (adjFilingCode && adjStateInput) {
                        // POST-APPORTIONED: pre × apportionment rate (single filing code matched)
                        const postAmount = amount * apportionmentRate;
                        const postLCAmount = lcAmount * apportionmentRate;
                        adjustmentRow[`${activityType}_PostApportioned`] += postAmount;
                        if (!adjustmentRow[`${activityType}_PostApportioned_LC`]) adjustmentRow[`${activityType}_PostApportioned_LC`] = 0;
                        adjustmentRow[`${activityType}_PostApportioned_LC`] += postLCAmount;

                        // STATE TAX-EFFECTED: post × state current rate
                        const taxEffectedAmount = postAmount * stateCurrentRate;
                        const taxEffectedLCAmount = postLCAmount * stateCurrentRate;
                        adjustmentRow[`${activityType}_TaxEffected`] += taxEffectedAmount;
                        if (!adjustmentRow[`${activityType}_TaxEffected_LC`]) adjustmentRow[`${activityType}_TaxEffected_LC`] = 0;
                        adjustmentRow[`${activityType}_TaxEffected_LC`] += taxEffectedLCAmount;

                        // FBOS: tax-effected × -1 × begin deferred rate
                        const fbosAmount = taxEffectedAmount * -1 * beginDeferredRate;
                        adjustmentRow[`${activityType}_FBOS`] += fbosAmount;
                        if (!adjustmentRow[`${activityType}_FBOS_LC`]) adjustmentRow[`${activityType}_FBOS_LC`] = 0;
                        adjustmentRow[`${activityType}_FBOS_LC`] += taxEffectedLCAmount * -1 * beginDeferredRate;
                      } else {
                        // No FilingCode or no matching state input: use the entity's state inputs in the active breakout scope.
                        const entityStateInputsFallback = getScopedEntityStateInputs(entityCode, sgKey);
                        let postAmountFb = 0;
                        let postLCAmountFb = 0;
                        let taxEffectedAmountFb = 0;
                        let taxEffectedLCAmountFb = 0;
                        entityStateInputsFallback.forEach((si) => {
                          const rate = getDeferredApportionmentRate(si, activityType);
                          const rawSR = parseFloat(si.CurrentStateRate) || 0;
                          const sr = rawSR > 1 ? rawSR / 100 : rawSR;
                          const postI = amount * rate;
                          const postLCI = lcAmount * rate;
                          postAmountFb += postI;
                          postLCAmountFb += postLCI;
                          taxEffectedAmountFb += postI * sr;
                          taxEffectedLCAmountFb += postLCI * sr;
                        });
                        adjustmentRow[`${activityType}_PostApportioned`] += postAmountFb;
                        if (!adjustmentRow[`${activityType}_PostApportioned_LC`]) adjustmentRow[`${activityType}_PostApportioned_LC`] = 0;
                        adjustmentRow[`${activityType}_PostApportioned_LC`] += postLCAmountFb;
                        adjustmentRow[`${activityType}_TaxEffected`] += taxEffectedAmountFb;
                        if (!adjustmentRow[`${activityType}_TaxEffected_LC`]) adjustmentRow[`${activityType}_TaxEffected_LC`] = 0;
                        adjustmentRow[`${activityType}_TaxEffected_LC`] += taxEffectedLCAmountFb;
                        const fbosAmountFb = taxEffectedAmountFb * -1 * beginDeferredRate;
                        adjustmentRow[`${activityType}_FBOS`] += fbosAmountFb;
                        if (!adjustmentRow[`${activityType}_FBOS_LC`]) adjustmentRow[`${activityType}_FBOS_LC`] = 0;
                        adjustmentRow[`${activityType}_FBOS_LC`] += taxEffectedLCAmountFb * -1 * beginDeferredRate;
                      }

                    } else if (isPostAppType) {
                      // POST-APPORTIONED: raw value
                      const postAmount = amount;
                      const postLCAmount = lcAmount;
                      adjustmentRow[`${activityType}_PostApportioned`] += postAmount;
                      if (!adjustmentRow[`${activityType}_PostApportioned_LC`]) adjustmentRow[`${activityType}_PostApportioned_LC`] = 0;
                      adjustmentRow[`${activityType}_PostApportioned_LC`] += postLCAmount;

                      // STATE TAX-EFFECTED: post × state current rate
                      const taxEffectedAmount = postAmount * stateCurrentRate;
                      const taxEffectedLCAmount = postLCAmount * stateCurrentRate;
                      adjustmentRow[`${activityType}_TaxEffected`] += taxEffectedAmount;
                      if (!adjustmentRow[`${activityType}_TaxEffected_LC`]) adjustmentRow[`${activityType}_TaxEffected_LC`] = 0;
                      adjustmentRow[`${activityType}_TaxEffected_LC`] += taxEffectedLCAmount;

                      // FBOS: tax-effected × -1 × begin deferred rate
                      adjustmentRow[`${activityType}_FBOS`] += taxEffectedAmount * -1 * beginDeferredRate;
                      if (!adjustmentRow[`${activityType}_FBOS_LC`]) adjustmentRow[`${activityType}_FBOS_LC`] = 0;
                      adjustmentRow[`${activityType}_FBOS_LC`] += taxEffectedLCAmount * -1 * beginDeferredRate;

                    } else if (isStateTaxEffectedType) {
                      // STATE TAX-EFFECTED: raw value (already tax-effected)
                      const taxEffectedAmount = amount;
                      const taxEffectedLCAmount = lcAmount;
                      adjustmentRow[`${activityType}_TaxEffected`] += taxEffectedAmount;
                      if (!adjustmentRow[`${activityType}_TaxEffected_LC`]) adjustmentRow[`${activityType}_TaxEffected_LC`] = 0;
                      adjustmentRow[`${activityType}_TaxEffected_LC`] += taxEffectedLCAmount;

                      // FBOS: tax-effected × -1 × begin deferred rate
                      adjustmentRow[`${activityType}_FBOS`] += taxEffectedAmount * -1 * beginDeferredRate;
                      if (!adjustmentRow[`${activityType}_FBOS_LC`]) adjustmentRow[`${activityType}_FBOS_LC`] = 0;
                      adjustmentRow[`${activityType}_FBOS_LC`] += taxEffectedLCAmount * -1 * beginDeferredRate;

                    }
                  }
                });

                // Calculate Adj. Beginning Balance (sum of Beginning Balance + Return to Provision + Deferred True-Up + Purchase Accounting)
                const beginningBalance = adjustmentRow['BeginningBalance'] || 0;
                const returnToProvision = adjustmentRow['ReturnToProvision'] || 0;
                const deferredTrueUp = adjustmentRow['DeferredTrueUp'] || 0;
                const purchaseAccounting = adjustmentRow['PurchaseAccounting'] || 0;

                // Calculate Adj. Beginning Balance (pre-apportioned)
                adjustmentRow['AdjBeginningBalance'] = beginningBalance + returnToProvision + deferredTrueUp + purchaseAccounting;

                // Calculate Adj. Beginning Balance (post-apportioned) - same as pre-apportioned for now
                adjustmentRow['AdjBeginningBalance_PostApportioned'] =
                (adjustmentRow['BeginningBalance_PostApportioned'] || 0) + (
                adjustmentRow['ReturnToProvision_PostApportioned'] || 0) + (
                adjustmentRow['DeferredTrueUp_PostApportioned'] || 0) + (
                adjustmentRow['PurchaseAccounting_PostApportioned'] || 0);

                // Calculate Adj. Beginning Balance (state tax-effected) - sum of state tax-effected components
                // Note: Each component already has the correct tax rate applied in the loop above
                adjustmentRow['AdjBeginningBalance_TaxEffected'] =
                (adjustmentRow['BeginningBalance_TaxEffected'] || 0) + (
                adjustmentRow['ReturnToProvision_TaxEffected'] || 0) + (
                adjustmentRow['DeferredTrueUp_TaxEffected'] || 0) + (
                adjustmentRow['PurchaseAccounting_TaxEffected'] || 0);

                // Calculate Adj. Beginning Balance (FBOS) - same as pre-apportioned for now
                adjustmentRow['AdjBeginningBalance_FBOS'] =
                (adjustmentRow['BeginningBalance_FBOS'] || 0) + (
                adjustmentRow['ReturnToProvision_FBOS'] || 0) + (
                adjustmentRow['DeferredTrueUp_FBOS'] || 0) + (
                adjustmentRow['PurchaseAccounting_FBOS'] || 0);

                // Calculate Adj. Beginning Balance using LC values for CTA calculation
                const beginningBalanceLC = adjustmentRow['BeginningBalance_LC'] || 0;
                const returnToProvisionLC = adjustmentRow['ReturnToProvision_LC'] || 0;
                const deferredTrueUpLC = adjustmentRow['DeferredTrueUp_LC'] || 0;
                const purchaseAccountingLC = adjustmentRow['PurchaseAccounting_LC'] || 0;
                const adjBeginningBalanceLC = beginningBalanceLC + returnToProvisionLC + deferredTrueUpLC + purchaseAccountingLC;

                // Calculate Adj. Beginning Balance (tax effected) using LC values for CTA calculation
                const adjBeginningBalanceTaxEffectedLC =
                (adjustmentRow['BeginningBalance_TaxEffected_LC'] || 0) + (
                adjustmentRow['ReturnToProvision_TaxEffected_LC'] || 0) + (
                adjustmentRow['DeferredTrueUp_TaxEffected_LC'] || 0) + (
                adjustmentRow['PurchaseAccounting_TaxEffected_LC'] || 0);

                // Calculate CTA Adjustment using the specified formula
                // CTA Adjustment (Pretax) = [(Beginning Balance (pretax)) x (End Spot Rate – Begin Spot Rate)] + [(Current Activity (pretax)) x (End Spot Rate – Average FX Rate)]
                // CTA Adjustment (Tax Effected) = [(Beginning Balance (tax effected)) x (End Spot Rate – Begin Spot Rate)] + [(Current Activity (tax effected)) x (End Spot Rate – Average FX Rate)]
                // NOTE: CTA calculations ALWAYS use LC values from adjustment inputs, regardless of display currency

                // Get FX rates from EntityInput data for this entity
                const entityInput = entityInputMapByKey.get(`${entityCode}__${selectedScenario?.scenarioCode}`);
                const toIndirectFxRate = (rate) => {
                  if (rate == null || rate === '') return 1.0;
                  const parsed = parseFloat(rate);
                  if (!Number.isFinite(parsed) || parsed === 0) return 0;
                  return 1 / parsed;
                };

                const beginSpotRate = toIndirectFxRate(entityInput?.BeginningSpotRate || 1.0);
                const endSpotRate = toIndirectFxRate(entityInput?.EndingSpotRate || 1.0);
                const averageFXRate = toIndirectFxRate(entityInput?.AverageFXRate || 1.0);

                // Calculate Pretax CTA Adjustment using LC values
                // Beginning balance for pretax = Adj. Beginning Balance (pretax) in LC
                const pretaxBeginningBalanceLC = adjBeginningBalanceLC;

                // Current activity for pretax = all columns between Adj. Beginning Balance and Ending Balance (excluding CTA) in LC
                const pretaxCurrentActivityLC =
                (adjustmentRow['CurrentActivityPL_LC'] || 0) + (
                adjustmentRow['OCIAdjustment_LC'] || 0) + (
                adjustmentRow['DeferredExpAdjustment_LC'] || 0) + (
                adjustmentRow['BalanceSheetAdjustment_LC'] || 0) + (
                adjustmentRow['CustomDeferredExpAdjustment_LC'] || 0) + (
                adjustmentRow['CustomBalanceSheetAdjustment_LC'] || 0);

                const pretaxBeginningComponent = pretaxBeginningBalanceLC * (endSpotRate - beginSpotRate);
                const pretaxCurrentComponent = pretaxCurrentActivityLC * (endSpotRate - averageFXRate);
                const ctaAdjustmentPretax = pretaxBeginningComponent + pretaxCurrentComponent;

                // Zero out CTA adjustment when LC currency is selected
                adjustmentRow['CTAAdjustment'] = selectedCurrency === 'LC' ? 0 : ctaAdjustmentPretax;

                // Calculate POST-APPORTIONED CTA Adjustment (same as PRE-APPORTIONED for now)
                adjustmentRow['CTAAdjustment_PostApportioned'] = selectedCurrency === 'LC' ? 0 : ctaAdjustmentPretax;

                // Calculate FBOS CTA Adjustment (same as PRE-APPORTIONED for now)
                adjustmentRow['CTAAdjustment_FBOS'] = selectedCurrency === 'LC' ? 0 : ctaAdjustmentPretax;

                // Calculate State Tax-Effected CTA Adjustment using LC values
                // Beginning balance for state tax-effected = Adj. Beginning Balance (state tax-effected) in LC
                const taxEffectedBeginningBalanceLC = adjBeginningBalanceTaxEffectedLC;

                // Current activity for tax-effected = all tax-effected columns between Adj. Beginning Balance and Ending Balance (excluding CTA) in LC
                const taxEffectedCurrentActivityLC =
                (adjustmentRow['CurrentActivityPL_TaxEffected_LC'] || 0) + (
                adjustmentRow['OCIAdjustment_TaxEffected_LC'] || 0) + (
                adjustmentRow['DeferredExpAdjustment_TaxEffected_LC'] || 0) + (
                adjustmentRow['BalanceSheetAdjustment_TaxEffected_LC'] || 0) + (
                adjustmentRow['CustomDeferredExpAdjustment_TaxEffected_LC'] || 0) + (
                adjustmentRow['CustomBalanceSheetAdjustment_TaxEffected_LC'] || 0);

                const taxEffectedBeginningComponent = taxEffectedBeginningBalanceLC * (endSpotRate - beginSpotRate);
                const taxEffectedCurrentComponent = taxEffectedCurrentActivityLC * (endSpotRate - averageFXRate);
                const ctaAdjustmentTaxEffected = taxEffectedBeginningComponent + taxEffectedCurrentComponent;

                // Zero out CTA adjustment when LC currency is selected
                adjustmentRow['CTAAdjustment_TaxEffected'] = selectedCurrency === 'LC' ? 0 : ctaAdjustmentTaxEffected;

                // Calculate Ending Balance: Adj. Beginning Balance + all columns between Adj. Beginning Balance and Ending Balance
                // Columns after AdjBeginningBalance: CurrentActivityPL, OCIAdjustment, DeferredExpAdjustment, BalanceSheetAdjustment, CustomDeferredExpAdjustment, CustomBalanceSheetAdjustment, CTAAdjustment

                // Pre-Apportioned Ending Balance
                const endingBalancePretax =
                (adjustmentRow['AdjBeginningBalance'] || 0) + (
                adjustmentRow['CurrentActivityPL'] || 0) + (
                adjustmentRow['OCIAdjustment'] || 0) + (
                adjustmentRow['DeferredExpAdjustment'] || 0) + (
                adjustmentRow['BalanceSheetAdjustment'] || 0) + (
                adjustmentRow['CustomDeferredExpAdjustment'] || 0) + (
                adjustmentRow['CustomBalanceSheetAdjustment'] || 0) + (
                adjustmentRow['CTAAdjustment'] || 0);

                // Post-Apportioned Ending Balance
                const endingBalancePostApportioned =
                (adjustmentRow['AdjBeginningBalance_PostApportioned'] || 0) + (
                adjustmentRow['CurrentActivityPL_PostApportioned'] || 0) + (
                adjustmentRow['OCIAdjustment_PostApportioned'] || 0) + (
                adjustmentRow['DeferredExpAdjustment_PostApportioned'] || 0) + (
                adjustmentRow['BalanceSheetAdjustment_PostApportioned'] || 0) + (
                adjustmentRow['CustomDeferredExpAdjustment_PostApportioned'] || 0) + (
                adjustmentRow['CustomBalanceSheetAdjustment_PostApportioned'] || 0) + (
                adjustmentRow['CTAAdjustment_PostApportioned'] || 0);

                // State Tax-Effected Ending Balance
                const endingBalanceTaxEffected =
                (adjustmentRow['AdjBeginningBalance_TaxEffected'] || 0) + (
                adjustmentRow['CurrentActivityPL_TaxEffected'] || 0) + (
                adjustmentRow['OCIAdjustment_TaxEffected'] || 0) + (
                adjustmentRow['DeferredExpAdjustment_TaxEffected'] || 0) + (
                adjustmentRow['BalanceSheetAdjustment_TaxEffected'] || 0) + (
                adjustmentRow['CustomDeferredExpAdjustment_TaxEffected'] || 0) + (
                adjustmentRow['CustomBalanceSheetAdjustment_TaxEffected'] || 0) + (
                adjustmentRow['CTAAdjustment_TaxEffected'] || 0);

                // FBOS Ending Balance
                const endingBalanceFBOS =
                (adjustmentRow['AdjBeginningBalance_FBOS'] || 0) + (
                adjustmentRow['CurrentActivityPL_FBOS'] || 0) + (
                adjustmentRow['OCIAdjustment_FBOS'] || 0) + (
                adjustmentRow['DeferredExpAdjustment_FBOS'] || 0) + (
                adjustmentRow['BalanceSheetAdjustment_FBOS'] || 0) + (
                adjustmentRow['CustomDeferredExpAdjustment_FBOS'] || 0) + (
                adjustmentRow['CustomBalanceSheetAdjustment_FBOS'] || 0) + (
                adjustmentRow['CTAAdjustment_FBOS'] || 0);

                adjustmentRow['EndingBalance'] = endingBalancePretax;
                adjustmentRow['EndingBalance_PostApportioned'] = endingBalancePostApportioned;
                adjustmentRow['EndingBalance_TaxEffected'] = endingBalanceTaxEffected;
                adjustmentRow['EndingBalance_FBOS'] = endingBalanceFBOS;

                // Calculate Tax Rate Change using the specified formula
                // Tax Rate Change = [(pretax Beginning balance + pretax deferred true up + pretax purchase accounting) x (Ending deferred rate - Beginning deferred rate)] 
                //                 + [pretax return to provision x (Ending deferred rate – Prior current rate)]
                //                 + [pretax Current year activity x (Ending deferred rate - Current rate)]  
                //                 + [(pretax deferred exp adjustment + pretax balance sheet adjustment + pretax OCI + pretax custom deferred adjustment + pretax custom balance sheet only adjustment) x (Ending deferred rate - Current rate)]

                // Get tax rates from EntityInput data for this entity
                const entityInputForTaxRate = entityInputMapByKey.get(`${entityCode}__${selectedScenario?.scenarioCode}`);

                // Get deferred and current tax rates (convert from percentage if needed)
                let endingDeferredRate = entityInputForTaxRate?.EndingDeferredTaxRate || 0;
                endingDeferredRate = endingDeferredRate > 1 ? endingDeferredRate / 100 : endingDeferredRate;

                let beginningDeferredRate = entityInputForTaxRate?.BeginningDeferredTaxRate || 0;
                beginningDeferredRate = beginningDeferredRate > 1 ? beginningDeferredRate / 100 : beginningDeferredRate;

                let priorCurrentRate = entityInputForTaxRate?.PriorCurrentTaxRate || 0;
                priorCurrentRate = priorCurrentRate > 1 ? priorCurrentRate / 100 : priorCurrentRate;

                let currentRate = entityInputForTaxRate?.CurrentTaxRate || 0;
                currentRate = currentRate > 1 ? currentRate / 100 : currentRate;

                // Component 1: Beginning balance items
                const beginningBalanceForTaxRate = adjustmentRow['BeginningBalance'] || 0;
                const deferredTrueUpForTaxRate = adjustmentRow['DeferredTrueUp'] || 0;
                const purchaseAccountingForTaxRate = adjustmentRow['PurchaseAccounting'] || 0;
                const taxRateComponent1 = (beginningBalanceForTaxRate + deferredTrueUpForTaxRate + purchaseAccountingForTaxRate) * (endingDeferredRate - beginningDeferredRate);

                // Component 2: Return to provision
                const returnToProvisionForTaxRate = adjustmentRow['ReturnToProvision'] || 0;
                const taxRateComponent2 = returnToProvisionForTaxRate * (endingDeferredRate - priorCurrentRate);

                // Component 3: Current year activity
                const currentYearActivityForTaxRate = adjustmentRow['CurrentActivityPL'] || 0;
                const taxRateComponent3 = currentYearActivityForTaxRate * (endingDeferredRate - currentRate);

                // Component 4: Other adjustments
                const deferredExpAdjForTaxRate = adjustmentRow['DeferredExpAdjustment'] || 0;
                const balanceSheetAdjForTaxRate = adjustmentRow['BalanceSheetAdjustment'] || 0;
                const ociForTaxRate = adjustmentRow['OCIAdjustment'] || 0;
                const customDeferredAdjForTaxRate = adjustmentRow['CustomDeferredExpAdjustment'] || 0;
                const customBalanceSheetAdjForTaxRate = adjustmentRow['CustomBalanceSheetAdjustment'] || 0;
                const taxRateComponent4 = (deferredExpAdjForTaxRate + balanceSheetAdjForTaxRate + ociForTaxRate + customDeferredAdjForTaxRate + customBalanceSheetAdjForTaxRate) * (endingDeferredRate - currentRate);

                // Calculate total Tax Rate Change
                const taxRateChange = taxRateComponent1 + taxRateComponent2 + taxRateComponent3 + taxRateComponent4;

                adjustmentRow['TaxRateChange_TaxEffected'] = taxRateChange;
                adjustmentRow['TaxRateChange'] = -taxRateChange;

                // Note: ReturnToProvision and DeferredTrueUp pretax columns should keep their original pretax values
                // They are already populated correctly in the activity type loop above
                // Do NOT overwrite them with tax-effected values

                // Current Activity - negated tax effected value
                adjustmentRow['CurrentActivity'] = -(adjustmentRow['CurrentActivityPL_TaxEffected'] || 0);

                // Write-Off = (Deferred Exp Adjustment + Custom Deferred Exp Adjustment) tax effected
                const deferredExpAdjustmentTaxEffected = adjustmentRow['DeferredExpAdjustment_TaxEffected'] || 0;
                const customDeferredExpAdjustmentTaxEffected = adjustmentRow['CustomDeferredExpAdjustment_TaxEffected'] || 0;
                adjustmentRow['WriteOff'] = -(deferredExpAdjustmentTaxEffected + customDeferredExpAdjustmentTaxEffected);

                // Create dedicated rollforward columns with negated tax-effected values
                adjustmentRow['ReturnToProvisionRollforward'] = -(adjustmentRow['ReturnToProvision_TaxEffected'] || 0);
                adjustmentRow['DeferredTrueUpRollforward'] = -(adjustmentRow['DeferredTrueUp_TaxEffected'] || 0);

                // Use the displayed column values (actual tax-effected, no pre-negation) for formula components
                const taxRateChangeDisplay = adjustmentRow['TaxRateChange'] || 0;
                const returnToProvisionDisplay = adjustmentRow['ReturnToProvisionRollforward'] || 0;
                const deferredTrueUpDisplay = adjustmentRow['DeferredTrueUpRollforward'] || 0;
                const currentActivityDisplay = adjustmentRow['CurrentActivity'] || 0;
                const writeOffDisplay = adjustmentRow['WriteOff'] || 0;

                // Valuation Allowance - populate for all VALUATION ALLOWANCE sections
                // Formula: -(Tax Rate Change + Current Activity + Deferred True-Up + Return to Provision)
                const isValuationAllowanceSection = header === 'STATE PRE-APPORTIONMENT VALUATION ALLOWANCES' ||
                header === 'STATE POST-APPORTIONED VALUATION ALLOWANCE' ||
                header === 'STATE TAX-EFFECTED VALUATION ALLOWANCE';

                if (isValuationAllowanceSection) {
                  adjustmentRow['ValuationAllowance'] = -(taxRateChangeDisplay +
                  currentActivityDisplay +
                  deferredTrueUpDisplay +
                  returnToProvisionDisplay);
                } else {
                  adjustmentRow['ValuationAllowance'] = 0;
                }

                const valuationAllowance = adjustmentRow['ValuationAllowance'] || 0;

                if (isValuationAllowanceSection) {
                  // For VALUATION ALLOWANCE sections, Deferred Expense equals ValuationAllowance
                  adjustmentRow['DeferredExpense'] = valuationAllowance;

                  // Hide the 5 specific rollforward columns
                  adjustmentRow['WriteOff'] = null;
                  adjustmentRow['TaxRateChange'] = null;
                  adjustmentRow['ReturnToProvisionRollforward'] = null;
                  adjustmentRow['DeferredTrueUpRollforward'] = null;
                  adjustmentRow['CurrentActivity'] = null;
                } else {
                  // DeferredExpense = Tax Rate Change + Return to Provision + Deferred True-Up + Current Activity + Write-Off + Valuation Allowance
                  adjustmentRow['DeferredExpense'] = taxRateChangeDisplay + returnToProvisionDisplay + deferredTrueUpDisplay + currentActivityDisplay + writeOffDisplay + valuationAllowance;
                }

                // Add STATE TAX-EFFECTED versions of deferred expense section columns
                adjustmentRow['CurrentActivity_TaxEffected'] = -(adjustmentRow['CurrentActivityPL_TaxEffected'] || 0);
                adjustmentRow['WriteOff_TaxEffected'] = -(deferredExpAdjustmentTaxEffected + customDeferredExpAdjustmentTaxEffected);
                adjustmentRow['ReturnToProvisionRollforward_TaxEffected'] = -(adjustmentRow['ReturnToProvision_TaxEffected'] || 0);
                adjustmentRow['DeferredTrueUpRollforward_TaxEffected'] = -(adjustmentRow['DeferredTrueUp_TaxEffected'] || 0);

                // Valuation Allowance (STATE TAX-EFFECTED)
                if (isValuationAllowanceSection) {
                  adjustmentRow['ValuationAllowance_TaxEffected'] = -(-taxRateChange +
                  adjustmentRow['CurrentActivity_TaxEffected'] +
                  adjustmentRow['DeferredTrueUpRollforward_TaxEffected'] +
                  adjustmentRow['ReturnToProvisionRollforward_TaxEffected']);
                  adjustmentRow['DeferredExpense_TaxEffected'] = adjustmentRow['ValuationAllowance_TaxEffected'];
                  adjustmentRow['WriteOff_TaxEffected'] = null;
                  adjustmentRow['ReturnToProvisionRollforward_TaxEffected'] = null;
                  adjustmentRow['DeferredTrueUpRollforward_TaxEffected'] = null;
                  adjustmentRow['CurrentActivity_TaxEffected'] = null;
                } else {
                  adjustmentRow['ValuationAllowance_TaxEffected'] = 0;
                  adjustmentRow['DeferredExpense_TaxEffected'] = -taxRateChange +
                  adjustmentRow['ReturnToProvisionRollforward_TaxEffected'] +
                  adjustmentRow['DeferredTrueUpRollforward_TaxEffected'] +
                  adjustmentRow['CurrentActivity_TaxEffected'] +
                  adjustmentRow['WriteOff_TaxEffected'] +
                  adjustmentRow['ValuationAllowance_TaxEffected'];
                }

                // Add FBOS versions of deferred expense section columns - derived from _FBOS activity type values
                const taxRateChangeFBOS = adjustmentRow['TaxRateChange_FBOS'] || 0; // already set above from _FBOS column
                const currentActivityFBOS = adjustmentRow['CurrentActivityPL_FBOS'] || 0;
                const deferredExpAdjustmentFBOS = adjustmentRow['DeferredExpAdjustment_FBOS'] || 0;
                const customDeferredExpAdjustmentFBOS = adjustmentRow['CustomDeferredExpAdjustment_FBOS'] || 0;
                const returnToProvisionFBOS = adjustmentRow['ReturnToProvision_FBOS'] || 0;
                const deferredTrueUpFBOS = adjustmentRow['DeferredTrueUp_FBOS'] || 0;

                adjustmentRow['TaxRateChange_FBOS'] = -taxRateChangeFBOS;
                adjustmentRow['CurrentActivity_FBOS'] = -currentActivityFBOS;
                adjustmentRow['WriteOff_FBOS'] = -(deferredExpAdjustmentFBOS + customDeferredExpAdjustmentFBOS);
                adjustmentRow['ReturnToProvisionRollforward_FBOS'] = -returnToProvisionFBOS;
                adjustmentRow['DeferredTrueUpRollforward_FBOS'] = -deferredTrueUpFBOS;

                if (isValuationAllowanceSection) {
                  adjustmentRow['ValuationAllowance_FBOS'] = -(taxRateChangeFBOS +
                  currentActivityFBOS +
                  deferredTrueUpFBOS +
                  returnToProvisionFBOS);
                  adjustmentRow['DeferredExpense_FBOS'] = adjustmentRow['ValuationAllowance_FBOS'];
                  adjustmentRow['WriteOff_FBOS'] = null;
                  adjustmentRow['ReturnToProvisionRollforward_FBOS'] = null;
                  adjustmentRow['DeferredTrueUpRollforward_FBOS'] = null;
                  adjustmentRow['CurrentActivity_FBOS'] = null;
                } else {
                  adjustmentRow['ValuationAllowance_FBOS'] = 0;
                  adjustmentRow['DeferredExpense_FBOS'] = taxRateChangeFBOS +
                  returnToProvisionFBOS +
                  deferredTrueUpFBOS +
                  currentActivityFBOS +
                  adjustmentRow['WriteOff_FBOS'] +
                  adjustmentRow['ValuationAllowance_FBOS'];
                }

                deferredRows.push(adjustmentRow);
              }
            }); // end uniqueAdjustments.forEach

            // Add section total row for this entity, sub-group, and category
            const sectionTotalRow = {
              id: `total-${entityCode}-${sgKey}-${headerIndex}`,
              EntityCode: '',
              AdjustmentCode: '',
              AdjustmentName: `TOTAL ${header}`,
              isSubFooter: true
            };

            // Calculate totals for each activity type from the section's adjustment rows
            ACTIVITY_TYPES.forEach((activityType) => {
              let total = 0;
              let postApportionedTotal = 0;
              let taxEffectedTotal = 0;
              let fbosTotal = 0;
              // Find all adjustment rows added for this section (since the last section header)
              const sectionStartIndex = deferredRows.findLastIndex((row) => row.isSection);
              const sectionRows = deferredRows.slice(sectionStartIndex + 1);

              sectionRows.forEach((row) => {
                if (!row.isSection && !row.isSubFooter) {
                  total += parseFloat(row[activityType]) || 0;
                  postApportionedTotal += parseFloat(row[`${activityType}_PostApportioned`]) || 0;
                  taxEffectedTotal += parseFloat(row[`${activityType}_TaxEffected`]) || 0;
                  fbosTotal += parseFloat(row[`${activityType}_FBOS`]) || 0;
                }
              });

              // Determine which columns to show in section totals based on adjustment type
              const shouldHidePreAndPost = adjustmentType === 'State Tax Effected Temp' ||
              adjustmentType === 'State Tax Effected Valuation Allowance';

              const shouldHidePre = adjustmentType === 'State Post-App Temp' ||
              adjustmentType === 'State Post-App NOL' ||
              adjustmentType === 'State Post-App Valuation Allowance';

              // Set PRE-APPORTIONED column
              if (shouldHidePreAndPost || shouldHidePre) {
                sectionTotalRow[activityType] = null;
              } else {
                sectionTotalRow[activityType] = total;
              }

              // Set POST-APPORTIONED and FBOS columns
              if (shouldHidePreAndPost) {
                sectionTotalRow[`${activityType}_PostApportioned`] = null;
                sectionTotalRow[`${activityType}_FBOS`] = null;
              } else {
                sectionTotalRow[`${activityType}_PostApportioned`] = postApportionedTotal;
                sectionTotalRow[`${activityType}_FBOS`] = fbosTotal;
              }

              sectionTotalRow[`${activityType}_TaxEffected`] = taxEffectedTotal;
            });

            // Calculate totals for CTA Adjustment and Ending Balance columns
            let ctaAdjustmentTotal = 0;
            let ctaAdjustmentPostApportionedTotal = 0;
            let ctaAdjustmentTaxEffectedTotal = 0;
            let ctaAdjustmentFBOSTotal = 0;
            let endingBalanceTotal = 0;
            let endingBalancePostApportionedTotal = 0;
            let endingBalanceTaxEffectedTotal = 0;
            let endingBalanceFBOSTotal = 0;

            const sectionStartIndex = deferredRows.findLastIndex((row) => row.isSection);
            const sectionRows = deferredRows.slice(sectionStartIndex + 1);

            // Calculate totals for new columns
            let deferredExpenseTotal = 0;
            let deferredExpenseTaxEffectedTotal = 0;
            let deferredExpenseFBOSTotal = 0;
            let taxRateChangeTotal = 0;
            let taxRateChangeTaxEffectedTotal = 0;
            let taxRateChangeFBOSTotal = 0;
            let returnToProvisionTotal = 0;
            let deferredTrueUpTotal = 0;
            let returnToProvisionRollforwardTotal = 0;
            let returnToProvisionRollforwardTaxEffectedTotal = 0;
            let returnToProvisionRollforwardFBOSTotal = 0;
            let deferredTrueUpRollforwardTotal = 0;
            let deferredTrueUpRollforwardTaxEffectedTotal = 0;
            let deferredTrueUpRollforwardFBOSTotal = 0;
            let currentActivityTotal = 0;
            let currentActivityTaxEffectedTotal = 0;
            let currentActivityFBOSTotal = 0;
            let writeOffTotal = 0;
            let writeOffTaxEffectedTotal = 0;
            let writeOffFBOSTotal = 0;
            let valuationAllowanceTotal = 0;
            let valuationAllowanceTaxEffectedTotal = 0;
            let valuationAllowanceFBOSTotal = 0;

            sectionRows.forEach((row) => {
              if (!row.isSection && !row.isSubFooter) {
                ctaAdjustmentTotal += parseFloat(row['CTAAdjustment']) || 0;
                ctaAdjustmentPostApportionedTotal += parseFloat(row['CTAAdjustment_PostApportioned']) || 0;
                ctaAdjustmentTaxEffectedTotal += parseFloat(row['CTAAdjustment_TaxEffected']) || 0;
                ctaAdjustmentFBOSTotal += parseFloat(row['CTAAdjustment_FBOS']) || 0;
                endingBalanceTotal += parseFloat(row['EndingBalance']) || 0;
                endingBalancePostApportionedTotal += parseFloat(row['EndingBalance_PostApportioned']) || 0;
                endingBalanceTaxEffectedTotal += parseFloat(row['EndingBalance_TaxEffected']) || 0;
                endingBalanceFBOSTotal += parseFloat(row['EndingBalance_FBOS']) || 0;

                // Add totals for new columns
                deferredExpenseTotal += parseFloat(row['DeferredExpense']) || 0;
                deferredExpenseTaxEffectedTotal += parseFloat(row['DeferredExpense_TaxEffected']) || 0;
                deferredExpenseFBOSTotal += parseFloat(row['DeferredExpense_FBOS']) || 0;
                taxRateChangeTotal += parseFloat(row['TaxRateChange']) || 0;
                taxRateChangeTaxEffectedTotal += parseFloat(row['TaxRateChange_TaxEffected']) || 0;
                taxRateChangeFBOSTotal += parseFloat(row['TaxRateChange_FBOS']) || 0;
                returnToProvisionTotal += parseFloat(row['ReturnToProvision']) || 0;
                deferredTrueUpTotal += parseFloat(row['DeferredTrueUp']) || 0;
                returnToProvisionRollforwardTotal += parseFloat(row['ReturnToProvisionRollforward']) || 0;
                returnToProvisionRollforwardTaxEffectedTotal += parseFloat(row['ReturnToProvisionRollforward_TaxEffected']) || 0;
                returnToProvisionRollforwardFBOSTotal += parseFloat(row['ReturnToProvisionRollforward_FBOS']) || 0;
                deferredTrueUpRollforwardTotal += parseFloat(row['DeferredTrueUpRollforward']) || 0;
                deferredTrueUpRollforwardTaxEffectedTotal += parseFloat(row['DeferredTrueUpRollforward_TaxEffected']) || 0;
                deferredTrueUpRollforwardFBOSTotal += parseFloat(row['DeferredTrueUpRollforward_FBOS']) || 0;
                currentActivityTotal += parseFloat(row['CurrentActivity']) || 0;
                currentActivityTaxEffectedTotal += parseFloat(row['CurrentActivity_TaxEffected']) || 0;
                currentActivityFBOSTotal += parseFloat(row['CurrentActivity_FBOS']) || 0;
                writeOffTotal += parseFloat(row['WriteOff']) || 0;
                writeOffTaxEffectedTotal += parseFloat(row['WriteOff_TaxEffected']) || 0;
                writeOffFBOSTotal += parseFloat(row['WriteOff_FBOS']) || 0;
                valuationAllowanceTotal += parseFloat(row['ValuationAllowance']) || 0;
                valuationAllowanceTaxEffectedTotal += parseFloat(row['ValuationAllowance_TaxEffected']) || 0;
                valuationAllowanceFBOSTotal += parseFloat(row['ValuationAllowance_FBOS']) || 0;
              }
            });

            sectionTotalRow['CTAAdjustment'] = ctaAdjustmentTotal;
            sectionTotalRow['CTAAdjustment_PostApportioned'] = ctaAdjustmentPostApportionedTotal;
            sectionTotalRow['CTAAdjustment_TaxEffected'] = ctaAdjustmentTaxEffectedTotal;
            sectionTotalRow['CTAAdjustment_FBOS'] = ctaAdjustmentFBOSTotal;
            sectionTotalRow['EndingBalance'] = endingBalanceTotal;
            sectionTotalRow['EndingBalance_PostApportioned'] = endingBalancePostApportionedTotal;
            sectionTotalRow['EndingBalance_TaxEffected'] = endingBalanceTaxEffectedTotal;
            sectionTotalRow['EndingBalance_FBOS'] = endingBalanceFBOSTotal;

            // Set totals for new columns
            sectionTotalRow['DeferredExpense'] = deferredExpenseTotal;
            sectionTotalRow['DeferredExpense_TaxEffected'] = deferredExpenseTaxEffectedTotal;
            sectionTotalRow['DeferredExpense_FBOS'] = deferredExpenseFBOSTotal;
            sectionTotalRow['TaxRateChange'] = taxRateChangeTotal;
            sectionTotalRow['TaxRateChange_TaxEffected'] = taxRateChangeTaxEffectedTotal;
            sectionTotalRow['TaxRateChange_FBOS'] = taxRateChangeFBOSTotal;
            sectionTotalRow['ReturnToProvision'] = returnToProvisionTotal;
            sectionTotalRow['DeferredTrueUp'] = deferredTrueUpTotal;
            sectionTotalRow['ReturnToProvisionRollforward'] = returnToProvisionRollforwardTotal;
            sectionTotalRow['ReturnToProvisionRollforward_TaxEffected'] = returnToProvisionRollforwardTaxEffectedTotal;
            sectionTotalRow['ReturnToProvisionRollforward_FBOS'] = returnToProvisionRollforwardFBOSTotal;
            sectionTotalRow['DeferredTrueUpRollforward'] = deferredTrueUpRollforwardTotal;
            sectionTotalRow['DeferredTrueUpRollforward_TaxEffected'] = deferredTrueUpRollforwardTaxEffectedTotal;
            sectionTotalRow['DeferredTrueUpRollforward_FBOS'] = deferredTrueUpRollforwardFBOSTotal;
            sectionTotalRow['CurrentActivity'] = currentActivityTotal;
            sectionTotalRow['CurrentActivity_TaxEffected'] = currentActivityTaxEffectedTotal;
            sectionTotalRow['CurrentActivity_FBOS'] = currentActivityFBOSTotal;
            sectionTotalRow['WriteOff'] = writeOffTotal;
            sectionTotalRow['WriteOff_TaxEffected'] = writeOffTaxEffectedTotal;
            sectionTotalRow['WriteOff_FBOS'] = writeOffFBOSTotal;
            sectionTotalRow['ValuationAllowance'] = valuationAllowanceTotal;
            sectionTotalRow['ValuationAllowance_TaxEffected'] = valuationAllowanceTaxEffectedTotal;
            sectionTotalRow['ValuationAllowance_FBOS'] = valuationAllowanceFBOSTotal;

            // For VALUATION ALLOWANCE section totals, hide the 5 specific rollforward columns
            const isValuationAllowanceSectionTotal = header === 'STATE PRE-APPORTIONMENT VALUATION ALLOWANCES' ||
            header === 'STATE POST-APPORTIONED VALUATION ALLOWANCE' ||
            header === 'STATE TAX-EFFECTED VALUATION ALLOWANCE';

            if (isValuationAllowanceSectionTotal) {
              sectionTotalRow['TaxRateChange'] = null;
              sectionTotalRow['TaxRateChange_FBOS'] = null;
              sectionTotalRow['ReturnToProvisionRollforward'] = null;
              sectionTotalRow['ReturnToProvisionRollforward_TaxEffected'] = null;
              sectionTotalRow['ReturnToProvisionRollforward_FBOS'] = null;
              sectionTotalRow['DeferredTrueUpRollforward'] = null;
              sectionTotalRow['DeferredTrueUpRollforward_TaxEffected'] = null;
              sectionTotalRow['DeferredTrueUpRollforward_FBOS'] = null;
              sectionTotalRow['CurrentActivity'] = null;
              sectionTotalRow['CurrentActivity_TaxEffected'] = null;
              sectionTotalRow['CurrentActivity_FBOS'] = null;
              sectionTotalRow['WriteOff'] = null;
              sectionTotalRow['WriteOff_TaxEffected'] = null;
              sectionTotalRow['WriteOff_FBOS'] = null;
            }

            deferredRows.push(sectionTotalRow);
          } // end if(adjustmentType)
        }); // end DEFERRED_ROLLFORWARD_HEADERS.forEach
      }); // end entitySubGroups.forEach

      // Add grand total row after all sub-groups for this entity
      const grandTotalRow = {
        id: `grand-total-${entityCode}`,
        EntityCode: '',
        AdjustmentCode: '',
        AdjustmentName: 'GRAND TOTAL TEMPORARY DIFFERENCES',
        isSubFooter: true
      };

      // Calculate grand totals for each activity type from all sections for this entity
      ACTIVITY_TYPES.forEach((activityType) => {
        let grandTotal = 0;
        let grandPostApportionedTotal = 0;
        let grandTaxEffectedTotal = 0;
        let grandFBOSTotal = 0;
        let currentSectionType = null;

        // Find all adjustment rows for this entity (not section headers or totals)
        deferredRows.forEach((row) => {
          // Track current section type
          if (row.isSection && row.AdjustmentName && row.AdjustmentName.includes(entityCode)) {
            // Extract section type from section header
            if (row.AdjustmentName.includes('GAAP TO STAT TEMPORARY DIFFERENCES')) {
              currentSectionType = 'GS Temporary';
            } else if (row.AdjustmentName.includes('STAT TO TAX TEMPORARY DIFFERENCES')) {
              currentSectionType = 'Temporary';
            } else if (row.AdjustmentName.includes('STATE TAX-EFFECTED TEMPORARY DIFFERENCES')) {
              currentSectionType = 'State Tax Effected Temp';
            } else if (row.AdjustmentName.includes('STATE TAX-EFFECTED VALUATION ALLOWANCE')) {
              currentSectionType = 'State Tax Effected Valuation Allowance';
            } else if (row.AdjustmentName.includes('STATE PRE-APPORTIONMENT VALUATION ALLOWANCES')) {
              currentSectionType = 'State Pre-App Valuation Allowance';
            } else if (row.AdjustmentName.includes('STATE POST-APPORTIONED TEMPORARY DIFFERENCES')) {
              currentSectionType = 'State Post-App Temp';
            } else if (row.AdjustmentName.includes('STATE POST-APPORTIONMENT NOL')) {
              currentSectionType = 'State Post-App NOL';
            } else if (row.AdjustmentName.includes('STATE POST-APPORTIONED VALUATION ALLOWANCE')) {
              currentSectionType = 'State Post-App Valuation Allowance';
            } else {
              currentSectionType = 'Other';
            }
          }

          if (!row.isSection && !row.isSubFooter && row.EntityCode === entityCode) {
            // Determine which columns to exclude from grand totals based on section type
            const shouldExcludePreAndPost = currentSectionType === 'State Tax Effected Temp' ||
            currentSectionType === 'State Tax Effected Valuation Allowance';

            const shouldExcludePre = currentSectionType === 'State Post-App Temp' ||
            currentSectionType === 'State Post-App NOL' ||
            currentSectionType === 'State Post-App Valuation Allowance';

            // Add to PRE-APPORTIONED grand total (unless excluded)
            if (!shouldExcludePreAndPost && !shouldExcludePre) {
              grandTotal += parseFloat(row[activityType]) || 0;
            }

            // Add to POST-APPORTIONED and FBOS grand totals (unless excluded)
            if (!shouldExcludePreAndPost) {
              grandPostApportionedTotal += parseFloat(row[`${activityType}_PostApportioned`]) || 0;
              grandFBOSTotal += parseFloat(row[`${activityType}_FBOS`]) || 0;
            }

            // Always add to TAX-EFFECTED grand total
            grandTaxEffectedTotal += parseFloat(row[`${activityType}_TaxEffected`]) || 0;
          }
        });

        grandTotalRow[activityType] = grandTotal;
        grandTotalRow[`${activityType}_PostApportioned`] = grandPostApportionedTotal;
        grandTotalRow[`${activityType}_TaxEffected`] = grandTaxEffectedTotal;
        grandTotalRow[`${activityType}_FBOS`] = grandFBOSTotal;
      });

      // Calculate grand totals for CTA Adjustment, Ending Balance, and new columns
      let grandCtaAdjustmentTotal = 0;
      let grandCtaAdjustmentPostApportionedTotal = 0;
      let grandCtaAdjustmentTaxEffectedTotal = 0;
      let grandCtaAdjustmentFBOSTotal = 0;
      let grandEndingBalanceTotal = 0;
      let grandEndingBalancePostApportionedTotal = 0;
      let grandEndingBalanceTaxEffectedTotal = 0;
      let grandEndingBalanceFBOSTotal = 0;
      let grandDeferredExpenseTotal = 0;
      let grandDeferredExpenseTaxEffectedTotal = 0;
      let grandDeferredExpenseFBOSTotal = 0;
      let grandTaxRateChangeTotal = 0;
      let grandTaxRateChangeTaxEffectedTotal = 0;
      let grandTaxRateChangeFBOSTotal = 0;
      let grandReturnToProvisionTotal = 0;
      let grandDeferredTrueUpTotal = 0;
      let grandReturnToProvisionRollforwardTotal = 0;
      let grandReturnToProvisionRollforwardTaxEffectedTotal = 0;
      let grandReturnToProvisionRollforwardFBOSTotal = 0;
      let grandDeferredTrueUpRollforwardTotal = 0;
      let grandDeferredTrueUpRollforwardTaxEffectedTotal = 0;
      let grandDeferredTrueUpRollforwardFBOSTotal = 0;
      let grandCurrentActivityTotal = 0;
      let grandCurrentActivityTaxEffectedTotal = 0;
      let grandCurrentActivityFBOSTotal = 0;
      let grandWriteOffTotal = 0;
      let grandWriteOffTaxEffectedTotal = 0;
      let grandWriteOffFBOSTotal = 0;
      let grandValuationAllowanceTotal = 0;
      let grandValuationAllowanceTaxEffectedTotal = 0;
      let grandValuationAllowanceFBOSTotal = 0;

      deferredRows.forEach((row) => {
        if (!row.isSection && !row.isSubFooter && row.EntityCode === entityCode) {
          grandCtaAdjustmentTotal += parseFloat(row['CTAAdjustment']) || 0;
          grandCtaAdjustmentPostApportionedTotal += parseFloat(row['CTAAdjustment_PostApportioned']) || 0;
          grandCtaAdjustmentTaxEffectedTotal += parseFloat(row['CTAAdjustment_TaxEffected']) || 0;
          grandCtaAdjustmentFBOSTotal += parseFloat(row['CTAAdjustment_FBOS']) || 0;
          grandEndingBalanceTotal += parseFloat(row['EndingBalance']) || 0;
          grandEndingBalancePostApportionedTotal += parseFloat(row['EndingBalance_PostApportioned']) || 0;
          grandEndingBalanceTaxEffectedTotal += parseFloat(row['EndingBalance_TaxEffected']) || 0;
          grandEndingBalanceFBOSTotal += parseFloat(row['EndingBalance_FBOS']) || 0;

          // Add grand totals for new columns
          grandDeferredExpenseTotal += parseFloat(row['DeferredExpense']) || 0;
          grandDeferredExpenseTaxEffectedTotal += parseFloat(row['DeferredExpense_TaxEffected']) || 0;
          grandDeferredExpenseFBOSTotal += parseFloat(row['DeferredExpense_FBOS']) || 0;
          grandTaxRateChangeTotal += parseFloat(row['TaxRateChange']) || 0;
          grandTaxRateChangeTaxEffectedTotal += parseFloat(row['TaxRateChange_TaxEffected']) || 0;
          grandTaxRateChangeFBOSTotal += parseFloat(row['TaxRateChange_FBOS']) || 0;
          grandReturnToProvisionTotal += parseFloat(row['ReturnToProvision']) || 0;
          grandDeferredTrueUpTotal += parseFloat(row['DeferredTrueUp']) || 0;
          grandReturnToProvisionRollforwardTotal += parseFloat(row['ReturnToProvisionRollforward']) || 0;
          grandReturnToProvisionRollforwardTaxEffectedTotal += parseFloat(row['ReturnToProvisionRollforward_TaxEffected']) || 0;
          grandReturnToProvisionRollforwardFBOSTotal += parseFloat(row['ReturnToProvisionRollforward_FBOS']) || 0;
          grandDeferredTrueUpRollforwardTotal += parseFloat(row['DeferredTrueUpRollforward']) || 0;
          grandDeferredTrueUpRollforwardTaxEffectedTotal += parseFloat(row['DeferredTrueUpRollforward_TaxEffected']) || 0;
          grandDeferredTrueUpRollforwardFBOSTotal += parseFloat(row['DeferredTrueUpRollforward_FBOS']) || 0;
          grandCurrentActivityTotal += parseFloat(row['CurrentActivity']) || 0;
          grandCurrentActivityTaxEffectedTotal += parseFloat(row['CurrentActivity_TaxEffected']) || 0;
          grandCurrentActivityFBOSTotal += parseFloat(row['CurrentActivity_FBOS']) || 0;
          grandWriteOffTotal += parseFloat(row['WriteOff']) || 0;
          grandWriteOffTaxEffectedTotal += parseFloat(row['WriteOff_TaxEffected']) || 0;
          grandWriteOffFBOSTotal += parseFloat(row['WriteOff_FBOS']) || 0;
          grandValuationAllowanceTotal += parseFloat(row['ValuationAllowance']) || 0;
          grandValuationAllowanceTaxEffectedTotal += parseFloat(row['ValuationAllowance_TaxEffected']) || 0;
          grandValuationAllowanceFBOSTotal += parseFloat(row['ValuationAllowance_FBOS']) || 0;
        }
      });

      grandTotalRow['CTAAdjustment'] = grandCtaAdjustmentTotal;
      grandTotalRow['CTAAdjustment_PostApportioned'] = grandCtaAdjustmentPostApportionedTotal;
      grandTotalRow['CTAAdjustment_TaxEffected'] = grandCtaAdjustmentTaxEffectedTotal;
      grandTotalRow['CTAAdjustment_FBOS'] = grandCtaAdjustmentFBOSTotal;
      grandTotalRow['EndingBalance'] = grandEndingBalanceTotal;
      grandTotalRow['EndingBalance_PostApportioned'] = grandEndingBalancePostApportionedTotal;
      grandTotalRow['EndingBalance_TaxEffected'] = grandEndingBalanceTaxEffectedTotal;
      grandTotalRow['EndingBalance_FBOS'] = grandEndingBalanceFBOSTotal;

      // Set grand totals for new columns
      grandTotalRow['DeferredExpense'] = grandDeferredExpenseTotal;
      grandTotalRow['DeferredExpense_TaxEffected'] = grandDeferredExpenseTaxEffectedTotal;
      grandTotalRow['DeferredExpense_FBOS'] = grandDeferredExpenseFBOSTotal;
      grandTotalRow['TaxRateChange'] = grandTaxRateChangeTotal;
      grandTotalRow['TaxRateChange_TaxEffected'] = grandTaxRateChangeTaxEffectedTotal;
      grandTotalRow['TaxRateChange_FBOS'] = grandTaxRateChangeFBOSTotal;
      grandTotalRow['ReturnToProvision'] = grandReturnToProvisionTotal;
      grandTotalRow['DeferredTrueUp'] = grandDeferredTrueUpTotal;
      grandTotalRow['ReturnToProvisionRollforward'] = grandReturnToProvisionRollforwardTotal;
      grandTotalRow['ReturnToProvisionRollforward_TaxEffected'] = grandReturnToProvisionRollforwardTaxEffectedTotal;
      grandTotalRow['ReturnToProvisionRollforward_FBOS'] = grandReturnToProvisionRollforwardFBOSTotal;
      grandTotalRow['DeferredTrueUpRollforward'] = grandDeferredTrueUpRollforwardTotal;
      grandTotalRow['DeferredTrueUpRollforward_TaxEffected'] = grandDeferredTrueUpRollforwardTaxEffectedTotal;
      grandTotalRow['DeferredTrueUpRollforward_FBOS'] = grandDeferredTrueUpRollforwardFBOSTotal;
      grandTotalRow['CurrentActivity'] = grandCurrentActivityTotal;
      grandTotalRow['CurrentActivity_TaxEffected'] = grandCurrentActivityTaxEffectedTotal;
      grandTotalRow['CurrentActivity_FBOS'] = grandCurrentActivityFBOSTotal;
      grandTotalRow['WriteOff'] = grandWriteOffTotal;
      grandTotalRow['WriteOff_TaxEffected'] = grandWriteOffTaxEffectedTotal;
      grandTotalRow['WriteOff_FBOS'] = grandWriteOffFBOSTotal;
      grandTotalRow['ValuationAllowance'] = grandValuationAllowanceTotal;
      grandTotalRow['ValuationAllowance_TaxEffected'] = grandValuationAllowanceTaxEffectedTotal;
      grandTotalRow['ValuationAllowance_FBOS'] = grandValuationAllowanceFBOSTotal;

      deferredRows.push(grandTotalRow);
    }); // end uniqueEntities.forEach

    return { deferredRows: annotateDeferredDrilldowns(deferredRows), activityTypes: ACTIVITY_TYPES };
  };

  // ── GROUP MODE path (defined as an inner function, called below) ─────────────
  const createDeferredRollforwardDataGroupMode = (data, stateGrouping = 'total') => {
    const scenarioStateInputs = stateInputs.filter((si) => si.ScenarioCode === selectedScenario?.scenarioCode);
    const getStateForFiling = (filingCode) => {
      if (!filingCode) return null;
      return masterStates.find((ms) => ms.FilingCode === filingCode)?.State ||
      scenarioStateInputs.find((si) => si.FilingCode === filingCode)?.StateCode ||
      null;
    };
    const getScopedEntityStateInputs = (entityCode, subGroupKey) => {
      const stateArr = Array.isArray(selectedState) ? selectedState : [];
      const filingCodeArr = Array.isArray(selectedFilingCode) ? selectedFilingCode : [];
      let inputs = scenarioStateInputs.filter((si) => si.EntityCode === entityCode);
      if (stateGrouping === 'filing' && subGroupKey && subGroupKey !== 'total') {
        inputs = inputs.filter((si) => si.FilingCode === subGroupKey);
      } else if (stateGrouping === 'state' && subGroupKey && subGroupKey !== 'total') {
        inputs = inputs.filter((si) => getStateForFiling(si.FilingCode) === subGroupKey);
      } else {
        if (filingCodeArr.length > 0) {
          inputs = inputs.filter((si) => filingCodeArr.includes(si.FilingCode));
        }
        if (stateArr.length > 0) {
          inputs = inputs.filter((si) => stateArr.includes(si.StateCode) || stateArr.includes(getStateForFiling(si.FilingCode)));
        }
      }
      return inputs;
    };

    const getSubGroups = (categoryData) => {
      if (stateGrouping === 'filing') {
        const uniqueFilings = [...new Set(categoryData.map((r) => r.FilingCode).filter(Boolean))].sort();
        return uniqueFilings.map((fc) => ({ key: fc, label: fc, slice: categoryData.filter((r) => r.FilingCode === fc) }));
      }
      if (stateGrouping === 'state') {
        const uniqueStates = [...new Set(categoryData.map((r) => getStateForFiling(r.FilingCode)).filter(Boolean))].sort();
        return uniqueStates.map((sc) => ({ key: sc, label: sc, slice: categoryData.filter((r) => getStateForFiling(r.FilingCode) === sc) }));
      }
      return [{ key: 'total', label: '', slice: categoryData }];
    };

    const deferredRows = [];

    const categoryMapping = {
      'GS Temporary': 'GAAP TO STAT TEMPORARY DIFFERENCES',
      'Temporary': 'STAT TO TAX TEMPORARY DIFFERENCES',
      'State Pre-App Temp': 'STATE PRE-APPORTIONMENT TEMPORARY DIFFERENCES',
      'State Pre-App NOL': 'STATE PRE-APPORTIONMENT NOL',
      'State Pre-App Valuation Allowance': 'STATE PRE-APPORTIONMENT VALUATION ALLOWANCES',
      'State Post-App Temp': 'STATE POST-APPORTIONED TEMPORARY DIFFERENCES',
      'State Post-App NOL': 'STATE POST-APPORTIONMENT NOL',
      'State Post-App Valuation Allowance': 'STATE POST-APPORTIONED VALUATION ALLOWANCE',
      'State Tax Effected Temp': 'STATE TAX-EFFECTED TEMPORARY DIFFERENCES',
      'State Tax Effected Valuation Allowance': 'STATE TAX-EFFECTED VALUATION ALLOWANCE'
    };

    const ENTITY_LEVEL_TYPES = new Set(['GS Temporary', 'Temporary']);

    // ── STATE BREAKOUT: state is the top-level organizer (PA, DE, etc.) ─────────
    if (stateGrouping === 'state') {
      const topLevelGroups = getSubGroups(data);
      topLevelGroups.forEach(({ key: sgKey, label: sgLabel, slice: sgSlice }) => {
        DEFERRED_ROLLFORWARD_HEADERS.forEach((header, headerIndex) => {
          const adjustmentType = Object.keys(categoryMapping).find((key) => categoryMapping[key] === header);
          if (!adjustmentType) return;
          const sourceSlice = ENTITY_LEVEL_TYPES.has(adjustmentType) ? data : sgSlice;
          const categoryData = sourceSlice.filter((row) =>
          row.AdjustmentSource === adjustmentType || row.AdjustmentType === adjustmentType
          );
          if (categoryData.length === 0) return;
          const sectionLabel = sgLabel && !ENTITY_LEVEL_TYPES.has(adjustmentType) ?
          `${sgLabel} - ${header}` :
          header;
          const sectionHeaderRow = {
            id: `section-group-state-${sgKey}-${headerIndex}`,
            EntityCode: '',
            AdjustmentCode: '',
            AdjustmentName: sectionLabel,
            isSection: true
          };
          ACTIVITY_TYPES.forEach((at) => {
            sectionHeaderRow[at] = null;
            sectionHeaderRow[`${at}_TaxEffected`] = null;
          });
          deferredRows.push(sectionHeaderRow);
          const uniqueAdjustments = [...new Set(categoryData.map((row) => row.AdjustmentCode))].filter(Boolean).sort();
          uniqueAdjustments.forEach((adjustmentCode) => {
            const firstRow = categoryData.find((row) => row.AdjustmentCode === adjustmentCode);
            if (!firstRow) return;
            const adjustmentRow = {
              id: `group-state-${sgKey}-${adjustmentType}-${adjustmentCode}`,
              EntityCode: '',
              AdjustmentCode: adjustmentCode,
              AdjustmentName: firstRow.AdjustmentName || '',
              AdjustmentNamePrefix: '',
              AdjustmentType: adjustmentType,
              SubGroupKey: !ENTITY_LEVEL_TYPES.has(adjustmentType) && sgKey !== 'total' ? sgKey : null,
              SubGroupType: !ENTITY_LEVEL_TYPES.has(adjustmentType) && sgKey !== 'total' ? stateGrouping : null,
              isSection: false
            };
            ACTIVITY_TYPES.forEach((at) => {
              adjustmentRow[at] = 0;
              adjustmentRow[`${at}_PostApportioned`] = 0;
              adjustmentRow[`${at}_TaxEffected`] = 0;
              adjustmentRow[`${at}_FBOS`] = 0;
            });
            categoryData.filter((row) => row.AdjustmentCode === adjustmentCode).forEach((adj) => {
              const entityCode = adj.EntityCode;
              const adjFilingCode = adj.FilingCode;
              const adjStateInput = stateInputMapByKey.get(`${adjFilingCode}__${entityCode}__${selectedScenario?.scenarioCode}`);
              const adjEntityInput = entityInputMapByKey.get(`${entityCode}__${selectedScenario?.scenarioCode}`);
              const activityType = adj.ActivityType;
              if (!activityType || !ACTIVITY_TYPES.includes(activityType)) return;
              const apportionmentRate = getDeferredApportionmentRate(adjStateInput, activityType);
              const rawStateCurrentRate = parseFloat(adjStateInput?.CurrentStateRate) || 0;
              const stateRate = rawStateCurrentRate > 1 ? rawStateCurrentRate / 100 : rawStateCurrentRate;
              const rawBeginDeferredRate = parseFloat(adjEntityInput?.BeginningDeferredTaxRate) || 0;
              const beginDeferredRate = rawBeginDeferredRate > 1 ? rawBeginDeferredRate / 100 : rawBeginDeferredRate;
              const amount = selectedCurrency === 'USD' ?
              parseFloat(adj.USDInput) || parseFloat(adj.USDAmount) || 0 :
              parseFloat(adj.LocalCurrencyInput) || parseFloat(adj.Amount) || 0;
              const isPreAppType = adjustmentType === 'State Pre-App Temp' || adjustmentType === 'State Pre-App NOL' || adjustmentType === 'State Pre-App Valuation Allowance';
              const isEntityLevelType = ENTITY_LEVEL_TYPES.has(adjustmentType);
              const isPostAppType = adjustmentType === 'State Post-App Temp' || adjustmentType === 'State Post-App NOL' || adjustmentType === 'State Post-App Valuation Allowance';
              const isTaxEffectedType = adjustmentType === 'State Tax Effected Temp' || adjustmentType === 'State Tax Effected Valuation Allowance';
              if (isEntityLevelType && !(adjFilingCode && adjStateInput)) {
                adjustmentRow[activityType] += amount;
                const entityStateInput = getScopedEntityStateInputs(entityCode, sgKey)[0];
                const rate = getDeferredApportionmentRate(entityStateInput, activityType);
                const rawSR = parseFloat(entityStateInput?.CurrentStateRate) || 0;
                const sr = rawSR > 1 ? rawSR / 100 : rawSR;
                const postAmount = amount * rate;
                const taxEffectedAmount = postAmount * sr;
                adjustmentRow[`${activityType}_PostApportioned`] += postAmount;
                adjustmentRow[`${activityType}_TaxEffected`] += taxEffectedAmount;
                adjustmentRow[`${activityType}_FBOS`] += taxEffectedAmount * -1 * beginDeferredRate;
              } else if (isPreAppType) {
                adjustmentRow[activityType] += amount;
                const postAmount = amount * apportionmentRate;
                adjustmentRow[`${activityType}_PostApportioned`] += postAmount;
                const taxEffectedAmount = postAmount * stateRate;
                adjustmentRow[`${activityType}_TaxEffected`] += taxEffectedAmount;
                adjustmentRow[`${activityType}_FBOS`] += taxEffectedAmount * -1 * beginDeferredRate;
              } else if (isPostAppType) {
                const postAmount = amount;
                adjustmentRow[`${activityType}_PostApportioned`] += postAmount;
                const taxEffectedAmount = postAmount * stateRate;
                adjustmentRow[`${activityType}_TaxEffected`] += taxEffectedAmount;
                adjustmentRow[`${activityType}_FBOS`] += taxEffectedAmount * -1 * beginDeferredRate;
              } else if (isTaxEffectedType) {
                adjustmentRow[`${activityType}_TaxEffected`] += amount;
                adjustmentRow[`${activityType}_FBOS`] += amount * -1 * beginDeferredRate;
              }
            });
            deferredRows.push(adjustmentRow);
          });
          const sectionTotalRow = {
            id: `total-group-state-${sgKey}-${headerIndex}`,
            EntityCode: '',
            AdjustmentCode: '',
            AdjustmentName: `TOTAL ${header}`,
            isSubFooter: true
          };
          ACTIVITY_TYPES.forEach((at) => {
            let total = 0,postTotal = 0,taxTotal = 0,fbosTotal = 0;
            const sectionStartIndex = deferredRows.findLastIndex((row) => row.isSection);
            deferredRows.slice(sectionStartIndex + 1).forEach((row) => {
              if (!row.isSection && !row.isSubFooter) {
                total += parseFloat(row[at]) || 0;
                postTotal += parseFloat(row[`${at}_PostApportioned`]) || 0;
                taxTotal += parseFloat(row[`${at}_TaxEffected`]) || 0;
                fbosTotal += parseFloat(row[`${at}_FBOS`]) || 0;
              }
            });
            sectionTotalRow[at] = total;
            sectionTotalRow[`${at}_PostApportioned`] = postTotal;
            sectionTotalRow[`${at}_TaxEffected`] = taxTotal;
            sectionTotalRow[`${at}_FBOS`] = fbosTotal;
          });
          deferredRows.push(sectionTotalRow);
        });
        const grandTotalRow = {
          id: `grand-total-group-state-${sgKey}`,
          EntityCode: '',
          AdjustmentCode: '',
          AdjustmentName: 'GRAND TOTAL TEMPORARY DIFFERENCES',
          isSubFooter: true
        };
        ACTIVITY_TYPES.forEach((at) => {
          let grandTotal = 0,grandPost = 0,grandTax = 0,grandFbos = 0;
          deferredRows.forEach((row) => {
            if (!row.isSection && !row.isSubFooter) {
              const rowSgKey = row.SubGroupKey || 'total';
              if (rowSgKey === sgKey) {
                grandTotal += parseFloat(row[at]) || 0;
                grandPost += parseFloat(row[`${at}_PostApportioned`]) || 0;
                grandTax += parseFloat(row[`${at}_TaxEffected`]) || 0;
                grandFbos += parseFloat(row[`${at}_FBOS`]) || 0;
              }
            }
          });
          grandTotalRow[at] = grandTotal;
          grandTotalRow[`${at}_PostApportioned`] = grandPost;
          grandTotalRow[`${at}_TaxEffected`] = grandTax;
          grandTotalRow[`${at}_FBOS`] = grandFbos;
        });
        deferredRows.push(grandTotalRow);
      });
      return { deferredRows: annotateDeferredDrilldowns(deferredRows), activityTypes: ACTIVITY_TYPES };
    }

    // Resolve each entity group's entityList (UUIDs) → EntityCodes
    const resolvedGroups = entityGroups.map((group) => {
      const groupEntityCodes = (group.entityList || []).
      map((entityId) => masterEntities.find((e) => e.id === entityId)?.EntityCode).
      filter(Boolean);
      return { group, groupEntityCodes };
    }).filter(({ groupEntityCodes }) => groupEntityCodes.length > 0);

    resolvedGroups.forEach(({ group, groupEntityCodes }) => {
      const groupName = group.name;
      const groupData = data.filter((row) => groupEntityCodes.includes(row.EntityCode));
      if (groupData.length === 0) return;

      const groupSubGroups = getSubGroups(groupData);

      groupSubGroups.forEach(({ key: sgKey, label: sgLabel, slice: sgSlice }) => {
        DEFERRED_ROLLFORWARD_HEADERS.forEach((header, headerIndex) => {
          const adjustmentType = Object.keys(categoryMapping).find((key) => categoryMapping[key] === header);
          if (!adjustmentType) return;

          const sourceSlice = ENTITY_LEVEL_TYPES.has(adjustmentType) ? groupData : sgSlice;
          const categoryData = sourceSlice.filter((row) =>
          row.AdjustmentSource === adjustmentType || row.AdjustmentType === adjustmentType
          );
          if (categoryData.length === 0) return;

          const sectionLabel = sgLabel && !ENTITY_LEVEL_TYPES.has(adjustmentType) ?
          `${groupName} - ${sgLabel} - ${header}` :
          `${groupName} - ${header}`;
          const sectionHeaderRow = {
            id: `section-group-${groupName}-${sgKey}-${headerIndex}`,
            EntityCode: '',
            AdjustmentCode: '',
            AdjustmentName: sectionLabel,
            isSection: true
          };
          ACTIVITY_TYPES.forEach((at) => {
            sectionHeaderRow[at] = null;
            sectionHeaderRow[`${at}_TaxEffected`] = null;
          });
          deferredRows.push(sectionHeaderRow);

          const uniqueAdjustments = [...new Set(categoryData.map((row) => row.AdjustmentCode))].filter(Boolean).sort();

          uniqueAdjustments.forEach((adjustmentCode) => {
            const firstRow = categoryData.find((row) => row.AdjustmentCode === adjustmentCode);
            if (!firstRow) return;

            const adjustmentRow = {
              id: `group-${groupName}-${sgKey}-${adjustmentType}-${adjustmentCode}`,
              EntityCode: '',
              GroupCode: groupName,
              AdjustmentCode: adjustmentCode,
              AdjustmentName: firstRow.AdjustmentName || '',
              AdjustmentNamePrefix: '',
              AdjustmentType: adjustmentType,
              SubGroupKey: !ENTITY_LEVEL_TYPES.has(adjustmentType) && sgKey !== 'total' ? sgKey : null,
              SubGroupType: !ENTITY_LEVEL_TYPES.has(adjustmentType) && sgKey !== 'total' ? stateGrouping : null,
              isSection: false
            };
            ACTIVITY_TYPES.forEach((at) => {
              adjustmentRow[at] = 0;
              adjustmentRow[`${at}_PostApportioned`] = 0;
              adjustmentRow[`${at}_TaxEffected`] = 0;
              adjustmentRow[`${at}_FBOS`] = 0;
            });

            const adjRows = categoryData.filter((row) => row.AdjustmentCode === adjustmentCode);

            adjRows.forEach((adj) => {
              const entityCode = adj.EntityCode;
              const adjFilingCode = adj.FilingCode;
              const adjStateInput = stateInputMapByKey.get(`${adjFilingCode}__${entityCode}__${selectedScenario?.scenarioCode}`);
              const adjEntityInput = entityInputs.find(
                (ei) => ei.EntityCode === entityCode && ei.ScenarioCode === selectedScenario?.scenarioCode
              );

              const amount = selectedCurrency === 'USD' ?
              parseFloat(adj.USDInput) || parseFloat(adj.USDAmount) || 0 :
              parseFloat(adj.LocalCurrencyInput) || parseFloat(adj.Amount) || 0;
              const lcAmount = parseFloat(adj.LocalCurrencyInput) || parseFloat(adj.Amount) || 0;

              const apportionmentRate = getDeferredApportionmentRate(adjStateInput, adj.ActivityType);
              const rawStateCurrentRate = parseFloat(adjStateInput?.CurrentStateRate) || 0;
              const stateCurrentRate = rawStateCurrentRate > 1 ? rawStateCurrentRate / 100 : rawStateCurrentRate;
              const rawBeginDeferredRate = parseFloat(adjEntityInput?.BeginningDeferredTaxRate) || 0;
              const beginDeferredRate = rawBeginDeferredRate > 1 ? rawBeginDeferredRate / 100 : rawBeginDeferredRate;

              const isPreAppType = adjustmentType === 'State Pre-App Temp' ||
              adjustmentType === 'State Pre-App NOL' ||
              adjustmentType === 'State Pre-App Valuation Allowance' ||
              adjustmentType === 'GS Temporary' ||
              adjustmentType === 'Temporary';
              const isEntityLevelPreApp = adjustmentType === 'GS Temporary' || adjustmentType === 'Temporary';
              const isPostAppType = adjustmentType === 'State Post-App Temp' ||
              adjustmentType === 'State Post-App NOL' ||
              adjustmentType === 'State Post-App Valuation Allowance';
              const isStateTaxEffectedType = adjustmentType === 'State Tax Effected Temp' ||
              adjustmentType === 'State Tax Effected Valuation Allowance';

              const activityType = adj.ActivityType;
              if (!activityType || !ACTIVITY_TYPES.includes(activityType)) return;

              if (isPreAppType) {
                adjustmentRow[activityType] += amount;
                if (!adjustmentRow[`${activityType}_LC`]) adjustmentRow[`${activityType}_LC`] = 0;
                adjustmentRow[`${activityType}_LC`] += lcAmount;

                const entityStateInputs = getScopedEntityStateInputs(entityCode, sgKey);

                if ((isEntityLevelPreApp && !(adjFilingCode && adjStateInput)) || !adjFilingCode || !adjStateInput) {
                  // Use the scoped entity apportionment rate when no filing-specific rate is available.
                  const entityStateInput = entityStateInputs[0];
                  const rate = getDeferredApportionmentRate(entityStateInput, activityType);
                  const rawSR = parseFloat(entityStateInput?.CurrentStateRate) || 0;
                  const sr = rawSR > 1 ? rawSR / 100 : rawSR;
                  const postAmount = amount * rate;
                  const postLCAmount = lcAmount * rate;
                  const taxEffectedAmount = postAmount * sr;
                  const taxEffectedLCAmount = postLCAmount * sr;
                  adjustmentRow[`${activityType}_PostApportioned`] += postAmount;
                  if (!adjustmentRow[`${activityType}_PostApportioned_LC`]) adjustmentRow[`${activityType}_PostApportioned_LC`] = 0;
                  adjustmentRow[`${activityType}_PostApportioned_LC`] += postLCAmount;
                  adjustmentRow[`${activityType}_TaxEffected`] += taxEffectedAmount;
                  if (!adjustmentRow[`${activityType}_TaxEffected_LC`]) adjustmentRow[`${activityType}_TaxEffected_LC`] = 0;
                  adjustmentRow[`${activityType}_TaxEffected_LC`] += taxEffectedLCAmount;
                  const fbosAmount = taxEffectedAmount * -1 * beginDeferredRate;
                  adjustmentRow[`${activityType}_FBOS`] += fbosAmount;
                  if (!adjustmentRow[`${activityType}_FBOS_LC`]) adjustmentRow[`${activityType}_FBOS_LC`] = 0;
                  adjustmentRow[`${activityType}_FBOS_LC`] += taxEffectedLCAmount * -1 * beginDeferredRate;
                } else {
                  // Single filing code matched
                  const postAmount = amount * apportionmentRate;
                  const postLCAmount = lcAmount * apportionmentRate;
                  adjustmentRow[`${activityType}_PostApportioned`] += postAmount;
                  if (!adjustmentRow[`${activityType}_PostApportioned_LC`]) adjustmentRow[`${activityType}_PostApportioned_LC`] = 0;
                  adjustmentRow[`${activityType}_PostApportioned_LC`] += postLCAmount;
                  const taxEffectedAmount = postAmount * stateCurrentRate;
                  const taxEffectedLCAmount = postLCAmount * stateCurrentRate;
                  adjustmentRow[`${activityType}_TaxEffected`] += taxEffectedAmount;
                  if (!adjustmentRow[`${activityType}_TaxEffected_LC`]) adjustmentRow[`${activityType}_TaxEffected_LC`] = 0;
                  adjustmentRow[`${activityType}_TaxEffected_LC`] += taxEffectedLCAmount;
                  const fbosAmount = taxEffectedAmount * -1 * beginDeferredRate;
                  adjustmentRow[`${activityType}_FBOS`] += fbosAmount;
                  if (!adjustmentRow[`${activityType}_FBOS_LC`]) adjustmentRow[`${activityType}_FBOS_LC`] = 0;
                  adjustmentRow[`${activityType}_FBOS_LC`] += taxEffectedLCAmount * -1 * beginDeferredRate;
                }
              } else if (isPostAppType) {
                const postAmount = amount;
                const postLCAmount = lcAmount;
                adjustmentRow[`${activityType}_PostApportioned`] += postAmount;
                if (!adjustmentRow[`${activityType}_PostApportioned_LC`]) adjustmentRow[`${activityType}_PostApportioned_LC`] = 0;
                adjustmentRow[`${activityType}_PostApportioned_LC`] += postLCAmount;
                const taxEffectedAmount = postAmount * stateCurrentRate;
                const taxEffectedLCAmount = postLCAmount * stateCurrentRate;
                adjustmentRow[`${activityType}_TaxEffected`] += taxEffectedAmount;
                if (!adjustmentRow[`${activityType}_TaxEffected_LC`]) adjustmentRow[`${activityType}_TaxEffected_LC`] = 0;
                adjustmentRow[`${activityType}_TaxEffected_LC`] += taxEffectedLCAmount;
                adjustmentRow[`${activityType}_FBOS`] += taxEffectedAmount * -1 * beginDeferredRate;
                if (!adjustmentRow[`${activityType}_FBOS_LC`]) adjustmentRow[`${activityType}_FBOS_LC`] = 0;
                adjustmentRow[`${activityType}_FBOS_LC`] += taxEffectedLCAmount * -1 * beginDeferredRate;
              } else if (isStateTaxEffectedType) {
                adjustmentRow[`${activityType}_TaxEffected`] += amount;
                if (!adjustmentRow[`${activityType}_TaxEffected_LC`]) adjustmentRow[`${activityType}_TaxEffected_LC`] = 0;
                adjustmentRow[`${activityType}_TaxEffected_LC`] += lcAmount;
                adjustmentRow[`${activityType}_FBOS`] += amount * -1 * beginDeferredRate;
                if (!adjustmentRow[`${activityType}_FBOS_LC`]) adjustmentRow[`${activityType}_FBOS_LC`] = 0;
                adjustmentRow[`${activityType}_FBOS_LC`] += lcAmount * -1 * beginDeferredRate;
              }
            });

            // ── Post-loop rollforward (same as entity-breakout) ──────────────
            adjustmentRow['AdjBeginningBalance'] =
            (adjustmentRow['BeginningBalance'] || 0) + (adjustmentRow['ReturnToProvision'] || 0) + (
            adjustmentRow['DeferredTrueUp'] || 0) + (adjustmentRow['PurchaseAccounting'] || 0);
            adjustmentRow['AdjBeginningBalance_PostApportioned'] =
            (adjustmentRow['BeginningBalance_PostApportioned'] || 0) + (adjustmentRow['ReturnToProvision_PostApportioned'] || 0) + (
            adjustmentRow['DeferredTrueUp_PostApportioned'] || 0) + (adjustmentRow['PurchaseAccounting_PostApportioned'] || 0);
            adjustmentRow['AdjBeginningBalance_TaxEffected'] =
            (adjustmentRow['BeginningBalance_TaxEffected'] || 0) + (adjustmentRow['ReturnToProvision_TaxEffected'] || 0) + (
            adjustmentRow['DeferredTrueUp_TaxEffected'] || 0) + (adjustmentRow['PurchaseAccounting_TaxEffected'] || 0);
            adjustmentRow['AdjBeginningBalance_FBOS'] =
            (adjustmentRow['BeginningBalance_FBOS'] || 0) + (adjustmentRow['ReturnToProvision_FBOS'] || 0) + (
            adjustmentRow['DeferredTrueUp_FBOS'] || 0) + (adjustmentRow['PurchaseAccounting_FBOS'] || 0);

            // CTA = 0 for group mode (multiple entities, no single FX rate)
            adjustmentRow['CTAAdjustment'] = 0;
            adjustmentRow['CTAAdjustment_PostApportioned'] = 0;
            adjustmentRow['CTAAdjustment_TaxEffected'] = 0;
            adjustmentRow['CTAAdjustment_FBOS'] = 0;

            adjustmentRow['EndingBalance'] =
            (adjustmentRow['AdjBeginningBalance'] || 0) + (adjustmentRow['CurrentActivityPL'] || 0) + (
            adjustmentRow['OCIAdjustment'] || 0) + (adjustmentRow['DeferredExpAdjustment'] || 0) + (
            adjustmentRow['BalanceSheetAdjustment'] || 0) + (adjustmentRow['CustomDeferredExpAdjustment'] || 0) + (
            adjustmentRow['CustomBalanceSheetAdjustment'] || 0);
            adjustmentRow['EndingBalance_PostApportioned'] =
            (adjustmentRow['AdjBeginningBalance_PostApportioned'] || 0) + (adjustmentRow['CurrentActivityPL_PostApportioned'] || 0) + (
            adjustmentRow['OCIAdjustment_PostApportioned'] || 0) + (adjustmentRow['DeferredExpAdjustment_PostApportioned'] || 0) + (
            adjustmentRow['BalanceSheetAdjustment_PostApportioned'] || 0) + (adjustmentRow['CustomDeferredExpAdjustment_PostApportioned'] || 0) + (
            adjustmentRow['CustomBalanceSheetAdjustment_PostApportioned'] || 0);
            adjustmentRow['EndingBalance_TaxEffected'] =
            (adjustmentRow['AdjBeginningBalance_TaxEffected'] || 0) + (adjustmentRow['CurrentActivityPL_TaxEffected'] || 0) + (
            adjustmentRow['OCIAdjustment_TaxEffected'] || 0) + (adjustmentRow['DeferredExpAdjustment_TaxEffected'] || 0) + (
            adjustmentRow['BalanceSheetAdjustment_TaxEffected'] || 0) + (adjustmentRow['CustomDeferredExpAdjustment_TaxEffected'] || 0) + (
            adjustmentRow['CustomBalanceSheetAdjustment_TaxEffected'] || 0);
            adjustmentRow['EndingBalance_FBOS'] =
            (adjustmentRow['AdjBeginningBalance_FBOS'] || 0) + (adjustmentRow['CurrentActivityPL_FBOS'] || 0) + (
            adjustmentRow['OCIAdjustment_FBOS'] || 0) + (adjustmentRow['DeferredExpAdjustment_FBOS'] || 0) + (
            adjustmentRow['BalanceSheetAdjustment_FBOS'] || 0) + (adjustmentRow['CustomDeferredExpAdjustment_FBOS'] || 0) + (
            adjustmentRow['CustomBalanceSheetAdjustment_FBOS'] || 0);

            // TaxRateChange = 0 (no single entity)
            adjustmentRow['TaxRateChange'] = 0;
            adjustmentRow['TaxRateChange_TaxEffected'] = 0;
            adjustmentRow['TaxRateChange_FBOS'] = 0;

            adjustmentRow['CurrentActivity'] = adjustmentRow['CurrentActivityPL_TaxEffected'] || 0;
            adjustmentRow['CurrentActivity_TaxEffected'] = adjustmentRow['CurrentActivityPL_TaxEffected'] || 0;
            adjustmentRow['CurrentActivity_FBOS'] = adjustmentRow['CurrentActivityPL_FBOS'] || 0;

            const grpDeferredExpTe = adjustmentRow['DeferredExpAdjustment_TaxEffected'] || 0;
            const grpCustomDeferredExpTe = adjustmentRow['CustomDeferredExpAdjustment_TaxEffected'] || 0;
            const grpDeferredExpFbos = adjustmentRow['DeferredExpAdjustment_FBOS'] || 0;
            const grpCustomDeferredExpFbos = adjustmentRow['CustomDeferredExpAdjustment_FBOS'] || 0;
            adjustmentRow['WriteOff'] = -(grpDeferredExpTe + grpCustomDeferredExpTe);
            adjustmentRow['WriteOff_TaxEffected'] = -(grpDeferredExpTe + grpCustomDeferredExpTe);
            adjustmentRow['WriteOff_FBOS'] = -(grpDeferredExpFbos + grpCustomDeferredExpFbos);

            adjustmentRow['ReturnToProvisionRollforward'] = adjustmentRow['ReturnToProvision_TaxEffected'] || 0;
            adjustmentRow['ReturnToProvisionRollforward_TaxEffected'] = adjustmentRow['ReturnToProvision_TaxEffected'] || 0;
            adjustmentRow['ReturnToProvisionRollforward_FBOS'] = adjustmentRow['ReturnToProvision_FBOS'] || 0;
            adjustmentRow['DeferredTrueUpRollforward'] = adjustmentRow['DeferredTrueUp_TaxEffected'] || 0;
            adjustmentRow['DeferredTrueUpRollforward_TaxEffected'] = adjustmentRow['DeferredTrueUp_TaxEffected'] || 0;
            adjustmentRow['DeferredTrueUpRollforward_FBOS'] = adjustmentRow['DeferredTrueUp_FBOS'] || 0;

            const grpIsVA = header === 'STATE PRE-APPORTIONMENT VALUATION ALLOWANCES' ||
            header === 'STATE POST-APPORTIONED VALUATION ALLOWANCE' ||
            header === 'STATE TAX-EFFECTED VALUATION ALLOWANCE';
            if (grpIsVA) {
              adjustmentRow['ValuationAllowance'] = -(adjustmentRow['TaxRateChange'] + adjustmentRow['CurrentActivity'] + adjustmentRow['DeferredTrueUpRollforward'] + adjustmentRow['ReturnToProvisionRollforward']);
              adjustmentRow['ValuationAllowance_TaxEffected'] = -(adjustmentRow['TaxRateChange_TaxEffected'] + adjustmentRow['CurrentActivity_TaxEffected'] + adjustmentRow['DeferredTrueUpRollforward_TaxEffected'] + adjustmentRow['ReturnToProvisionRollforward_TaxEffected']);
              adjustmentRow['ValuationAllowance_FBOS'] = -(adjustmentRow['TaxRateChange_FBOS'] + adjustmentRow['CurrentActivity_FBOS'] + adjustmentRow['DeferredTrueUpRollforward_FBOS'] + adjustmentRow['ReturnToProvisionRollforward_FBOS']);
              adjustmentRow['DeferredExpense'] = adjustmentRow['ValuationAllowance'];
              adjustmentRow['DeferredExpense_TaxEffected'] = adjustmentRow['ValuationAllowance_TaxEffected'];
              adjustmentRow['DeferredExpense_FBOS'] = adjustmentRow['ValuationAllowance_FBOS'];
              adjustmentRow['WriteOff'] = null;adjustmentRow['WriteOff_TaxEffected'] = null;adjustmentRow['WriteOff_FBOS'] = null;
              adjustmentRow['TaxRateChange'] = null;adjustmentRow['TaxRateChange_FBOS'] = null;
              adjustmentRow['ReturnToProvisionRollforward'] = null;adjustmentRow['ReturnToProvisionRollforward_TaxEffected'] = null;adjustmentRow['ReturnToProvisionRollforward_FBOS'] = null;
              adjustmentRow['DeferredTrueUpRollforward'] = null;adjustmentRow['DeferredTrueUpRollforward_TaxEffected'] = null;adjustmentRow['DeferredTrueUpRollforward_FBOS'] = null;
              adjustmentRow['CurrentActivity'] = null;adjustmentRow['CurrentActivity_TaxEffected'] = null;adjustmentRow['CurrentActivity_FBOS'] = null;
            } else {
              adjustmentRow['ValuationAllowance'] = 0;
              adjustmentRow['ValuationAllowance_TaxEffected'] = 0;
              adjustmentRow['ValuationAllowance_FBOS'] = 0;
              adjustmentRow['DeferredExpense'] = adjustmentRow['TaxRateChange'] + adjustmentRow['ReturnToProvisionRollforward'] + adjustmentRow['DeferredTrueUpRollforward'] + adjustmentRow['CurrentActivity'] + adjustmentRow['WriteOff'] + adjustmentRow['ValuationAllowance'];
              adjustmentRow['DeferredExpense_TaxEffected'] = adjustmentRow['TaxRateChange_TaxEffected'] + adjustmentRow['ReturnToProvisionRollforward_TaxEffected'] + adjustmentRow['DeferredTrueUpRollforward_TaxEffected'] + adjustmentRow['CurrentActivity_TaxEffected'] + adjustmentRow['WriteOff_TaxEffected'] + adjustmentRow['ValuationAllowance_TaxEffected'];
              adjustmentRow['DeferredExpense_FBOS'] = adjustmentRow['TaxRateChange_FBOS'] + adjustmentRow['ReturnToProvisionRollforward_FBOS'] + adjustmentRow['DeferredTrueUpRollforward_FBOS'] + adjustmentRow['CurrentActivity_FBOS'] + adjustmentRow['WriteOff_FBOS'] + adjustmentRow['ValuationAllowance_FBOS'];
            }

            deferredRows.push(adjustmentRow);
          }); // end uniqueAdjustments.forEach

          // Section total row
          const sectionTotalRow = {
            id: `total-group-${groupName}-${sgKey}-${headerIndex}`,
            EntityCode: '', GroupCode: groupName,
            AdjustmentCode: '', AdjustmentName: `TOTAL ${header}`, isSubFooter: true
          };
          const shouldHidePreAndPost = adjustmentType === 'State Tax Effected Temp' || adjustmentType === 'State Tax Effected Valuation Allowance';
          const shouldHidePre = adjustmentType === 'State Post-App Temp' || adjustmentType === 'State Post-App NOL' || adjustmentType === 'State Post-App Valuation Allowance';
          const isVASectionTotal = header === 'STATE PRE-APPORTIONMENT VALUATION ALLOWANCES' || header === 'STATE POST-APPORTIONED VALUATION ALLOWANCE' || header === 'STATE TAX-EFFECTED VALUATION ALLOWANCE';

          const secStartIdx = deferredRows.findLastIndex((row) => row.isSection);
          const secRows = deferredRows.slice(secStartIdx + 1).filter((r) => !r.isSection && !r.isSubFooter);

          ACTIVITY_TYPES.forEach((at) => {
            let total = 0,postTotal = 0,taxTotal = 0,fbosTotal = 0;
            secRows.forEach((row) => {
              total += parseFloat(row[at]) || 0;
              postTotal += parseFloat(row[`${at}_PostApportioned`]) || 0;
              taxTotal += parseFloat(row[`${at}_TaxEffected`]) || 0;
              fbosTotal += parseFloat(row[`${at}_FBOS`]) || 0;
            });
            sectionTotalRow[at] = shouldHidePreAndPost || shouldHidePre ? null : total;
            sectionTotalRow[`${at}_PostApportioned`] = shouldHidePreAndPost ? null : postTotal;
            sectionTotalRow[`${at}_TaxEffected`] = taxTotal;
            sectionTotalRow[`${at}_FBOS`] = shouldHidePreAndPost ? null : fbosTotal;
          });

          // Sum derived columns for section total
          const derivedCols = ['CTAAdjustment', 'EndingBalance', 'DeferredExpense', 'TaxRateChange', 'CurrentActivity', 'WriteOff', 'ReturnToProvisionRollforward', 'DeferredTrueUpRollforward', 'ValuationAllowance'];
          derivedCols.forEach((col) => {
            ['', '_PostApportioned', '_TaxEffected', '_FBOS'].forEach((suffix) => {
              const key = `${col}${suffix}`;
              sectionTotalRow[key] = secRows.reduce((sum, row) => sum + (parseFloat(row[key]) || 0), 0);
            });
          });
          if (shouldHidePreAndPost) {
            ['CTAAdjustment', 'EndingBalance', 'DeferredExpense'].forEach((col) => {
              sectionTotalRow[`${col}_PostApportioned`] = null;
              sectionTotalRow[`${col}_FBOS`] = null;
              sectionTotalRow[col] = null;
            });
          }
          if (isVASectionTotal) {
            ['TaxRateChange', 'CurrentActivity', 'WriteOff', 'ReturnToProvisionRollforward', 'DeferredTrueUpRollforward'].forEach((col) => {
              sectionTotalRow[col] = null;
              sectionTotalRow[`${col}_TaxEffected`] = null;
              sectionTotalRow[`${col}_FBOS`] = null;
            });
          }
          deferredRows.push(sectionTotalRow);
        }); // end DEFERRED_ROLLFORWARD_HEADERS.forEach
      }); // end groupSubGroups.forEach

      // Grand total row for this group
      const grandTotalRow = {
        id: `grand-total-group-${groupName}`,
        EntityCode: '', GroupCode: groupName,
        AdjustmentCode: '', AdjustmentName: 'GRAND TOTAL TEMPORARY DIFFERENCES', isSubFooter: true
      };

      ACTIVITY_TYPES.forEach((at) => {
        let grandTotal = 0,grandPost = 0,grandTax = 0,grandFbos = 0;
        deferredRows.forEach((row) => {
          if (!row.isSection && !row.isSubFooter && row.GroupCode === groupName) {
            grandTotal += parseFloat(row[at]) || 0;
            grandPost += parseFloat(row[`${at}_PostApportioned`]) || 0;
            grandTax += parseFloat(row[`${at}_TaxEffected`]) || 0;
            grandFbos += parseFloat(row[`${at}_FBOS`]) || 0;
          }
        });
        grandTotalRow[at] = grandTotal;
        grandTotalRow[`${at}_PostApportioned`] = grandPost;
        grandTotalRow[`${at}_TaxEffected`] = grandTax;
        grandTotalRow[`${at}_FBOS`] = grandFbos;
      });
      const derivedCols2 = ['CTAAdjustment', 'EndingBalance', 'DeferredExpense', 'TaxRateChange', 'CurrentActivity', 'WriteOff', 'ReturnToProvisionRollforward', 'DeferredTrueUpRollforward', 'ValuationAllowance'];
      derivedCols2.forEach((col) => {
        ['', '_PostApportioned', '_TaxEffected', '_FBOS'].forEach((suffix) => {
          const key = `${col}${suffix}`;
          grandTotalRow[key] = deferredRows.filter((r) => !r.isSection && !r.isSubFooter && r.GroupCode === groupName).
          reduce((sum, row) => sum + (parseFloat(row[key]) || 0), 0);
        });
      });
      deferredRows.push(grandTotalRow);
    }); // end resolvedGroups.forEach

    return { deferredRows: annotateDeferredDrilldowns(deferredRows), activityTypes: ACTIVITY_TYPES };
  };

  // Pivot data to use FilingCodes (from StateInputs) as columns and adjustments as rows, grouped by sections
  const pivotData = (data, deferredSourceData = data) => {
    // Build unique filing codes from stateInputs for the selected scenario, preserving order
    const scenarioStateInputs = stateInputs.filter(
      (si) => si.ScenarioCode === selectedScenario?.scenarioCode
    );
    // O(1) map lookup — replaces O(n) .find() on every call throughout pivotData
    const siMap = new Map(scenarioStateInputs.map((si) => [`${si.FilingCode}__${si.EntityCode}`, si]));
    const siForKey = (key) => siMap.get(key);
    // Unique keys: FilingCode__EntityCode composites to handle same filing code across multiple entities
    let uniqueFilingCodes = [...new Set(
      scenarioStateInputs.map((si) => `${si.FilingCode}__${si.EntityCode}`).filter((k) => k)
    )];

    // Apply state filter: keep only filing codes belonging to selected states
    const _pivotStateArr = Array.isArray(selectedState) ? selectedState : [];
    if (_pivotStateArr.length > 0) {
      uniqueFilingCodes = uniqueFilingCodes.filter((fc) => {
        const si = siForKey(fc);
        return si && _pivotStateArr.includes(si.StateCode);
      });
    }

    // Apply filing code filter
    const _pivotFilingArr = Array.isArray(selectedFilingCode) ? selectedFilingCode : [];
    if (_pivotFilingArr.length > 0) {
      uniqueFilingCodes = uniqueFilingCodes.filter((fc) => {
        const si = siForKey(fc);
        return si && _pivotFilingArr.includes(si.FilingCode);
      });
    }

    // No fallback: if there are no matching StateInput records (either none in the scenario
    // or all eliminated by filters), the table should be empty rather than showing
    // unrelated entity data.
    const uniqueEntities = uniqueFilingCodes;

    // Precompute reverse-lookup maps so sumSection and row processing are O(rows)
    // instead of O(rows × filingCodes) with nested forEach loops.
    const entityToFcKeys = new Map(); // EntityCode → [filingCodeKey, ...]
    const stateCompositeToFcKey = new Map(); // `FilingCode__EntityCode` → filingCodeKey
    uniqueEntities.forEach((fc) => {
      const si = siForKey(fc);
      if (!si) return;
      if (!entityToFcKeys.has(si.EntityCode)) entityToFcKeys.set(si.EntityCode, []);
      entityToFcKeys.get(si.EntityCode).push(fc);
      stateCompositeToFcKey.set(`${si.FilingCode}__${si.EntityCode}`, fc);
    });

    // Group adjustments by their adjustment type (which maps to sections)
    const groupedByType = {};
    data.forEach((row) => {
      const adjustmentType = row.AdjustmentSource || row.AdjustmentType || 'Other';
      if (!groupedByType[adjustmentType]) {
        groupedByType[adjustmentType] = [];
      }
      groupedByType[adjustmentType].push(row);
    });

    const pivotedRows = [];
    let isFirstSection = true;
    let sectionTotalsCache = {}; // Cache to store section totals for calculations

    // For ETR view, pre-populate sectionTotalsCache with all intermediate calculated values
    // by summing raw section data from groupedByType for every section in the full sectionMapping.
    // This mirrors what the current-provision forEach loop would compute, so that values like
    // 'State Current Tax Expense' and 'Total State Return Basis Current Tax Expense' are available.
    if (calculationType === 'effective-tax-rate') {
      // Helper: sum a section's data rows into per-filingCode totals (unscaled raw amounts).
      // Scaling by (apportionmentRate × stateRate) is applied only in the display row-rendering
      // path below, NOT here, because this pre-calc chain applies rates itself when computing
      // State Current Tax Expense and downstream values.
      const sumSection = (sectionKey, activityTypeFilter = null) => {
        const sectionData = groupedByType[sectionKey] || [];
        const isState = STATE_SPECIFIC_SECTION_TYPES.includes(sectionKey);
        const totals = {};
        uniqueEntities.forEach((fc) => {totals[fc] = 0;});
        // Single pass over rows using precomputed reverse maps — O(rows) not O(rows × filingCodes)
        sectionData.forEach((row) => {
          if (activityTypeFilter && row.ActivityType !== activityTypeFilter) return;
          const rawAmt = selectedCurrency === 'USD' ?
          parseFloat(row.USDInput) || parseFloat(row.USDAmount) || 0 :
          parseFloat(row.LocalCurrencyInput) || parseFloat(row.Amount) || 0;
          if (isState) {
            const fc = stateCompositeToFcKey.get(`${row.FilingCode}__${row.EntityCode}`);
            if (fc !== undefined) totals[fc] += rawAmt;
          } else {
            const fcs = entityToFcKeys.get(row.EntityCode);
            if (fcs) fcs.forEach((fc) => {totals[fc] += rawAmt;});
          }
        });
        return totals;
      };

      // Sum every section in the full sectionMapping
      const raw = {};
      Object.keys(sectionMapping).forEach((key) => {raw[key] = sumSection(key);});

      // Store raw section totals in cache
      Object.keys(raw).forEach((key) => {sectionTotalsCache[key] = raw[key];});

      // Now compute intermediate calculated values in order
      // Statutory Pretax = Pretax + GS Permanent + GS Temporary
      sectionTotalsCache['Statutory Pretax'] = {};
      uniqueEntities.forEach((fc) => {
        sectionTotalsCache['Statutory Pretax'][fc] = (raw['Pretax']?.[fc] || 0) + (raw['GS Permanent']?.[fc] || 0) + (raw['GS Temporary']?.[fc] || 0);
      });

      // Taxable Income Before Adjustments = Statutory Pretax + Permanent + Payable + Temporary
      sectionTotalsCache['Taxable Income Before Adjustments'] = {};
      uniqueEntities.forEach((fc) => {
        sectionTotalsCache['Taxable Income Before Adjustments'][fc] =
        (sectionTotalsCache['Statutory Pretax']?.[fc] || 0) + (raw['Permanent']?.[fc] || 0) + (raw['Payable']?.[fc] || 0) + (raw['Temporary']?.[fc] || 0);
      });

      // State Pre-App Income Before NOL
      sectionTotalsCache['State Pre-App Income Before NOL'] = {};
      uniqueEntities.forEach((fc) => {
        sectionTotalsCache['State Pre-App Income Before NOL'][fc] =
        (sectionTotalsCache['Taxable Income Before Adjustments']?.[fc] || 0) + (raw['State Pre-App Perm']?.[fc] || 0) + (raw['State Pre-App Temp']?.[fc] || 0);
      });

      // State Pre-App Income After NOL
      sectionTotalsCache['State Pre-App Income After NOL'] = {};
      uniqueEntities.forEach((fc) => {
        sectionTotalsCache['State Pre-App Income After NOL'][fc] =
        (sectionTotalsCache['State Pre-App Income Before NOL']?.[fc] || 0) + (raw['State Pre-App NOL']?.[fc] || 0);
      });

      // Apportionment rate and State Post Apportioned Income
      sectionTotalsCache['Current Apportionment Rate'] = {};
      uniqueEntities.forEach((fc) => {
        const si = siForKey(fc);
        const rate = parseFloat(si?.CurrentApportionmentRate) || 0;
        sectionTotalsCache['Current Apportionment Rate'][fc] = rate > 1 ? rate / 100 : rate;
      });
      sectionTotalsCache['State Post Apportioned Income'] = {};
      uniqueEntities.forEach((fc) => {
        sectionTotalsCache['State Post Apportioned Income'][fc] =
        (sectionTotalsCache['State Pre-App Income After NOL']?.[fc] || 0) * (sectionTotalsCache['Current Apportionment Rate']?.[fc] || 0);
      });

      // State Post-App Taxable Income
      sectionTotalsCache['State Post-App Taxable Income'] = {};
      uniqueEntities.forEach((fc) => {
        sectionTotalsCache['State Post-App Taxable Income'][fc] =
        (sectionTotalsCache['State Post Apportioned Income']?.[fc] || 0) + (raw['State Post-App Perm']?.[fc] || 0) + (raw['State Post-App Temp']?.[fc] || 0);
      });

      // State Post-App Income After NOL
      sectionTotalsCache['State Post-App Income After NOL'] = {};
      uniqueEntities.forEach((fc) => {
        sectionTotalsCache['State Post-App Income After NOL'][fc] =
        (sectionTotalsCache['State Post-App Taxable Income']?.[fc] || 0) + (raw['State Post-App NOL']?.[fc] || 0);
      });

      // State Current Tax Expense = State Post-App Income After NOL × state tax rate
      sectionTotalsCache['Current State Tax Rate'] = {};
      uniqueEntities.forEach((fc) => {
        const si = siForKey(fc);
        const rate = parseFloat(si?.CurrentStateRate) || 0;
        sectionTotalsCache['Current State Tax Rate'][fc] = rate > 1 ? rate / 100 : rate;
      });
      sectionTotalsCache['State Current Tax Expense'] = {};
      uniqueEntities.forEach((fc) => {
        sectionTotalsCache['State Current Tax Expense'][fc] =
        (sectionTotalsCache['State Post-App Income After NOL']?.[fc] || 0) * (sectionTotalsCache['Current State Tax Rate']?.[fc] || 0);
      });

      // Total State Return Basis Current Tax Expense = State Current Tax Expense + State Tax Effected Temp + State Return
      sectionTotalsCache['Total State Return Basis Current Tax Expense'] = {};
      uniqueEntities.forEach((fc) => {
        sectionTotalsCache['Total State Return Basis Current Tax Expense'][fc] =
        (sectionTotalsCache['State Current Tax Expense']?.[fc] || 0) + (raw['State Tax Effected Temp']?.[fc] || 0) + (raw['State Tax Adj. Return']?.[fc] || 0);
      });
    }

    // For ETR view, use ETR-specific section mapping; otherwise use the standard one
    const activeSectionMapping = calculationType === 'effective-tax-rate' ? etrSectionMapping : sectionMapping;
    const activeSectionTotalTitles = calculationType === 'effective-tax-rate' ? etrSectionTotalTitles : sectionTotalTitles;

    // For ETR view, prepare a TAX EXPENSE section to be inserted at the top after calculations
    let taxExpenseSection = null;
    if (calculationType === 'effective-tax-rate') {
      taxExpenseSection = {
        header: {
          id: 'section-tax-expense',
          AdjustmentCode: '',
          AdjustmentName: 'TAX EXPENSE',
          isSection: true
        },
        currentTaxExpense: {
          id: 'etr-state-current-tax-expense',
          AdjustmentCode: '',
          AdjustmentName: 'State Current Tax Expense',
          isSection: false
        },
        deferredTaxExpense: {
          id: 'etr-state-deferred-tax-expense',
          AdjustmentCode: '',
          AdjustmentName: 'State Deferred Tax Expense',
          isSection: false
        },
        totalTaxExpense: {
          id: 'etr-total-state-tax-expense-top',
          AdjustmentCode: '',
          AdjustmentName: 'Total State Tax Expense',
          isSubFooter: true
        }
      };
      uniqueEntities.forEach((filingCode) => {
        taxExpenseSection.header[filingCode] = null;
        taxExpenseSection.currentTaxExpense[filingCode] = 0;
        taxExpenseSection.deferredTaxExpense[filingCode] = 0;
        taxExpenseSection.totalTaxExpense[filingCode] = 0;
      });
    }

    // Process each section in order - show all sections regardless of data
    Object.entries(activeSectionMapping).forEach(([adjustmentType, sectionTitle]) => {
      const sectionData = groupedByType[adjustmentType];

      // Add sub-footer for previous section (except for the first section)
      if (!isFirstSection && pivotedRows.length > 0) {
        // Calculate totals for the previous section
        const previousAdjustmentType = Object.keys(activeSectionMapping)[Object.keys(activeSectionMapping).indexOf(adjustmentType) - 1];
        const sectionTotals = {
          id: `footer-${adjustmentType}`,
          AdjustmentCode: '',
          AdjustmentName: activeSectionTotalTitles[previousAdjustmentType] || 'Section Total',
          isSubFooter: true
        };

        // Calculate totals for each filing code column
        const currentSectionTotals = {};
        uniqueEntities.forEach((fc) => {currentSectionTotals[fc] = 0;});
        // Single backward scan across all filing codes at once (replaces N separate scans)
        for (let i = pivotedRows.length - 1; i >= 0; i--) {
          const row = pivotedRows[i];
          if (row.isSection) break;
          if (!row.isSubFooter) {
            uniqueEntities.forEach((fc) => {currentSectionTotals[fc] += parseFloat(row[fc]) || 0;});
          }
        }
        uniqueEntities.forEach((fc) => {sectionTotals[fc] = currentSectionTotals[fc];});

        // Cache the section totals for later calculations.
        // In ETR view, State Return/Provision/RTP pivot rows are scaled for display, but the
        // downstream TAX EXPENSE calculation needs unscaled totals. Store unscaled totals here.
        const ETR_UNSCALE_TYPES = new Set(['State Tax Adj. Return', 'State Tax Adj. Provision', 'State Tax Adj. RTP']);
        if (calculationType === 'effective-tax-rate' && ETR_UNSCALE_TYPES.has(previousAdjustmentType)) {
          const unscaledTotals = {};
          uniqueEntities.forEach((filingCode) => {
            const rawSection = groupedByType[previousAdjustmentType] || [];
            let rawTotal = 0;
            rawSection.forEach((row) => {
              if (row.ActivityType !== 'CurrentActivityPL') return;
              if (stateCompositeToFcKey.get(`${row.FilingCode}__${row.EntityCode}`) !== filingCode) return;
              rawTotal += selectedCurrency === 'USD' ?
              parseFloat(row.USDInput) || parseFloat(row.USDAmount) || 0 :
              parseFloat(row.LocalCurrencyInput) || parseFloat(row.Amount) || 0;
            });
            unscaledTotals[filingCode] = rawTotal;
          });
          sectionTotalsCache[previousAdjustmentType] = unscaledTotals;
        } else {
          sectionTotalsCache[previousAdjustmentType] = currentSectionTotals;
        }

        pivotedRows.push(sectionTotals);

        // Add special calculated total after GS Temporary section
        if (previousAdjustmentType === 'GS Temporary') {
          const calculatedTotal = {
            id: 'calculated-gaap-book-income',
            AdjustmentCode: '',
            AdjustmentName: 'STATUTORY PRETAX BOOK INCOME',
            isCalculatedTotal: true
          };

          // Calculate: Pretax total + GS Permanent total + GS Temporary total
          uniqueEntities.forEach((filingCode) => {
            const pretaxTotal = sectionTotalsCache['Pretax']?.[filingCode] || 0;
            const gsPermTotal = sectionTotalsCache['GS Permanent']?.[filingCode] || 0;
            const gsTempTotal = currentSectionTotals[filingCode] || 0;

            calculatedTotal[filingCode] = pretaxTotal + gsPermTotal + gsTempTotal;
          });

          // Cache the statutory pretax book income for later calculations
          sectionTotalsCache['Statutory Pretax'] = {};
          uniqueEntities.forEach((filingCode) => {
            sectionTotalsCache['Statutory Pretax'][filingCode] = calculatedTotal[filingCode];
          });

          pivotedRows.push(calculatedTotal);
        }

        // Add special calculated total after Temporary section (STAT TO TAX TEMPORARY DIFFERENCES)
        if (previousAdjustmentType === 'Temporary') {
          const taxableIncomeTotal = {
            id: 'calculated-taxable-income-before-adjustments',
            AdjustmentCode: '',
            AdjustmentName: 'FEDERAL TAXABLE INCOME BEFORE ADJUSTMENTS',
            isCalculatedTotal: true
          };

          // Calculate: Statutory Pretax Book Income + Permanent + Payable + Temporary totals
          uniqueEntities.forEach((filingCode) => {
            const statutoryPretaxTotal = sectionTotalsCache['Statutory Pretax']?.[filingCode] || 0;
            const permanentTotal = sectionTotalsCache['Permanent']?.[filingCode] || 0;
            const payableTotal = sectionTotalsCache['Payable']?.[filingCode] || 0;
            const temporaryTotal = currentSectionTotals[filingCode] || 0;

            taxableIncomeTotal[filingCode] = statutoryPretaxTotal + permanentTotal + payableTotal + temporaryTotal;
          });

          // Cache the taxable income before adjustments for later calculations
          sectionTotalsCache['Taxable Income Before Adjustments'] = {};
          uniqueEntities.forEach((filingCode) => {
            sectionTotalsCache['Taxable Income Before Adjustments'][filingCode] = taxableIncomeTotal[filingCode];
          });

          pivotedRows.push(taxableIncomeTotal);
        }

        // Add special calculated total after State Pre-App Temp section
        if (previousAdjustmentType === 'State Pre-App Temp') {
          const statePreAppIncomeBeforeNOL = {
            id: 'calculated-state-pre-app-income-before-nol',
            AdjustmentCode: '',
            AdjustmentName: 'STATE PRE-APPORTIONED INCOME BEFORE NOL',
            isCalculatedTotal: true
          };

          // Calculate: TAXABLE INCOME BEFORE ADJUSTMENTS + State Pre-App Perm + State Pre-App Temp totals
          uniqueEntities.forEach((filingCode) => {
            const taxableIncomeBeforeAdjTotal = sectionTotalsCache['Taxable Income Before Adjustments']?.[filingCode] || 0;
            const statePreAppPermTotal = sectionTotalsCache['State Pre-App Perm']?.[filingCode] || 0;
            const statePreAppTempTotal = currentSectionTotals[filingCode] || 0;

            statePreAppIncomeBeforeNOL[filingCode] = taxableIncomeBeforeAdjTotal + statePreAppPermTotal + statePreAppTempTotal;
          });

          // Cache for later calculations
          sectionTotalsCache['State Pre-App Income Before NOL'] = {};
          uniqueEntities.forEach((filingCode) => {
            sectionTotalsCache['State Pre-App Income Before NOL'][filingCode] = statePreAppIncomeBeforeNOL[filingCode];
          });

          pivotedRows.push(statePreAppIncomeBeforeNOL);
        }

        // Add special calculated total after State Pre-App NOL section
        if (previousAdjustmentType === 'State Pre-App NOL') {
          const statePreAppIncomeAfterNOL = {
            id: 'calculated-state-pre-app-income-after-nol',
            AdjustmentCode: '',
            AdjustmentName: 'STATE PRE-APPORTIONED INCOME AFTER NOL',
            isCalculatedTotal: true
          };

          // Calculate: STATE PRE-APPORTIONED INCOME BEFORE NOL + State Pre-App NOL total
          uniqueEntities.forEach((filingCode) => {
            const statePreAppIncomeBeforeNOLTotal = sectionTotalsCache['State Pre-App Income Before NOL']?.[filingCode] || 0;
            const statePreAppNOLTotal = currentSectionTotals[filingCode] || 0;

            statePreAppIncomeAfterNOL[filingCode] = statePreAppIncomeBeforeNOLTotal + statePreAppNOLTotal;
          });

          // Cache for later calculations
          sectionTotalsCache['State Pre-App Income After NOL'] = {};
          uniqueEntities.forEach((filingCode) => {
            sectionTotalsCache['State Pre-App Income After NOL'][filingCode] = statePreAppIncomeAfterNOL[filingCode];
          });

          pivotedRows.push(statePreAppIncomeAfterNOL);

          // Add current apportionment rate row immediately after state pre-app income after NOL
          const currentApportionmentRate = {
            id: 'calculated-current-apportionment-rate',
            AdjustmentCode: '',
            AdjustmentName: 'CURRENT APPORTIONMENT RATE PERCENTAGE',
            isCalculatedTotal: true
          };

          // Get current apportionment rate from StateInput for each FilingCode
          uniqueEntities.forEach((filingCode) => {
            const stateInput = siForKey(filingCode);
            const apportionmentRate = parseFloat(stateInput?.CurrentApportionmentRate) || 0;
            const rateDecimal = apportionmentRate > 1 ? apportionmentRate / 100 : apportionmentRate;
            const pre = sectionTotalsCache['State Pre-App Income After NOL']?.[filingCode] || 0;
            currentApportionmentRate[filingCode] = apportionmentRate; // raw value exactly as input
            // Store scale (100 if entered as %, 1 if entered as decimal) for aggregate columns
            currentApportionmentRate[`${filingCode}_Scale`] = apportionmentRate > 1 ? 100 : 1;
            // Store underlying pre/post values for computing aggregate rates in grouped/total columns
            currentApportionmentRate[`${filingCode}_PreApp`] = pre;
            currentApportionmentRate[`${filingCode}_Post`] = pre * rateDecimal;
          });

          // Cache the current apportionment rates as decimals for calculations
          sectionTotalsCache['Current Apportionment Rate'] = {};
          uniqueEntities.forEach((filingCode) => {
            const stateInput = siForKey(filingCode);
            const apportionmentRate = parseFloat(stateInput?.CurrentApportionmentRate) || 0;
            const rateDecimal = apportionmentRate > 1 ? apportionmentRate / 100 : apportionmentRate;
            sectionTotalsCache['Current Apportionment Rate'][filingCode] = rateDecimal;
          });

          pivotedRows.push(currentApportionmentRate);

          // Add STATE POST APPORTIONED INCOME row immediately after current apportionment rate
          const statePostApportionedIncome = {
            id: 'calculated-state-post-apportioned-income',
            AdjustmentCode: '',
            AdjustmentName: 'STATE POST APPORTIONED INCOME',
            isCalculatedTotal: true
          };

          // Calculate: STATE PRE-APPORTIONED INCOME AFTER NOL x CURRENT APPORTIONMENT RATE PERCENTAGE
          uniqueEntities.forEach((filingCode) => {
            const statePreAppIncomeAfterNOLTotal = sectionTotalsCache['State Pre-App Income After NOL']?.[filingCode] || 0;
            const apportionmentRateDecimal = sectionTotalsCache['Current Apportionment Rate']?.[filingCode] || 0;

            statePostApportionedIncome[filingCode] = statePreAppIncomeAfterNOLTotal * apportionmentRateDecimal;
          });

          // Cache for later calculations
          sectionTotalsCache['State Post Apportioned Income'] = {};
          uniqueEntities.forEach((filingCode) => {
            sectionTotalsCache['State Post Apportioned Income'][filingCode] = statePostApportionedIncome[filingCode];
          });

          pivotedRows.push(statePostApportionedIncome);
        }

        // Add special calculated total after State Post-App Temp section
        if (previousAdjustmentType === 'State Post-App Temp') {
          const statePostAppTaxableIncome = {
            id: 'calculated-state-post-app-taxable-income',
            AdjustmentCode: '',
            AdjustmentName: 'STATE POST-APPORTIONED TAXABLE INCOME',
            isCalculatedTotal: true
          };

          // Calculate: STATE POST APPORTIONED INCOME + State Post-App Perm + State Post-App Temp totals
          uniqueEntities.forEach((filingCode) => {
            const statePostAppIncomeTotal = sectionTotalsCache['State Post Apportioned Income']?.[filingCode] || 0;
            const statePostAppPermTotal = sectionTotalsCache['State Post-App Perm']?.[filingCode] || 0;
            const statePostAppTempTotal = currentSectionTotals[filingCode] || 0;

            statePostAppTaxableIncome[filingCode] = statePostAppIncomeTotal + statePostAppPermTotal + statePostAppTempTotal;
          });

          // Cache for later calculations
          sectionTotalsCache['State Post-App Taxable Income'] = {};
          uniqueEntities.forEach((filingCode) => {
            sectionTotalsCache['State Post-App Taxable Income'][filingCode] = statePostAppTaxableIncome[filingCode];
          });

          pivotedRows.push(statePostAppTaxableIncome);
        }

        // Add special calculated total after State Post-App NOL section
        if (previousAdjustmentType === 'State Post-App NOL') {
          const statePostAppIncomeAfterNOL = {
            id: 'calculated-state-post-app-income-after-nol',
            AdjustmentCode: '',
            AdjustmentName: 'STATE POST-APPORTIONED INCOME AFTER NOL',
            isCalculatedTotal: true
          };

          // Calculate: STATE POST-APPORTIONED TAXABLE INCOME + State Post-App NOL total
          uniqueEntities.forEach((filingCode) => {
            const statePostAppTaxableIncomeTotal = sectionTotalsCache['State Post-App Taxable Income']?.[filingCode] || 0;
            const statePostAppNOLTotal = currentSectionTotals[filingCode] || 0;

            statePostAppIncomeAfterNOL[filingCode] = statePostAppTaxableIncomeTotal + statePostAppNOLTotal;
          });

          // Cache for later calculations
          sectionTotalsCache['State Post-App Income After NOL'] = {};
          uniqueEntities.forEach((filingCode) => {
            sectionTotalsCache['State Post-App Income After NOL'][filingCode] = statePostAppIncomeAfterNOL[filingCode];
          });

          pivotedRows.push(statePostAppIncomeAfterNOL);

          // Add current state tax rate row immediately after state post-app income after NOL
          const currentStateTaxRate = {
            id: 'calculated-current-state-tax-rate',
            AdjustmentCode: '',
            AdjustmentName: 'CURRENT STATE TAX RATE PERCENTAGE',
            isCalculatedTotal: true
          };

          // Get current state tax rate from StateInput for each FilingCode
          uniqueEntities.forEach((filingCode) => {
            const stateInput = siForKey(filingCode);
            const stateTaxRate = parseFloat(stateInput?.CurrentStateRate) || 0;
            const stateTaxRatePercentage = stateTaxRate > 1 ? stateTaxRate : stateTaxRate * 100;
            currentStateTaxRate[filingCode] = `${stateTaxRatePercentage.toFixed(2)}%`;
          });

          // Cache the current state tax rates as decimals for calculations
          sectionTotalsCache['Current State Tax Rate'] = {};
          uniqueEntities.forEach((filingCode) => {
            const stateInput = siForKey(filingCode);
            const stateTaxRate = parseFloat(stateInput?.CurrentStateRate) || 0;
            const rateDecimal = stateTaxRate > 1 ? stateTaxRate / 100 : stateTaxRate;
            sectionTotalsCache['Current State Tax Rate'][filingCode] = rateDecimal;
          });

          pivotedRows.push(currentStateTaxRate);

          // Add STATE CURRENT TAX EXPENSE row immediately after current state tax rate
          const stateCurrentTaxExpense = {
            id: 'calculated-state-current-tax-expense',
            AdjustmentCode: '',
            AdjustmentName: 'STATE CURRENT TAX EXPENSE',
            isCalculatedTotal: true
          };

          // Calculate: STATE POST-APPORTIONED INCOME AFTER NOL x CURRENT STATE TAX RATE PERCENTAGE
          uniqueEntities.forEach((filingCode) => {
            const statePostAppIncomeAfterNOLTotal = sectionTotalsCache['State Post-App Income After NOL']?.[filingCode] || 0;
            const stateTaxRateDecimal = sectionTotalsCache['Current State Tax Rate']?.[filingCode] || 0;

            stateCurrentTaxExpense[filingCode] = statePostAppIncomeAfterNOLTotal * stateTaxRateDecimal;
          });

          // Cache for later calculations
          sectionTotalsCache['State Current Tax Expense'] = {};
          uniqueEntities.forEach((filingCode) => {
            sectionTotalsCache['State Current Tax Expense'][filingCode] = stateCurrentTaxExpense[filingCode];
          });

          pivotedRows.push(stateCurrentTaxExpense);
        }

        // Add special calculated total after State Return section
        if (previousAdjustmentType === 'State Tax Adj. Return') {
          const totalStateReturnBasisCurrentTaxExpense = {
            id: 'calculated-total-state-return-basis-current-tax-expense',
            AdjustmentCode: '',
            AdjustmentName: 'TOTAL STATE RETURN BASIS CURRENT TAX EXPENSE',
            isCalculatedTotal: true
          };

          // Calculate: STATE CURRENT TAX EXPENSE + State Tax Effected Temp + State Return totals
          // In ETR view, use unscaled State Return total (from cache, which was stored unscaled above)
          uniqueEntities.forEach((filingCode) => {
            const stateCurrentTaxExpenseTotal = sectionTotalsCache['State Current Tax Expense']?.[filingCode] || 0;
            const stateTaxEffectedTempTotal = sectionTotalsCache['State Tax Effected Temp']?.[filingCode] || 0;
            const stateReturnTotal = calculationType === 'effective-tax-rate' ?
            sectionTotalsCache['State Tax Adj. Return']?.[filingCode] || 0 :
            currentSectionTotals[filingCode] || 0;

            totalStateReturnBasisCurrentTaxExpense[filingCode] = stateCurrentTaxExpenseTotal + stateTaxEffectedTempTotal + stateReturnTotal;
          });

          // Cache for later calculations
          sectionTotalsCache['Total State Return Basis Current Tax Expense'] = {};
          uniqueEntities.forEach((filingCode) => {
            sectionTotalsCache['Total State Return Basis Current Tax Expense'][filingCode] = totalStateReturnBasisCurrentTaxExpense[filingCode];
          });

          // Only display this row in current-provision view, not in ETR
          if (calculationType !== 'effective-tax-rate') {
            pivotedRows.push(totalStateReturnBasisCurrentTaxExpense);
          }
        }

        // Add special calculated total after State RTP section
        if (previousAdjustmentType === 'State Tax Adj. RTP') {
          const totalStateCurrentTaxExpense = {
            id: 'calculated-total-state-current-tax-expense',
            AdjustmentCode: '',
            AdjustmentName: 'TOTAL STATE CURRENT TAX EXPENSE',
            isCalculatedTotal: true
          };

          // Calculate: TOTAL STATE RETURN BASIS CURRENT TAX EXPENSE + State Provision + State RTP totals
          uniqueEntities.forEach((filingCode) => {
            const totalStateReturnBasisCurrentTaxExpenseTotal = sectionTotalsCache['Total State Return Basis Current Tax Expense']?.[filingCode] || 0;
            const stateProvisionTotal = sectionTotalsCache['State Tax Adj. Provision']?.[filingCode] || 0;
            const stateRTPTotal = currentSectionTotals[filingCode] || 0;

            totalStateCurrentTaxExpense[filingCode] = totalStateReturnBasisCurrentTaxExpenseTotal + stateProvisionTotal + stateRTPTotal;
          });

          // Cache for later calculations
          sectionTotalsCache['Total State Current Tax Expense'] = {};
          uniqueEntities.forEach((filingCode) => {
            sectionTotalsCache['Total State Current Tax Expense'][filingCode] = totalStateCurrentTaxExpense[filingCode];
          });

          pivotedRows.push(totalStateCurrentTaxExpense);

          // For ETR view, populate the TAX EXPENSE section with state current tax expense
          if (calculationType === 'effective-tax-rate' && taxExpenseSection) {
            uniqueEntities.forEach((filingCode) => {
              taxExpenseSection.currentTaxExpense[filingCode] = totalStateCurrentTaxExpense[filingCode];
            });
          }
        }

        // ETR-only: cache intermediate totals without displaying rows
        if (calculationType === 'effective-tax-rate') {
          // After GS Permanent: cache Statutory Pretax (Pretax + GS Permanent)
          if (previousAdjustmentType === 'GS Permanent') {
            sectionTotalsCache['Statutory Pretax'] = {};
            uniqueEntities.forEach((filingCode) => {
              sectionTotalsCache['Statutory Pretax'][filingCode] =
              (sectionTotalsCache['Pretax']?.[filingCode] || 0) + (currentSectionTotals[filingCode] || 0);
            });
          }

          // After Permanent: cache Taxable Income Before State Adjustments
          if (previousAdjustmentType === 'Permanent') {
            sectionTotalsCache['Taxable Income Before State Adjustments'] = {};
            uniqueEntities.forEach((filingCode) => {
              sectionTotalsCache['Taxable Income Before State Adjustments'][filingCode] =
              (sectionTotalsCache['Statutory Pretax']?.[filingCode] || 0) + (currentSectionTotals[filingCode] || 0);
            });
          }

          // After State Pre-App Perm: cache State Adjusted Income
          if (previousAdjustmentType === 'State Pre-App Perm') {
            sectionTotalsCache['State Adjusted Income'] = {};
            uniqueEntities.forEach((filingCode) => {
              sectionTotalsCache['State Adjusted Income'][filingCode] =
              (sectionTotalsCache['Taxable Income Before State Adjustments']?.[filingCode] || 0) + (currentSectionTotals[filingCode] || 0);
            });
          }

          // After State Post-App Perm: cache State Post-App Adjusted Income
          if (previousAdjustmentType === 'State Post-App Perm') {
            sectionTotalsCache['State Post-App Adjusted Income'] = {};
            uniqueEntities.forEach((filingCode) => {
              sectionTotalsCache['State Post-App Adjusted Income'][filingCode] =
              (sectionTotalsCache['State Adjusted Income']?.[filingCode] || 0) + (currentSectionTotals[filingCode] || 0);
            });
          }

          // Note: 'Total State Return Basis Current Tax Expense' is correctly cached by the
          // current-provision block above (State Current Tax Expense + Tax Effected Temp + State Return)
          // and is used in the final sub-footer block below to populate CURRTAXEX
        }

        // Add special calculated total after TI temporary section (TAXABLE INCOME TEMP ADJUSTMENT)
        if (previousAdjustmentType === 'TI temporary') {
          const taxableIncomeBeforeNOL = {
            id: 'calculated-taxable-income-before-nol',
            AdjustmentCode: '',
            AdjustmentName: 'TAXABLE INCOME BEFORE NET OPERATING LOSS',
            isCalculatedTotal: true
          };

          uniqueEntities.forEach((filingCode) => {
            const taxableIncomeBeforeAdjTotal = sectionTotalsCache['Taxable Income Before Adjustments']?.[filingCode] || 0;
            const tiPermTotal = sectionTotalsCache['TI Permanent']?.[filingCode] || 0;
            const tiTempTotal = currentSectionTotals[filingCode] || 0;
            taxableIncomeBeforeNOL[filingCode] = taxableIncomeBeforeAdjTotal + tiPermTotal + tiTempTotal;
          });

          // Cache the taxable income before NOL for later calculations
          sectionTotalsCache['Taxable Income Before NOL'] = {};
          uniqueEntities.forEach((filingCode) => {
            sectionTotalsCache['Taxable Income Before NOL'][filingCode] = taxableIncomeBeforeNOL[filingCode];
          });

          pivotedRows.push(taxableIncomeBeforeNOL);
        }

        // Add TAXABLE INCOME BEFORE NOL calculation right before Net Operating Loss section
        // This ensures it runs even if TI temporary section doesn't exist
        if (adjustmentType === 'Net Operating Loss') {
          // Only add if we haven't already added it after TI temporary section
          const alreadyAdded = pivotedRows.some((row) => row.id === 'calculated-taxable-income-before-nol');
          if (!alreadyAdded) {
            const taxableIncomeBeforeNOL = {
              id: 'calculated-taxable-income-before-nol',
              AdjustmentCode: '',
              AdjustmentName: 'TAXABLE INCOME BEFORE NET OPERATING LOSS',
              isCalculatedTotal: true
            };

            uniqueEntities.forEach((filingCode) => {
              const taxableIncomeBeforeAdjTotal = sectionTotalsCache['Taxable Income Before Adjustments']?.[filingCode] || 0;
              const tiPermTotal = sectionTotalsCache['TI Permanent']?.[filingCode] || 0;
              const tiTempTotal = sectionTotalsCache['TI Temporary']?.[filingCode] || 0;
              taxableIncomeBeforeNOL[filingCode] = taxableIncomeBeforeAdjTotal + tiPermTotal + tiTempTotal;
            });

            // Cache the taxable income before NOL for later calculations
            sectionTotalsCache['Taxable Income Before NOL'] = {};
            uniqueEntities.forEach((filingCode) => {
              sectionTotalsCache['Taxable Income Before NOL'][filingCode] = taxableIncomeBeforeNOL[filingCode];
            });

            pivotedRows.push(taxableIncomeBeforeNOL);
          }
        }

        // Add special calculated total after Net Operating Loss section
        if (previousAdjustmentType === 'Net Operating Loss') {
          const totalTaxableIncome = {
            id: 'calculated-total-taxable-income',
            AdjustmentCode: '',
            AdjustmentName: 'TOTAL TAXABLE INCOME',
            isCalculatedTotal: true
          };

          uniqueEntities.forEach((filingCode) => {
            const taxableIncomeBeforeNOL = sectionTotalsCache['Taxable Income Before NOL']?.[filingCode] || 0;
            const netOperatingLossTotal = currentSectionTotals[filingCode] || 0;
            totalTaxableIncome[filingCode] = netOperatingLossTotal + taxableIncomeBeforeNOL;
          });

          // Cache the total taxable income for later calculations
          sectionTotalsCache['Total Taxable Income'] = {};
          uniqueEntities.forEach((filingCode) => {
            sectionTotalsCache['Total Taxable Income'][filingCode] = totalTaxableIncome[filingCode];
          });

          pivotedRows.push(totalTaxableIncome);

          // Add current tax rate row immediately after total taxable income
          // For each FilingCode, look up the EntityCode from stateInputs and find the EntityInput tax rate
          const currentTaxRate = {
            id: 'calculated-current-tax-rate',
            AdjustmentCode: '',
            AdjustmentName: 'CURRENT TAX RATE PERCENTAGE',
            isCalculatedTotal: true
          };

          uniqueEntities.forEach((filingCode) => {
            const si = siForKey(filingCode);
            const entityInput = entityInputs.find((ei) =>
            ei.EntityCode === si?.EntityCode &&
            ei.ScenarioCode === selectedScenario?.scenarioCode
            );
            const taxRate = parseFloat(entityInput?.CurrentTaxRate) || 0;
            const taxRatePercentage = taxRate > 1 ? taxRate : taxRate * 100;
            currentTaxRate[filingCode] = `${taxRatePercentage.toFixed(2)}%`;
          });

          // Cache the current tax rates as decimals for calculations
          sectionTotalsCache['Current Tax Rate'] = {};
          uniqueEntities.forEach((filingCode) => {
            const si = siForKey(filingCode);
            const entityInput = entityInputs.find((ei) =>
            ei.EntityCode === si?.EntityCode &&
            ei.ScenarioCode === selectedScenario?.scenarioCode
            );
            const taxRate = parseFloat(entityInput?.CurrentTaxRate) || 0;
            const taxRateDecimal = taxRate > 1 ? taxRate / 100 : taxRate;
            sectionTotalsCache['Current Tax Rate'][filingCode] = taxRateDecimal;
          });

          pivotedRows.push(currentTaxRate);

          // Add current tax expense before adjustments row
          const currentTaxExpense = {
            id: 'calculated-current-tax-expense',
            AdjustmentCode: '',
            AdjustmentName: 'CURRENT TAX EXPENSE BEFORE ADJUSTMENTS',
            isCalculatedTotal: true
          };

          uniqueEntities.forEach((filingCode) => {
            const totalTaxableIncomeValue = sectionTotalsCache['Total Taxable Income']?.[filingCode] || 0;
            const currentTaxRateValue = sectionTotalsCache['Current Tax Rate']?.[filingCode] || 0;
            currentTaxExpense[filingCode] = totalTaxableIncomeValue * currentTaxRateValue;
          });

          // Cache the current tax expense for later calculations
          sectionTotalsCache['Current Tax Expense Before Adjustments'] = {};
          uniqueEntities.forEach((filingCode) => {
            sectionTotalsCache['Current Tax Expense Before Adjustments'][filingCode] = currentTaxExpense[filingCode];
          });

          pivotedRows.push(currentTaxExpense);
        }

        // Add special calculated total after Tax Adj. Return section (RETURN BASIS TAX ADJUSTMENTS)
        if (previousAdjustmentType === 'Tax Adj. Return') {
          const totalReturnBasisTaxExpense = {
            id: 'calculated-total-return-basis-tax-expense',
            AdjustmentCode: '',
            AdjustmentName: 'TOTAL RETURN BASIS CURRENT TAX EXPENSE',
            isCalculatedTotal: true
          };

          uniqueEntities.forEach((filingCode) => {
            const currentTaxExpenseValue = sectionTotalsCache['Current Tax Expense Before Adjustments']?.[filingCode] || 0;
            const taxEffectedTempTotal = sectionTotalsCache['Tax Effected Temporary']?.[filingCode] || 0;
            const returnBasisTaxAdjTotal = currentSectionTotals[filingCode] || 0;

            totalReturnBasisTaxExpense[filingCode] = currentTaxExpenseValue + taxEffectedTempTotal + returnBasisTaxAdjTotal;
          });

          // Cache the total return basis current tax expense for later calculations
          sectionTotalsCache['Total Return Basis Current Tax Expense'] = {};
          uniqueEntities.forEach((filingCode) => {
            sectionTotalsCache['Total Return Basis Current Tax Expense'][filingCode] = totalReturnBasisTaxExpense[filingCode];
          });

          pivotedRows.push(totalReturnBasisTaxExpense);
        }

      }

      // Always add section header row
      const sectionHeaderRow = {
        id: `section-${adjustmentType}`,
        AdjustmentCode: '',
        AdjustmentName: sectionTitle,
        isSection: true
      };
      uniqueEntities.forEach((fc) => {sectionHeaderRow[fc] = null;});
      pivotedRows.push(sectionHeaderRow);

      // Determine if this is a state-specific section (uses FilingCode matching)
      const isStateSection = STATE_SPECIFIC_SECTION_TYPES.includes(adjustmentType);

      // Add adjustment rows for this section if data exists
      if (sectionData && sectionData.length > 0) {
        // Group adjustments by adjustment code to track multiple entries
        const adjustmentCodeGroups = {};

        sectionData.forEach((row) => {
          const code = row.AdjustmentCode;
          if (!adjustmentCodeGroups[code]) {
            adjustmentCodeGroups[code] = [];
          }
          adjustmentCodeGroups[code].push(row);
        });

        // Process each adjustment code group - combine all rows with same code
        Object.keys(adjustmentCodeGroups).sort().forEach((adjustmentCode) => {
          const adjustmentsWithThisCode = adjustmentCodeGroups[adjustmentCode];
          const adjustmentName = adjustmentsWithThisCode[0]?.AdjustmentName || '';

          // Create a single combined row for this adjustment code
          const pivotedRow = {
            id: `${adjustmentCode}_combined`,
            AdjustmentCode: adjustmentCode,
            AdjustmentName: adjustmentName,
            isSection: false
          };

          // Section types whose raw amounts are scaled by (apportionmentRate × stateRate) in the ETR view
          const ETR_SCALED_TYPES = new Set([
          'Pretax', 'GS Permanent', 'Permanent',
          'State Pre-App Perm', 'State Post-App Perm',
          'State Tax Adj. Return', 'State Tax Adj. Provision', 'State Tax Adj. RTP']
          );
          const isEtrScaledSection = calculationType === 'effective-tax-rate' && ETR_SCALED_TYPES.has(adjustmentType);

          // Populate columns based on section type:
          // - State-specific sections: only populate the column whose FilingCode matches adjustment.FilingCode
          // - Non-state sections: populate ALL FilingCode columns belonging to the adjustment's EntityCode
          uniqueEntities.forEach((filingCode) => {
            let totalAmount = 0;

            // Compute ETR multiplier (apportionmentRate × stateRate) for this filing code
            let etrMultiplier = 1;
            if (isEtrScaledSection) {
              const si = siForKey(filingCode);
              if (si) {
                const rawApp = parseFloat(si.CurrentApportionmentRate) || 0;
                const appRate = rawApp > 1 ? rawApp / 100 : rawApp;
                const rawState = parseFloat(si.CurrentStateRate) || 0;
                const stateRate = rawState > 1 ? rawState / 100 : rawState;
                etrMultiplier = appRate * stateRate;
              }
            }

            if (isStateSection) {
              // Only include adjustments whose FilingCode+EntityCode matches this column
              const si = siForKey(filingCode);
              const matchingAdjs = adjustmentsWithThisCode.filter((row) =>
              si && row.FilingCode === si.FilingCode && row.EntityCode === si.EntityCode
              );
              matchingAdjs.forEach((adj) => {
                totalAmount += (selectedCurrency === 'USD' ?
                adj.USDInput || adj.USDAmount || 0 :
                adj.LocalCurrencyInput || adj.Amount || 0) * etrMultiplier;
              });
            } else {
              // Fan out: include adjustments whose EntityCode matches the stateInput for this FilingCode
              const si = siForKey(filingCode);
              if (si) {
                const entityAdjs = adjustmentsWithThisCode.filter((row) =>
                row.EntityCode === si.EntityCode
                );
                entityAdjs.forEach((adj) => {
                  totalAmount += (selectedCurrency === 'USD' ?
                  adj.USDInput || adj.USDAmount || 0 :
                  adj.LocalCurrencyInput || adj.Amount || 0) * etrMultiplier;
                });
              }
            }

            pivotedRow[filingCode] = totalAmount;
          });

          pivotedRows.push(pivotedRow);
        });
      }

      isFirstSection = false;
    });

    // Add final sub-footer for the last section
    if (pivotedRows.length > 0) {
      const lastAdjustmentType = Object.keys(activeSectionMapping)[Object.keys(activeSectionMapping).length - 1];
      const finalSectionTotals = {
        id: `footer-final`,
        AdjustmentCode: '',
        AdjustmentName: activeSectionTotalTitles[lastAdjustmentType] || 'Section Total',
        isSubFooter: true
      };

      // Calculate totals for each filing code column for the last section
      const currentSectionTotals = {};
      uniqueEntities.forEach((filingCode) => {
        let total = 0;
        // Find all non-header, non-footer rows since the last section header
        for (let i = pivotedRows.length - 1; i >= 0; i--) {
          const row = pivotedRows[i];
          if (row.isSection) break; // Stop at the last section header
          if (!row.isSection && !row.isSubFooter) {
            total += parseFloat(row[filingCode]) || 0;
          }
        }
        finalSectionTotals[filingCode] = total;
        currentSectionTotals[filingCode] = total;
      });

      // Cache the final section totals.
      // In ETR view, State RTP pivot rows are scaled for display but TAX EXPENSE needs unscaled totals.
      const ETR_UNSCALE_TYPES_FINAL = new Set(['State Tax Adj. Return', 'State Tax Adj. Provision', 'State Tax Adj. RTP']);
      if (calculationType === 'effective-tax-rate' && ETR_UNSCALE_TYPES_FINAL.has(lastAdjustmentType)) {
        const unscaledFinalTotals = {};
        uniqueEntities.forEach((filingCode) => {
          const rawSection = groupedByType[lastAdjustmentType] || [];
          let rawTotal = 0;
          rawSection.forEach((row) => {
            if (row.ActivityType !== 'CurrentActivityPL') return;
            if (stateCompositeToFcKey.get(`${row.FilingCode}__${row.EntityCode}`) !== filingCode) return;
            rawTotal += selectedCurrency === 'USD' ?
            parseFloat(row.USDInput) || parseFloat(row.USDAmount) || 0 :
            parseFloat(row.LocalCurrencyInput) || parseFloat(row.Amount) || 0;
          });
          unscaledFinalTotals[filingCode] = rawTotal;
        });
        sectionTotalsCache[lastAdjustmentType] = unscaledFinalTotals;
      } else {
        sectionTotalsCache[lastAdjustmentType] = currentSectionTotals;
      }

      pivotedRows.push(finalSectionTotals);

      // ETR view: State RTP is the last section so the forEach inner block never fires for it.
      // Calculate Total State Current Tax Expense here using the same formula as the current-provision block,
      // with currentSectionTotals holding the State RTP totals just calculated above.
      if (calculationType === 'effective-tax-rate' && lastAdjustmentType === 'State Tax Adj. RTP' && taxExpenseSection) {
        // Compute State Current Tax Expense from CurrentActivityPL-only rows to exactly match
        // the current-provision 'TOTAL STATE CURRENT TAX EXPENSE'. Reads directly from
        // groupedByType (same raw data current provision receives after its ActivityType filter)
        // so no sectionTotalsCache values are read or modified here.
        const cpSumFC = (sectionKey, fc) => {
          const isStateType = STATE_SPECIFIC_SECTION_TYPES.includes(sectionKey);
          let total = 0;
          (groupedByType[sectionKey] || []).forEach((row) => {
            if (row.ActivityType !== 'CurrentActivityPL') return;
            const amt = selectedCurrency === 'USD' ?
            parseFloat(row.USDInput) || parseFloat(row.USDAmount) || 0 :
            parseFloat(row.LocalCurrencyInput) || parseFloat(row.Amount) || 0;
            if (isStateType) {
              if (stateCompositeToFcKey.get(`${row.FilingCode}__${row.EntityCode}`) === fc) total += amt;
            } else {
              const fcs = entityToFcKeys.get(row.EntityCode);
              if (fcs && fcs.includes(fc)) total += amt;
            }
          });
          return total;
        };
        uniqueEntities.forEach((filingCode) => {
          const si = siForKey(filingCode);
          const cpPretax = cpSumFC('Pretax', filingCode) + cpSumFC('GS Permanent', filingCode) + cpSumFC('GS Temporary', filingCode);
          const cpTaxable = cpPretax + cpSumFC('Permanent', filingCode) + cpSumFC('Payable', filingCode) + cpSumFC('Temporary', filingCode);
          const cpPreAppBeforeNOL = cpTaxable + cpSumFC('State Pre-App Perm', filingCode) + cpSumFC('State Pre-App Temp', filingCode);
          const cpPreAppAfterNOL = cpPreAppBeforeNOL + cpSumFC('State Pre-App NOL', filingCode);
          const rawApp = parseFloat(si?.CurrentApportionmentRate) || 0;
          const appRate = rawApp > 1 ? rawApp / 100 : rawApp;
          const cpPostAppTaxable = cpPreAppAfterNOL * appRate + cpSumFC('State Post-App Perm', filingCode) + cpSumFC('State Post-App Temp', filingCode);
          const cpPostAppAfterNOL = cpPostAppTaxable + cpSumFC('State Post-App NOL', filingCode);
          const rawStateRate = parseFloat(si?.CurrentStateRate) || 0;
          const stateRate = rawStateRate > 1 ? rawStateRate / 100 : rawStateRate;
          taxExpenseSection.currentTaxExpense[filingCode] =
          cpPostAppAfterNOL * stateRate +
          cpSumFC('State Tax Effected Temp', filingCode) +
          cpSumFC('State Tax Adj. Return', filingCode) +
          cpSumFC('State Tax Adj. Provision', filingCode) +
          cpSumFC('State Tax Adj. RTP', filingCode);
        });

        // Get deferred rollforward grand total rows to pull _TaxEffected and _FBOS values.
        const { deferredRows: etrDeferredRowsTotal } =
        createDeferredRollforwardData(deferredSourceData, 'all', 'total');
        const etrTotalGrandRow = etrDeferredRowsTotal.find((row) =>
        row.AdjustmentName === 'GRAND TOTAL TEMPORARY DIFFERENCES' &&
        row.id === 'grand-total-all-total'
        );

        // Use entity-breakout grand totals (id: 'grand-total-${entityCode}') so each entity
        // only receives credit for its own deferred amounts, regardless of shared filing codes.
        const { deferredRows: etrDeferredRowsByEntity } =
        createDeferredRollforwardData(deferredSourceData, 'entity', 'total');

        // uniqueEntities here are composite keys: `${FilingCode}__${EntityCode}`
        const getGrandTotal = (compositeKey) => {
          const entityCode = String(compositeKey).split('__')[1];

          if (entityCode) {
            // Only credit the first composite key for this entity — prevents double-counting
            // when the same entity has multiple state filings (multiple composite keys).
            const firstKeyForEntity = uniqueEntities.find((k) => k.split('__')[1] === entityCode);
            if (compositeKey !== firstKeyForEntity) return null;

            const perEntity = etrDeferredRowsByEntity.find((row) =>
            row.AdjustmentName === 'GRAND TOTAL TEMPORARY DIFFERENCES' &&
            row.id === `grand-total-${entityCode}`
            );
            if (perEntity) return perEntity;
          }

          return uniqueEntities.length === 1 ? etrTotalGrandRow : null;
        };

        // Add DEFERRED TAX EXPENSES section header
        const deferredTaxExpensesHeader = {
          id: 'section-state-deferred',
          AdjustmentCode: '',
          AdjustmentName: 'DEFERRED TAX EXPENSES',
          isSection: true
        };
        uniqueEntities.forEach((fc) => {deferredTaxExpensesHeader[fc] = null;});
        pivotedRows.push(deferredTaxExpensesHeader);

        const deferredStateRateChange = {
          id: 'deferred-state-rate-change',
          AdjustmentCode: '',
          AdjustmentName: 'Deferred State Tax Rate Change',
          isSubFooter: true
        };
        uniqueEntities.forEach((fc) => {
          const gt = getGrandTotal(fc);
          deferredStateRateChange[fc] = gt?.TaxRateChange_TaxEffected || 0;
        });
        pivotedRows.push(deferredStateRateChange);

        const deferredStateRTP = {
          id: 'deferred-state-rtp',
          AdjustmentCode: '',
          AdjustmentName: 'Deferred State Return-to-Provision',
          isSubFooter: true
        };
        uniqueEntities.forEach((fc) => {
          const gt = getGrandTotal(fc);
          deferredStateRTP[fc] = gt?.ReturnToProvisionRollforward_TaxEffected || 0;
        });
        pivotedRows.push(deferredStateRTP);

        const deferredStateTrueUp = {
          id: 'deferred-state-true-up',
          AdjustmentCode: '',
          AdjustmentName: 'Deferred State True-Up',
          isSubFooter: true
        };
        uniqueEntities.forEach((fc) => {
          const gt = getGrandTotal(fc);
          deferredStateTrueUp[fc] = gt?.DeferredTrueUpRollforward_TaxEffected || 0;
        });
        pivotedRows.push(deferredStateTrueUp);

        const deferredStateWriteOff = {
          id: 'deferred-state-write-off',
          AdjustmentCode: '',
          AdjustmentName: 'Deferred State Write-off',
          isSubFooter: true
        };
        uniqueEntities.forEach((fc) => {
          const gt = getGrandTotal(fc);
          deferredStateWriteOff[fc] = gt?.WriteOff_TaxEffected || 0;
        });
        pivotedRows.push(deferredStateWriteOff);

        const deferredStateValuationAllowance = {
          id: 'deferred-state-valuation-allowance',
          AdjustmentCode: '',
          AdjustmentName: 'Deferred State Valuation Allowance',
          isSubFooter: true
        };
        uniqueEntities.forEach((fc) => {
          const gt = getGrandTotal(fc);
          deferredStateValuationAllowance[fc] = gt?.ValuationAllowance_TaxEffected || 0;
        });
        pivotedRows.push(deferredStateValuationAllowance);

        // TOTAL STATE TAX EXPENSE = current + all state tax-effected deferred rows
        const totalStateTaxExpense = {
          id: 'total-state-tax-expense',
          AdjustmentCode: '',
          AdjustmentName: 'TOTAL STATE TAX EXPENSE',
          isSubFooter: true
        };
        uniqueEntities.forEach((filingCode) => {
          const currentVal = taxExpenseSection.currentTaxExpense[filingCode] || 0;
          const deferredTotal =
          (deferredStateRateChange[filingCode] || 0) + (
          deferredStateRTP[filingCode] || 0) + (
          deferredStateTrueUp[filingCode] || 0) + (
          deferredStateWriteOff[filingCode] || 0) + (
          deferredStateValuationAllowance[filingCode] || 0);
          totalStateTaxExpense[filingCode] = currentVal + deferredTotal;
        });
        pivotedRows.push(totalStateTaxExpense);

        // Add DEFERRED FBOS TAX EXPENSES section header
        const deferredFBOSTaxExpensesHeader = {
          id: 'section-fbos-deferred',
          AdjustmentCode: '',
          AdjustmentName: 'DEFERRED FBOS TAX EXPENSES',
          isSection: true
        };
        uniqueEntities.forEach((fc) => {deferredFBOSTaxExpensesHeader[fc] = null;});
        pivotedRows.push(deferredFBOSTaxExpensesHeader);

        const deferredFBOSStateRateChange = {
          id: 'deferred-fbos-state-rate-change',
          AdjustmentCode: '',
          AdjustmentName: 'Deferred FBOS State Tax Rate Change',
          isSubFooter: true
        };
        uniqueEntities.forEach((fc) => {
          const gt = getGrandTotal(fc);
          deferredFBOSStateRateChange[fc] = gt?.TaxRateChange_FBOS || 0;
        });
        pivotedRows.push(deferredFBOSStateRateChange);

        const deferredFBOSStateRTP = {
          id: 'deferred-fbos-state-rtp',
          AdjustmentCode: '',
          AdjustmentName: 'Deferred FBOS State Return-to-Provision',
          isSubFooter: true
        };
        uniqueEntities.forEach((fc) => {
          const gt = getGrandTotal(fc);
          deferredFBOSStateRTP[fc] = gt?.ReturnToProvisionRollforward_FBOS || 0;
        });
        pivotedRows.push(deferredFBOSStateRTP);

        const deferredFBOSStateTrueUp = {
          id: 'deferred-fbos-state-true-up',
          AdjustmentCode: '',
          AdjustmentName: 'Deferred FBOS State True-Up',
          isSubFooter: true
        };
        uniqueEntities.forEach((fc) => {
          const gt = getGrandTotal(fc);
          deferredFBOSStateTrueUp[fc] = gt?.DeferredTrueUpRollforward_FBOS || 0;
        });
        pivotedRows.push(deferredFBOSStateTrueUp);

        const deferredFBOSStateWriteOff = {
          id: 'deferred-fbos-state-write-off',
          AdjustmentCode: '',
          AdjustmentName: 'Deferred FBOS State Write-off',
          isSubFooter: true
        };
        uniqueEntities.forEach((fc) => {
          const gt = getGrandTotal(fc);
          deferredFBOSStateWriteOff[fc] = gt?.WriteOff_FBOS || 0;
        });
        pivotedRows.push(deferredFBOSStateWriteOff);

        const deferredFBOSStateValuationAllowance = {
          id: 'deferred-fbos-state-valuation-allowance',
          AdjustmentCode: '',
          AdjustmentName: 'Deferred FBOS State Valuation Allowance',
          isSubFooter: true
        };
        uniqueEntities.forEach((fc) => {
          const gt = getGrandTotal(fc);
          deferredFBOSStateValuationAllowance[fc] = gt?.ValuationAllowance_FBOS || 0;
        });
        pivotedRows.push(deferredFBOSStateValuationAllowance);

        // TOTAL FBOS STATE TAX EXPENSE = sum of all FBOS deferred rows
        const totalFBOSStateTaxExpense = {
          id: 'total-fbos-state-tax-expense',
          AdjustmentCode: '',
          AdjustmentName: 'TOTAL FBOS STATE TAX EXPENSE',
          isSubFooter: true
        };
        uniqueEntities.forEach((filingCode) => {
          totalFBOSStateTaxExpense[filingCode] =
          (deferredFBOSStateRateChange[filingCode] || 0) + (
          deferredFBOSStateRTP[filingCode] || 0) + (
          deferredFBOSStateTrueUp[filingCode] || 0) + (
          deferredFBOSStateWriteOff[filingCode] || 0) + (
          deferredFBOSStateValuationAllowance[filingCode] || 0);
        });
        pivotedRows.push(totalFBOSStateTaxExpense);
      }

      // Add the final calculated total after the last section (Tax Adj. RTP)
      if (lastAdjustmentType === 'Tax Adj. RTP') {
        const totalCurrentTaxExpense = {
          id: 'calculated-total-current-tax-expense',
          AdjustmentCode: '',
          AdjustmentName: 'TOTAL CURRENT TAX EXPENSE',
          isCalculatedTotal: true
        };

        uniqueEntities.forEach((filingCode) => {
          const returnBasisTaxExpenseValue = sectionTotalsCache['Total Return Basis Current Tax Expense']?.[filingCode] || 0;
          const provisionBasisTaxAdjTotal = sectionTotalsCache['Tax Adj. provision']?.[filingCode] || 0;
          const returnToProvisionAdjTotal = currentSectionTotals[filingCode] || 0;

          totalCurrentTaxExpense[filingCode] = returnBasisTaxExpenseValue + provisionBasisTaxAdjTotal + returnToProvisionAdjTotal;
        });

        pivotedRows.push(totalCurrentTaxExpense);

        // Add total deferred tax expense row
        const totalDeferredTaxExpense = {
          id: 'calculated-total-deferred-tax-expense',
          AdjustmentCode: '',
          AdjustmentName: 'TOTAL DEFERRED TAX EXPENSE',
          isCalculatedTotal: true
        };

        const { deferredRows } = deferredBreakout1 === 'group' ?
        createDeferredRollforwardDataGroupMode(deferredSourceData, deferredBreakout2) :
        createDeferredRollforwardData(deferredSourceData, deferredBreakout1, deferredBreakout2);

        uniqueEntities.forEach((filingCode) => {
          const si = scenarioStateInputs.find((s) => s.FilingCode === filingCode);
          const entityCode = si?.EntityCode || filingCode;
          const grandTotalRow = deferredRows.find((row) =>
          row.AdjustmentName === 'GRAND TOTAL TEMPORARY DIFFERENCES' &&
          row.id === `grand-total-${entityCode}`
          );
          totalDeferredTaxExpense[filingCode] = grandTotalRow?.DeferredExpense || 0;
        });

        pivotedRows.push(totalDeferredTaxExpense);

        // Add total tax expense row
        const totalTaxExpense = {
          id: 'calculated-total-tax-expense',
          AdjustmentCode: '',
          AdjustmentName: 'TOTAL TAX EXPENSE',
          isCalculatedTotal: true
        };

        uniqueEntities.forEach((filingCode) => {
          const currentTaxExpenseValue = totalCurrentTaxExpense[filingCode] || 0;
          const deferredTaxExpenseValue = totalDeferredTaxExpense[filingCode] || 0;
          totalTaxExpense[filingCode] = currentTaxExpenseValue + deferredTaxExpenseValue;
        });

        pivotedRows.push(totalTaxExpense);
      }
    }

    // Add final TOTAL STATE CURRENT TAX EXPENSE after all sections are processed
    if (sectionTotalsCache['State Tax Adj. RTP']) {
      const totalStateCurrentTaxExpense = {
        id: 'calculated-total-state-current-tax-expense-final',
        AdjustmentCode: '',
        AdjustmentName: 'TOTAL STATE CURRENT TAX EXPENSE',
        isCalculatedTotal: true
      };

      uniqueEntities.forEach((filingCode) => {
        const totalStateReturnBasisCurrentTaxExpenseTotal = sectionTotalsCache['Total State Return Basis Current Tax Expense']?.[filingCode] || 0;
        const stateProvisionTotal = sectionTotalsCache['State Tax Adj. Provision']?.[filingCode] || 0;
        const stateRTPTotal = sectionTotalsCache['State Tax Adj. RTP']?.[filingCode] || 0;
        totalStateCurrentTaxExpense[filingCode] = totalStateReturnBasisCurrentTaxExpenseTotal + stateProvisionTotal + stateRTPTotal;
      });

      sectionTotalsCache['Total State Current Tax Expense'] = {};
      uniqueEntities.forEach((filingCode) => {
        sectionTotalsCache['Total State Current Tax Expense'][filingCode] = totalStateCurrentTaxExpense[filingCode];
      });

      // Only display this row in current-provision view, not in ETR
      if (calculationType !== 'effective-tax-rate') {
        pivotedRows.push(totalStateCurrentTaxExpense);
      }
    }

    // Add TOTAL STATE DEFERRED EXPENSE — from deferred rollforward grand total DeferredExpense_TaxEffected
    const totalStateDeferredExpense = {
      id: 'calculated-total-state-deferred-expense',
      AdjustmentCode: '',
      AdjustmentName: 'TOTAL STATE DEFERRED EXPENSE',
      isCalculatedTotal: true
    };
    {
      // Use entity-breakout grand totals (id: 'grand-total-${entityCode}') so each entity
      // only receives credit for its own deferred amounts, regardless of shared filing codes.
      const { deferredRows: _cpDeferredRowsByEntity } =
      createDeferredRollforwardData(deferredSourceData, 'entity', 'total');
      const { deferredRows: _cpDeferredRowsTotal } =
      createDeferredRollforwardData(deferredSourceData, 'all', 'total');
      const cpTotalGrandRow = _cpDeferredRowsTotal.find((row) =>
      row.AdjustmentName === 'GRAND TOTAL TEMPORARY DIFFERENCES' &&
      row.id === 'grand-total-all-total'
      );
      // uniqueEntities are composite keys: `${FilingCode}__${EntityCode}`
      uniqueEntities.forEach((compositeKey) => {
        const entityCode = String(compositeKey).split('__')[1];
        let perEntity = 0;
        if (entityCode) {
          // Only credit the first composite key for this entity — prevents double-counting
          // when the same entity has multiple state filings.
          const firstKeyForEntity = uniqueEntities.find((k) => k.split('__')[1] === entityCode);
          if (compositeKey === firstKeyForEntity) {
            const entityGrandRow = _cpDeferredRowsByEntity.find((row) =>
            row.AdjustmentName === 'GRAND TOTAL TEMPORARY DIFFERENCES' &&
            row.id === `grand-total-${entityCode}`
            );
            perEntity = entityGrandRow?.DeferredExpense_TaxEffected || 0;
          }
        }
        // If only one column exists, use the consolidated total grand row directly.
        const fallback = uniqueEntities.length === 1 ?
        cpTotalGrandRow?.DeferredExpense_TaxEffected || 0 :
        0;
        totalStateDeferredExpense[compositeKey] = perEntity || fallback;
      });
    }
    // Only display this row in current-provision view, not in ETR
    if (calculationType !== 'effective-tax-rate') {
      pivotedRows.push(totalStateDeferredExpense);
    }

    // Add TOTAL STATE TAX EXPENSE
    const totalStateTaxExpense = {
      id: 'calculated-total-state-tax-expense',
      AdjustmentCode: '',
      AdjustmentName: 'TOTAL STATE TAX EXPENSE',
      isCalculatedTotal: true
    };
    uniqueEntities.forEach((filingCode) => {
      const totalStateCurrentTaxExpenseValue = sectionTotalsCache['Total State Current Tax Expense']?.[filingCode] || 0;
      const totalStateDeferredExpenseValue = totalStateDeferredExpense[filingCode] || 0;
      totalStateTaxExpense[filingCode] = totalStateCurrentTaxExpenseValue + totalStateDeferredExpenseValue;
    });
    // Only display this row in current-provision view, not in ETR
    if (calculationType !== 'effective-tax-rate') {
      pivotedRows.push(totalStateTaxExpense);
    }

    // For ETR view, populate deferred tax expense and insert TAX EXPENSE section at the top
    if (calculationType === 'effective-tax-rate' && taxExpenseSection) {
      // This ETR row is presentation-only: source every value from the Deferred
      // Rollforward's state calculation, then let the column groupings sum those
      // same filing/entity values. This keeps Total, State, State Filing, and Entity
      // views reconciled without recalculating rates in ETR.
      const deferredExpenseForState = (sourceRows, stateCode) => {
        if (!sourceRows.length || !stateCode) return 0;
        const { deferredRows: stateDeferredRows } =
        createDeferredRollforwardData(sourceRows, 'all', 'state');
        const stateGrandTotal = stateDeferredRows.find((row) =>
        row.AdjustmentName === 'GRAND TOTAL TEMPORARY DIFFERENCES' &&
        row.id === `grand-total-all-${stateCode}`
        );
        return parseFloat(stateGrandTotal?.DeferredExpense_TaxEffected) || 0;
      };

      // Capture the exact state totals first. These are the same authoritative
      // values displayed by Deferred Rollforward when grouped by State.
      const stateDeferredExpenseByState = new Map();
      const etrStateCodes = [...new Set(
        uniqueEntities.map((key) => siForKey(key)?.StateCode).filter(Boolean)
      )];
      etrStateCodes.forEach((stateCode) => {
        const stateSIs = scenarioStateInputs.filter((si) => si.StateCode === stateCode);
        const stateFilingCodes = new Set(stateSIs.map((si) => si.FilingCode).filter(Boolean));
        const stateEntityCodes = new Set(stateSIs.map((si) => si.EntityCode).filter(Boolean));
        const stateDeferredSourceRows = deferredSourceData.filter((row) =>
        stateFilingCodes.has(row.FilingCode) || (
        !row.FilingCode && stateEntityCodes.has(row.EntityCode))
        );
        stateDeferredExpenseByState.set(
          stateCode,
          deferredExpenseForState(stateDeferredSourceRows, stateCode)
        );
      });

      // Build the lowest-level values displayed by ETR. State-specific adjustments
      // stay with their filing/entity key. Entity-level (no-filing) adjustments are
      // the residual of the correct state/entity rollforward and are placed on the
      // first filing for that state/entity, matching the rollforward's existing
      // first-applicable-StateInput behavior without duplicating the amount.
      const stateEntityGroups = new Map();
      uniqueEntities.forEach((compositeKey) => {
        const si = siForKey(compositeKey);
        if (!si?.StateCode || !si?.EntityCode) return;
        const groupKey = `${si.StateCode}__${si.EntityCode}`;
        if (!stateEntityGroups.has(groupKey)) {
          stateEntityGroups.set(groupKey, {
            stateCode: si.StateCode,
            entityCode: si.EntityCode,
            compositeKeys: []
          });
        }
        stateEntityGroups.get(groupKey).compositeKeys.push(compositeKey);
      });

      stateEntityGroups.forEach(({ stateCode, entityCode, compositeKeys }) => {
        const filingCodes = new Set(
          compositeKeys.map((key) => siForKey(key)?.FilingCode).filter(Boolean)
        );
        const stateEntitySourceRows = deferredSourceData.filter((row) =>
        row.EntityCode === entityCode && (
        filingCodes.has(row.FilingCode) || !row.FilingCode)
        );
        const stateEntityTotal = deferredExpenseForState(stateEntitySourceRows, stateCode);

        let filingSpecificTotal = 0;
        compositeKeys.forEach((compositeKey) => {
          const filingCode = siForKey(compositeKey)?.FilingCode;
          const filingSpecificRows = stateEntitySourceRows.filter((row) =>
          row.FilingCode === filingCode
          );
          const filingSpecificExpense = deferredExpenseForState(filingSpecificRows, stateCode);
          taxExpenseSection.deferredTaxExpense[compositeKey] = filingSpecificExpense;
          filingSpecificTotal += filingSpecificExpense;
        });

        if (compositeKeys.length > 0) {
          const firstCompositeKey = compositeKeys[0];
          taxExpenseSection.deferredTaxExpense[firstCompositeKey] +=
          stateEntityTotal - filingSpecificTotal;
        }
      });

      // The state rollforward is the authoritative source. Some rollforward rows
      // combine the same adjustment code across entities, so independently derived
      // state/entity pieces can have a reconciliation difference. Keep that
      // residual on the state's first underlying key so every ETR grouping adds back
      // to the exact state GRAND TOTAL TEMPORARY DIFFERENCES amount.
      const stateGroups = new Map();
      uniqueEntities.forEach((compositeKey) => {
        const si = siForKey(compositeKey);
        if (!si?.StateCode) return;
        if (!stateGroups.has(si.StateCode)) stateGroups.set(si.StateCode, []);
        stateGroups.get(si.StateCode).push(compositeKey);
      });

      stateGroups.forEach((compositeKeys, stateCode) => {
        const stateTotal = stateDeferredExpenseByState.get(stateCode) || 0;
        const allocatedTotal = compositeKeys.reduce((sum, compositeKey) =>
        sum + (taxExpenseSection.deferredTaxExpense[compositeKey] || 0), 0
        );

        if (compositeKeys.length > 0) {
          taxExpenseSection.deferredTaxExpense[compositeKeys[0]] +=
          stateTotal - allocatedTotal;
        }
      });

      uniqueEntities.forEach((filingCode) => {
        taxExpenseSection.totalTaxExpense[filingCode] =
        taxExpenseSection.currentTaxExpense[filingCode] + taxExpenseSection.deferredTaxExpense[filingCode];
      });
      pivotedRows.unshift(
        taxExpenseSection.header,
        taxExpenseSection.currentTaxExpense,
        taxExpenseSection.deferredTaxExpense,
        taxExpenseSection.totalTaxExpense
      );
    }

    annotatePivotDrilldowns(pivotedRows);

    const stateDeferredAdjustmentTypes = new Set([
      'GS Temporary',
      'Temporary',
      'State Pre-App Temp',
      'State Pre-App NOL',
      'State Pre-App Valuation Allowance',
      'State Post-App Temp',
      'State Post-App NOL',
      'State Post-App Valuation Allowance',
      'State Tax Effected Temp',
      'State Tax Effected Valuation Allowance'
    ]);
    const deferredLineageRows = deferredSourceData.filter((row) =>
    stateDeferredAdjustmentTypes.has(row.AdjustmentSource || row.AdjustmentType)
    );
    pivotedRows.forEach((row) => {
      const name = String(row.AdjustmentName || '').toUpperCase();
      if (name.includes('DEFERRED') && name.includes('EXPENSE')) {
        attachDrilldownLineage(row, deferredLineageRows);
      } else if (name.includes('TOTAL STATE TAX EXPENSE') || name.includes('TOTAL TAX EXPENSE')) {
        attachDrilldownLineage(row, [...data, ...deferredLineageRows]);
      }
    });

    return { pivotedRows, uniqueEntities };
  };

  // Filter and transform data using useMemo to prevent flickering
  const displayRows = useMemo(() => {
    let filteredRows = allRows;

    if (selectedScenarioCode && selectedScenarioCode !== 'All') {
      filteredRows = filteredRows.filter((row) => row.ScenarioCode === selectedScenarioCode);
    }

    const entityArr = Array.isArray(selectedEntity) ? selectedEntity : [];
    const groupSelections = entityArr.filter((e) => e.startsWith('[Group] '));
    const plainEntities = entityArr.filter((e) => !e.startsWith('[Group] '));
    if (entityArr.length > 0) {
      let entityCodesToFilter = [...plainEntities];
      groupSelections.forEach((sel) => {
        const groupName = sel.replace('[Group] ', '');
        const selectedGroup = entityGroups.find((g) => g.name === groupName);
        if (selectedGroup?.entityList?.length > 0) {
          const codes = selectedGroup.entityList.
          map((entityId) => masterEntityCodeById.get(entityId)).
          filter((code) => code);
          entityCodesToFilter = [...entityCodesToFilter, ...codes];
        }
      });
      if (entityCodesToFilter.length > 0) {
        filteredRows = filteredRows.filter((row) => entityCodesToFilter.includes(row.EntityCode));
      }
    }

    if (selectedTag && selectedTag !== 'All') {
      filteredRows = filteredRows.filter((row) => row.Tag === selectedTag);
    }

    // Filter by State (via FilingCode → StateCode lookup from stateInputs)
    const stateArr = Array.isArray(selectedState) ? selectedState : [];
    if (stateArr.length > 0) {
      const stateFilteredSIs = stateInputs.filter(
        (si) => stateArr.includes(si.StateCode) && (!selectedScenarioCode || si.ScenarioCode === selectedScenarioCode)
      );
      const filingCodesInStates = new Set(stateFilteredSIs.map((si) => si.FilingCode).filter(Boolean));
      const entityCodesInStates = new Set(stateFilteredSIs.map((si) => si.EntityCode).filter(Boolean));
      // Keep rows that have a matching FilingCode OR have no FilingCode but belong to an entity in those states
      filteredRows = filteredRows.filter((row) =>
      filingCodesInStates.has(row.FilingCode) ||
      !row.FilingCode && entityCodesInStates.has(row.EntityCode)
      );
    }

    // Filter by Filing Code
    const filingCodeArr = Array.isArray(selectedFilingCode) ? selectedFilingCode : [];
    if (filingCodeArr.length > 0) {
      // Determine which entity codes are actually assigned to the selected filing code(s)
      const entityCodesForFilingCodes = new Set(
        stateInputs.
        filter((si) => filingCodeArr.includes(si.FilingCode) && (!selectedScenarioCode || si.ScenarioCode === selectedScenarioCode)).
        map((si) => si.EntityCode).
        filter(Boolean)
      );
      // Keep rows that match the selected filing codes OR have no FilingCode but belong to an entity assigned to those filing codes
      filteredRows = filteredRows.filter((row) =>
      filingCodeArr.includes(row.FilingCode) ||
      !row.FilingCode && entityCodesForFilingCodes.has(row.EntityCode)
      );
    }

    // For current provision table, only show adjustments with ActivityType = 'CurrentActivityPL'
    if (calculationType === 'current-provision') {
      filteredRows = filteredRows.filter((row) => row.ActivityType === 'CurrentActivityPL');
    }

    // Handle different calculation types
    let finalRows = [];

    if (calculationType === 'deferred-rollforward') {
      // Match current provision's active-state gate: the deferred rollforward should only
      // use entities and filing/entity pairs that have StateInput records in this scenario.
      const scenarioMatchesStateInput = (si) =>
      !selectedScenarioCode || selectedScenarioCode === 'All' || si.ScenarioCode === selectedScenarioCode;
      const activeStateEntityCodes = new Set(
        stateInputs
          .filter(scenarioMatchesStateInput)
          .map((si) => si.EntityCode)
          .filter(Boolean)
      );
      const activeStateFilingEntityKeys = new Set(
        stateInputs
          .filter(scenarioMatchesStateInput)
          .map((si) => `${si.FilingCode}__${si.EntityCode}`)
          .filter(Boolean)
      );
      // Keep entity-level temporary adjustments from appearing for entities that do
      // not participate in the selected scenario's state filings.
      const DEFERRED_ENTITY_LEVEL_TYPES = new Set(['GS Temporary', 'Temporary']);
      const deferredFilteredRows = filteredRows.filter((row) => {
        if (!activeStateEntityCodes.has(row.EntityCode)) return false;
        if (row.FilingCode) {
          return activeStateFilingEntityKeys.has(`${row.FilingCode}__${row.EntityCode}`);
        }
        const adjType = row.AdjustmentSource || row.AdjustmentType;
        if (DEFERRED_ENTITY_LEVEL_TYPES.has(adjType)) {
          return activeStateEntityCodes.has(row.EntityCode);
        }
        return true;
      });
      // Create deferred rollforward table structure
      const { deferredRows } = deferredBreakout1 === 'group' ?
      createDeferredRollforwardDataGroupMode(deferredFilteredRows, deferredBreakout2) :
      createDeferredRollforwardData(deferredFilteredRows, deferredBreakout1, deferredBreakout2);
      finalRows = deferredRows;
    } else {
      // Use filteredRows instead of allRows for pivot to ensure updates are reflected.
      // For current provision, keep the table filtered to CurrentActivityPL,
      // but compute deferred totals from the unfiltered scenario data.
      const deferredSourceRows = calculationType === 'current-provision' ?
      allRows.filter((row) => {
        if (selectedScenarioCode && selectedScenarioCode !== 'All') {
          if (row.ScenarioCode !== selectedScenarioCode) return false;
        }
        const _scEntityArr = Array.isArray(selectedEntity) ? selectedEntity : [];
        const _scPlainEntities = _scEntityArr.filter((e) => !e.startsWith('[Group] '));
        if (_scPlainEntities.length > 0 && !_scPlainEntities.includes(row.EntityCode)) return false;
        if (selectedTag && selectedTag !== 'All') {
          if (row.Tag !== selectedTag) return false;
        }
        return true;
      }) :
      filteredRows;

      const { pivotedRows } = pivotData(filteredRows, deferredSourceRows);

      // Filter rows based on calculation type
      finalRows = pivotedRows;
      if (calculationType === 'effective-tax-rate') {
        // ETR view: show all rows (section headers, adjustments, and totals)
        finalRows = pivotedRows;
      }
    }

    // Collapse rows above FEDERAL TAXABLE INCOME BEFORE ADJUSTMENTS for current-provision
    if (calculationType === 'current-provision' && topRowsCollapsed) {
      const cutoffIdx = finalRows.findIndex((r) => r.id === 'calculated-taxable-income-before-adjustments');
      if (cutoffIdx > 0) {
        finalRows = finalRows.slice(cutoffIdx);
      }
    }

    return finalRows;
  }, [allRows, stateInputs, entityInputs, entityGroups, masterEntityCodeById, selectedScenarioCode, selectedEntity, selectedTag, selectedCurrency, calculationType, etrEntityGroup, etrStateGroup, deferredBreakout1, deferredBreakout2, selectedState, selectedFilingCode, topRowsCollapsed]);

  // Dialog handlers
  const handleOpenDialog = (row = defaultCalculation) => {
    setCurrentRow({ ...row });
    setEditMode(!!row.id);
    setOpenDialog(true);
  };


  // Handle cell clicks for editing adjustments - now opens full edit modal
  const handleCellClick = useCallback((params) => {
    if (params.row.isSection) return;
    if (!params.value || params.value === 0 || params.value === '-') return;

    const lineageCodes = params.row.DrilldownAdjustmentCodes;
    const adjustmentCodes = lineageCodes?.length ? lineageCodes : [params.row.AdjustmentCode];
    const selectedEntityValues = Array.isArray(selectedEntity) ? selectedEntity : [];
    const selectedPlainEntities = selectedEntityValues.filter((value) => !value.startsWith('[Group] '));
    const selectedGroupEntities = selectedEntityValues
      .filter((value) => value.startsWith('[Group] '))
      .flatMap((value) => {
        const groupName = value.replace('[Group] ', '');
        const group = entityGroups.find((candidate) => candidate.name === groupName);
        return (group?.entityList || []).map((id) => masterEntityCodeById.get(id)).filter(Boolean);
      });
    const selectedEntityCodes = [...new Set([...selectedPlainEntities, ...selectedGroupEntities])];
    const selectedStates = Array.isArray(selectedState) ? selectedState : [];
    const selectedFilingCodes = Array.isArray(selectedFilingCode) ? selectedFilingCode : [];
    const scenarioSIs = stateInputs.filter((si) => si.ScenarioCode === selectedScenarioCode);

    const stateForAdjustment = (row) =>
    scenarioSIs.find((si) => si.FilingCode === row.FilingCode && si.EntityCode === row.EntityCode)?.StateCode ||
    masterStates.find((state) => state.FilingCode === row.FilingCode)?.State;

    const isInActiveView = (row) => {
      if (selectedScenarioCode && selectedScenarioCode !== 'All' && row.ScenarioCode !== selectedScenarioCode) return false;
      if (selectedTag && selectedTag !== 'All' && row.Tag !== selectedTag) return false;
      if (selectedEntityCodes.length && !selectedEntityCodes.includes(row.EntityCode)) return false;
      if (selectedStates.length && row.FilingCode && !selectedStates.includes(stateForAdjustment(row))) return false;
      if (selectedFilingCodes.length && row.FilingCode && !selectedFilingCodes.includes(row.FilingCode)) return false;
      return true;
    };

    let matchingAdjustments;

    if (calculationType === 'deferred-rollforward') {
      const activityTypesToMatch = getDeferredActivityTypes(params.field, ACTIVITY_TYPES);
      if (!activityTypesToMatch) return;
      const lineageAdjustmentTypes = params.row.DrilldownAdjustmentTypes?.length ?
      params.row.DrilldownAdjustmentTypes :
      [params.row.AdjustmentType].filter(Boolean);
      const lineageEntityCodes = params.row.DrilldownEntityCodes?.length ?
      params.row.DrilldownEntityCodes :
      [params.row.EntityCode].filter(Boolean);
      const lineageGroupCodes = params.row.DrilldownGroupCodes || [];
      const groupEntityCodes = lineageGroupCodes.flatMap((groupName) => {
        const group = entityGroups.find((candidate) => candidate.name === groupName);
        return (group?.entityList || []).map((id) => masterEntityCodeById.get(id)).filter(Boolean);
      });
      const scopedEntityCodes = [...new Set([...lineageEntityCodes, ...groupEntityCodes])];
      const subGroups = params.row.DrilldownSubGroups || [];

      matchingAdjustments = allRows.filter((row) => {
        const rowType = row.AdjustmentSource || row.AdjustmentType;
        let sameSubGroup = true;
        if (subGroups.length) {
          sameSubGroup = subGroups.some((subGroup) => {
            const [type, key] = subGroup.split('::');
            return type === 'filing' ? row.FilingCode === key : stateForAdjustment(row) === key;
          });
        }
        return isInActiveView(row) &&
        adjustmentCodes.includes(row.AdjustmentCode) &&
        (!lineageAdjustmentTypes.length || lineageAdjustmentTypes.includes(rowType)) &&
        (!scopedEntityCodes.length || scopedEntityCodes.includes(row.EntityCode)) &&
        sameSubGroup &&
        activityTypesToMatch.includes(row.ActivityType);
      });
    } else {
      const columnKeys = params.drilldownKeys?.length ? params.drilldownKeys : [params.field];
      const columnStateInputs = columnKeys.flatMap((key) => {
        const compositeMatch = scenarioSIs.find((si) => `${si.FilingCode}__${si.EntityCode}` === key);
        return compositeMatch ? [compositeMatch] : scenarioSIs.filter((si) => si.FilingCode === key);
      });
      const columnEntities = [...new Set(columnStateInputs.map((si) => si.EntityCode).filter(Boolean))];
      const includesDeferredInputs = /DEFERRED|TOTAL (?:STATE )?TAX EXPENSE/i.test(params.row.AdjustmentName || '');

      matchingAdjustments = allRows.filter((row) => {
        if (!isInActiveView(row) || !adjustmentCodes.includes(row.AdjustmentCode)) return false;
        if (calculationType === 'current-provision' && !includesDeferredInputs && row.ActivityType !== 'CurrentActivityPL') return false;
        const rowType = row.AdjustmentSource || row.AdjustmentType;
        if (STATE_SPECIFIC_SECTION_TYPES.includes(rowType)) {
          return columnStateInputs.some((si) =>
          si.FilingCode === row.FilingCode && si.EntityCode === row.EntityCode
          );
        }
        return columnEntities.includes(row.EntityCode);
      });
    }

    if (matchingAdjustments && matchingAdjustments.length > 0) {
      // Always show table modal, even for single adjustment
      setMatchingAdjustments(matchingAdjustments);
      setSelectionDialogOpen(true);
    }
  }, [
  allRows,
  calculationType,
  entityGroups,
  masterEntityCodeById,
  masterStates,
  selectedEntity,
  selectedFilingCode,
  selectedScenarioCode,
  selectedState,
  selectedTag,
  setMatchingAdjustments,
  setSelectionDialogOpen,
  stateInputs]
  );

  // Handle editing an adjustment - opens full modal like AdjustmentInputs page
  const handleEditAdjustment = (adjustment) => {
    setEditMode(true);
    const amount = adjustment.Amount || adjustment.Value || '';







    setCurrentAdjustment({
      id: adjustment.originalId || adjustment.id,
      AdjustmentId: adjustment.AdjustmentId,
      EntityCode: adjustment.EntityCode || '',
      EntityName: adjustment.EntityName || '',
      SubEntity: adjustment.SubEntity || '',
      AdjustmentCode: adjustment.AdjustmentCode || '',
      AdjustmentName: adjustment.AdjustmentName || '',
      ActivityType: adjustment.ActivityType || '',
      Amount: amount,
      USDAmount: adjustment.USDAmount || '',
      Tag: adjustment.Tag || '',
      SubTag: adjustment.SubTag || '',
      AdjustmentType: adjustment.AdjustmentType || '',
      ScenarioCode: adjustment.ScenarioCode || selectedScenario?.scenarioCode || 'SC001',
      PointInTime: adjustment.PointInTime || selectedScenario?.PointInTime || '',
      ModifiedBy: 'admin'
    });
    setEntityName(adjustment.EntityName || '');
    setAdjustmentName(adjustment.AdjustmentName || '');
    fetchPointInTimes();
    setOpenDialog(true);
  };

  // Handle deleting an adjustment from the selection dialog
  const handleDeleteAdjustment = async (adjustment) => {
    if (window.confirm('Are you sure you want to delete this adjustment?')) {
      try {
        const id = adjustment.originalId || adjustment.id;
        const adjustmentType = adjustment.AdjustmentType;
        const routePath = adjustmentTypeToRouteMap[adjustmentType];

        if (!routePath) {
          throw new Error('No route configured for this adjustment type');
        }

        await api.delete(`/adjustments/${routePath}/${id}`);

        setNotification({
          open: true,
          message: 'Adjustment deleted successfully',
          severity: 'success'
        });

        // Close the selection dialog and refresh data
        setSelectionDialogOpen(false);
        fetchStateCalculations();
      } catch (error) {
        console.error('Error deleting adjustment:', error);
        setNotification({
          open: true,
          message: `Failed to delete adjustment: ${error.response?.data?.message || error.message}`,
          severity: 'error'
        });
      }
    }
  };

  // Fetch point in times for the dialog
  const fetchPointInTimes = async () => {
    try {
      const response = await api.get('/point-in-times');
      if (response.data && response.data.data) {
        setPointInTimes(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching point in times:', error);
    }
  };

  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const handleRequestClose = () => setConfirmCloseOpen(true);

  // Handle closing the dialog
  const handleClose = () => {
    setOpenDialog(false);
    setEditMode(false);
    setConfirmCloseOpen(false);
    setCurrentAdjustment({
      id: '',
      AdjustmentId: '',
      EntityCode: '',
      EntityName: '',
      SubEntity: '',
      AdjustmentCode: '',
      AdjustmentName: '',
      ActivityType: '',
      Amount: '',
      USDAmount: '',
      Tag: '',
      SubTag: '',
      AdjustmentType: '',
      ScenarioCode: '',
      PointInTime: '',
      ModifiedBy: 'admin'
    });
    setEntityName('');
    setAdjustmentName('');
  };

  // CRUD actions - Updated to work with full adjustment modal
  const handleSave = async () => {
    try {
      // Validate required fields
      if (!currentAdjustment.EntityCode || !currentAdjustment.AdjustmentCode) {
        setNotification({ open: true, message: 'Entity Code and Adjustment Code are required', severity: 'error' });
        return;
      }

      if (editMode) {
        // Handle full adjustment update


        const adjustmentType = currentAdjustment.AdjustmentType;
        const routePath = adjustmentTypeToRouteMap[adjustmentType];

        if (routePath && currentAdjustment.id) {
          // Prepare update data for backend
          const updateData = {
            EntityCode: currentAdjustment.EntityCode,
            AdjustmentCode: currentAdjustment.AdjustmentCode,
            Amount: parseFloat(currentAdjustment.Amount) || 0,
            USDAmount: parseFloat(currentAdjustment.USDAmount) || 0,
            ActivityType: currentAdjustment.ActivityType,
            Tag: currentAdjustment.Tag,
            SubTag: currentAdjustment.SubTag,
            SubEntity: currentAdjustment.SubEntity,
            ScenarioCode: currentAdjustment.ScenarioCode,
            PointInTime: currentAdjustment.PointInTime,
            ModifiedBy: currentAdjustment.ModifiedBy,
            AdjustmentId: currentAdjustment.AdjustmentId
          };



          // Make backend API call
          await api.put(`/adjustments/${routePath}/${currentAdjustment.id}`, updateData);

          // Refresh the calculations data
          await fetchStateCalculations();

          setNotification({ open: true, message: 'Adjustment updated successfully', severity: 'success' });
        } else {
          setNotification({ open: true, message: 'Could not update adjustment - missing route or ID', severity: 'error' });
        }
      }

      handleClose();
    } catch (error) {
      console.error('Error saving adjustment:', error);
      setNotification({ open: true, message: `Failed to save adjustment: ${error.message}`, severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    try {
      setRows(rows.filter((row) => row.id !== id));
      setNotification({ open: true, message: 'Tax calculation deleted successfully', severity: 'success' });
    } catch (error) {
      setNotification({ open: true, message: 'Failed to delete tax calculation', severity: 'error' });
    }
  };

  // Remove the renderCell function as it's no longer needed with the new structure

  // Dynamic columns with entities as headers
  const columns = useMemo(() => {
    if (calculationType === 'deferred-rollforward') {
      // Create columns for deferred rollforward view
      const deferredColumns = [
      {
        field: 'AdjustmentName',
        headerName: 'Adjustment Name',
        minWidth: 400,
        maxWidth: 400,
        flex: 1,
        renderCell: (params) => {
          if (params.row.isSection) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#424242',
                color: 'white',
                padding: '8px',
                width: '100%',
                borderRadius: '4px'
              }}>
                  {params.formattedValue}
                </Box>);

          }
          if (params.row.isSubFooter) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '8px',
                width: '100%',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right'
              }}>
                  {params.formattedValue}
                </Box>);

          }
          // For regular adjustment rows, show EntityCode + [StatePrefix] + AdjustmentCode + AdjustmentName
          return (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              gap: 1
            }}>
                {params.row.EntityCode &&
              <Box sx={{
                textAlign: 'left',
                fontWeight: 'bold',
                minWidth: 'fit-content',
                flexShrink: 0
              }}>
                    {params.row.EntityCode}
                  </Box>
              }
                {params.row.AdjustmentNamePrefix &&
              <Box sx={{
                textAlign: 'left',
                fontWeight: 'bold',
                color: '#1976d2',
                minWidth: 'fit-content',
                flexShrink: 0
              }}>
                    {params.row.AdjustmentNamePrefix.replace(' - ', '')}
                  </Box>
              }
                <Box sx={{
                textAlign: 'left',
                fontWeight: 'bold',
                minWidth: 'fit-content',
                flexShrink: 0
              }}>
                  {params.row.AdjustmentCode}
                </Box>
                <Box sx={{
                textAlign: 'left',
                flex: 1
              }}>
                  {params.value}
                </Box>
              </Box>);

        }
      }];


      // Add regular activity type columns (first set)
      ACTIVITY_TYPES.forEach((activityType) => {
        let headerName;
        if (activityType === 'DeferredTrueUp') {
          headerName = 'DEFERRED TRUE UP (PRE-APPORTIONED)';
        } else if (activityType === 'AdjBeginningBalance') {
          headerName = 'ADJ. BEGINNING BALANCE (PRE-APPORTIONED)';
        } else if (activityType === 'ReturnToProvision') {
          headerName = 'RETURN TO PROVISION (PRE-APPORTIONED)';
        } else {
          headerName = `${activityType.replace(/([A-Z])/g, ' $1').trim().toUpperCase()} (PRE-APPORTIONED)`;
        }

        deferredColumns.push({
          field: activityType,
          headerName: headerName,
          minWidth: 120,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              // In effective tax rate view, make sub-footers clickable if they have values
              if (calculationType === 'effective-tax-rate' && params.value && params.value !== 0 && params.value !== '-') {
                return (
                  <Box sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    borderTop: '1px solid #ccc',
                    textAlign: 'right',
                    width: '100%',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => handleCellClick(params)}>

                    {params.formattedValue}
                  </Box>);

              }
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Make non-zero values clickable for editing
            if (params.value && params.value !== 0 && params.value !== '-') {
              return (
                <Box
                  sx={{
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    textAlign: 'right',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => handleCellClick(params)}>

                  {params.formattedValue}
                </Box>);

            }
            return params.formattedValue;
          }
        });
      });

      // Add CTA Adjustment column (only show when currency is USD)
      if (selectedCurrency !== 'LC') {
        deferredColumns.push({
          field: 'CTAAdjustment',
          headerName: 'CTA ADJUSTMENT (PRE-APPORTIONED)',
          minWidth: 160,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Make non-zero values clickable for editing
            if (params.value && params.value !== 0 && params.value !== '-') {
              return (
                <Box
                  sx={{
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    textAlign: 'right',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => handleCellClick(params)}>

                  {params.formattedValue}
                </Box>);

            }
            return params.formattedValue;
          }
        });
      }

      // Add Ending Balance column
      deferredColumns.push({
        field: 'EndingBalance',
        headerName: 'ENDING BALANCE (PRE-APPORTIONED)',
        minWidth: 160,
        type: 'number',
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === 0) return '-';
          return formatRoundedNumberWithParentheses(params.value);
        },
        renderCell: (params) => {
          // Don't show values for section headers
          if (params.row.isSection) {
            return null;
          }
          // Style sub-footer totals
          if (params.row.isSubFooter) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Style calculated totals
          if (params.row.isCalculatedTotal) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Make non-zero values clickable for editing
          if (params.value && params.value !== 0 && params.value !== '-') {
            return (
              <Box
                sx={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  textAlign: 'right',
                  width: '100%',
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }
                }}
                onClick={() => handleCellClick(params)}>

                {params.formattedValue}
              </Box>);

          }
          return params.formattedValue;
        }
      });

      // Add post-apportioned activity type columns (second set - same as pre-apportioned)
      ACTIVITY_TYPES.forEach((activityType) => {
        let headerName;
        if (activityType === 'DeferredTrueUp') {
          headerName = 'DEFERRED TRUE UP (POST-APPORTIONED)';
        } else if (activityType === 'AdjBeginningBalance') {
          headerName = 'ADJ. BEGINNING BALANCE (POST-APPORTIONED)';
        } else if (activityType === 'ReturnToProvision') {
          headerName = 'RETURN TO PROVISION (POST-APPORTIONED)';
        } else {
          headerName = `${activityType.replace(/([A-Z])/g, ' $1').trim().toUpperCase()} (POST-APPORTIONED)`;
        }

        deferredColumns.push({
          field: `${activityType}_PostApportioned`,
          headerName: headerName,
          minWidth: 120,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Make non-zero values clickable for editing
            if (params.value && params.value !== 0 && params.value !== '-') {
              return (
                <Box
                  sx={{
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    textAlign: 'right',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => handleCellClick(params)}>

                  {params.formattedValue}
                </Box>);

            }
            return params.formattedValue;
          }
        });
      });

      // Add CTA Adjustment (POST-APPORTIONED) column (only show when currency is USD)
      if (selectedCurrency !== 'LC') {
        deferredColumns.push({
          field: 'CTAAdjustment_PostApportioned',
          headerName: 'CTA ADJUSTMENT (POST-APPORTIONED)',
          minWidth: 160,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Make non-zero values clickable for editing
            if (params.value && params.value !== 0 && params.value !== '-') {
              return (
                <Box
                  sx={{
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    textAlign: 'right',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => handleCellClick(params)}>

                  {params.formattedValue}
                </Box>);

            }
            return params.formattedValue;
          }
        });
      }

      // Add Ending Balance (POST-APPORTIONED) column
      deferredColumns.push({
        field: 'EndingBalance_PostApportioned',
        headerName: 'ENDING BALANCE (POST-APPORTIONED)',
        minWidth: 160,
        type: 'number',
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === 0) return '-';
          return formatRoundedNumberWithParentheses(params.value);
        },
        renderCell: (params) => {
          // Don't show values for section headers
          if (params.row.isSection) {
            return null;
          }
          // Style sub-footer totals
          if (params.row.isSubFooter) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Style calculated totals
          if (params.row.isCalculatedTotal) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Make non-zero values clickable for editing
          if (params.value && params.value !== 0 && params.value !== '-') {
            return (
              <Box
                sx={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  textAlign: 'right',
                  width: '100%',
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }
                }}
                onClick={() => handleCellClick(params)}>

                {params.formattedValue}
              </Box>);

          }
          return params.formattedValue;
        }
      });

      // Add state tax-effected activity type columns (third set)
      ACTIVITY_TYPES.forEach((activityType) => {
        let headerName;
        if (activityType === 'AdjBeginningBalance') {
          headerName = 'ADJ. BEGINNING BALANCE (STATE TAX-EFFECTED)';
        } else {
          headerName = `${activityType.replace(/([A-Z])/g, ' $1').trim().toUpperCase()} (STATE TAX-EFFECTED)`;
        }

        deferredColumns.push({
          field: `${activityType}_TaxEffected`,
          headerName: headerName,
          minWidth: 140,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Make non-zero values clickable for editing (drills down to underlying adjustments
            // via handleCellClick, which strips the _TaxEffected suffix to resolve the ActivityType)
            if (params.value && params.value !== 0 && params.value !== '-') {
              return (
                <Box
                  sx={{
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    textAlign: 'right',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => handleCellClick(params)}>

                  {params.formattedValue}
                </Box>);

            }
            // Regular values
            return params.formattedValue;
          }
        });
      });

      // Add Tax Rate Change State Tax-Effected column
      deferredColumns.push({
        field: 'TaxRateChange_TaxEffected',
        headerName: 'TAX RATE CHANGE (STATE TAX-EFFECTED)',
        minWidth: 180,
        type: 'number',
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === 0) return '-';
          return formatRoundedNumberWithParentheses(params.value);
        },
        renderCell: (params) => {
          // Don't show values for section headers
          if (params.row.isSection) {
            return null;
          }
          // Style sub-footer totals
          if (params.row.isSubFooter) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Style calculated totals
          if (params.row.isCalculatedTotal) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Regular values
          return params.formattedValue;
        }
      });

      // Add CTA Adjustment State Tax-Effected column (only show when currency is USD)
      if (selectedCurrency !== 'LC') {
        deferredColumns.push({
          field: 'CTAAdjustment_TaxEffected',
          headerName: 'CTA ADJUSTMENT (STATE TAX-EFFECTED)',
          minWidth: 180,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Regular values
            return params.formattedValue;
          }
        });
      }

      // Add Ending Balance State Tax-Effected column
      deferredColumns.push({
        field: 'EndingBalance_TaxEffected',
        headerName: 'ENDING BALANCE (STATE TAX-EFFECTED)',
        minWidth: 180,
        type: 'number',
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === 0) return '-';
          return formatRoundedNumberWithParentheses(params.value);
        },
        renderCell: (params) => {
          // Don't show values for section headers
          if (params.row.isSection) {
            return null;
          }
          // Style sub-footer totals
          if (params.row.isSubFooter) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Style calculated totals
          if (params.row.isCalculatedTotal) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Make non-zero values clickable for editing (drills down to all contributing
          // activity types for this adjustment/entity/section via handleCellClick)
          if (params.value && params.value !== 0 && params.value !== '-') {
            return (
              <Box
                sx={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  textAlign: 'right',
                  width: '100%',
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }
                }}
                onClick={() => handleCellClick(params)}>

                {params.formattedValue}
              </Box>);

          }
          return params.formattedValue;
        }
      });

      // Add FBOS activity type columns (fourth set - same as pre-apportioned and post-apportioned)
      ACTIVITY_TYPES.forEach((activityType) => {
        let headerName;
        if (activityType === 'DeferredTrueUp') {
          headerName = 'DEFERRED TRUE UP (FBOS)';
        } else if (activityType === 'AdjBeginningBalance') {
          headerName = 'ADJ. BEGINNING BALANCE (FBOS)';
        } else if (activityType === 'ReturnToProvision') {
          headerName = 'RETURN TO PROVISION (FBOS)';
        } else {
          headerName = `${activityType.replace(/([A-Z])/g, ' $1').trim().toUpperCase()} (FBOS)`;
        }

        deferredColumns.push({
          field: `${activityType}_FBOS`,
          headerName: headerName,
          minWidth: 120,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Make non-zero values clickable for editing
            if (params.value && params.value !== 0 && params.value !== '-') {
              return (
                <Box
                  sx={{
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    textAlign: 'right',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => handleCellClick(params)}>

                  {params.formattedValue}
                </Box>);

            }
            return params.formattedValue;
          }
        });
      });

      // Add CTA Adjustment (FBOS) column (only show when currency is USD)
      if (selectedCurrency !== 'LC') {
        deferredColumns.push({
          field: 'CTAAdjustment_FBOS',
          headerName: 'CTA ADJUSTMENT (FBOS)',
          minWidth: 160,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Make non-zero values clickable for editing
            if (params.value && params.value !== 0 && params.value !== '-') {
              return (
                <Box
                  sx={{
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    textAlign: 'right',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => handleCellClick(params)}>

                  {params.formattedValue}
                </Box>);

            }
            return params.formattedValue;
          }
        });
      }

      // Add Ending Balance (FBOS) column
      deferredColumns.push({
        field: 'EndingBalance_FBOS',
        headerName: 'ENDING BALANCE (FBOS)',
        minWidth: 160,
        type: 'number',
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === 0) return '-';
          return formatRoundedNumberWithParentheses(params.value);
        },
        renderCell: (params) => {
          // Don't show values for section headers
          if (params.row.isSection) {
            return null;
          }
          // Style sub-footer totals
          if (params.row.isSubFooter) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Style calculated totals
          if (params.row.isCalculatedTotal) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Make non-zero values clickable for editing
          if (params.value && params.value !== 0 && params.value !== '-') {
            return (
              <Box
                sx={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  textAlign: 'right',
                  width: '100%',
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }
                }}
                onClick={() => handleCellClick(params)}>

                {params.formattedValue}
              </Box>);

          }
          return params.formattedValue;
        }
      });

      // Add new calculated columns - now with STATE TAX-EFFECTED and FBOS versions
      const newColumns = [
      'DeferredExpense',
      'TaxRateChange',
      'ReturnToProvision',
      'DeferredTrueUpFinal',
      'CurrentActivity',
      'WriteOff',
      'ValuationAllowance'];


      // Add STATE TAX-EFFECTED versions first
      newColumns.forEach((columnName) => {
        // Determine the header name
        let headerName;
        let fieldName;
        if (columnName === 'DeferredTrueUpFinal') {
          headerName = 'DEFERRED TRUE UP (STATE TAX-EFFECTED)';
          fieldName = 'DeferredTrueUpRollforward_TaxEffected';
        } else if (columnName === 'ReturnToProvision') {
          headerName = 'RETURN TO PROVISION (STATE TAX-EFFECTED)';
          fieldName = 'ReturnToProvisionRollforward_TaxEffected';
        } else if (columnName === 'TaxRateChange') {
          // TaxRateChange already has a _TaxEffected version, skip to avoid duplicate
          return;
        } else {
          headerName = columnName.replace(/([A-Z])/g, ' $1').trim().toUpperCase().replace(/TO/g, 'TO').replace(/UP/g, 'UP').replace(/OFF/g, 'OFF') + ' (STATE TAX-EFFECTED)';
          fieldName = columnName + '_TaxEffected';
        }

        deferredColumns.push({
          field: fieldName,
          headerName: headerName,
          minWidth: 180,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Make non-zero values clickable for editing
            if (params.value && params.value !== 0 && params.value !== '-') {
              return (
                <Box
                  sx={{
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    textAlign: 'right',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => handleCellClick(params)}>

                  {params.formattedValue}
                </Box>);

            }
            return params.formattedValue;
          }
        });
      });

      // Add FBOS versions
      newColumns.forEach((columnName) => {
        // Determine the header name
        let headerName;
        let fieldName;
        if (columnName === 'DeferredTrueUpFinal') {
          headerName = 'DEFERRED TRUE UP (FBOS)';
          fieldName = 'DeferredTrueUpRollforward_FBOS';
        } else if (columnName === 'ReturnToProvision') {
          headerName = 'RETURN TO PROVISION (FBOS)';
          fieldName = 'ReturnToProvisionRollforward_FBOS';
        } else {
          headerName = columnName.replace(/([A-Z])/g, ' $1').trim().toUpperCase().replace(/TO/g, 'TO').replace(/UP/g, 'UP').replace(/OFF/g, 'OFF') + ' (FBOS)';
          fieldName = columnName + '_FBOS';
        }

        deferredColumns.push({
          field: fieldName,
          headerName: headerName,
          minWidth: 180,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Make non-zero values clickable for editing
            if (params.value && params.value !== 0 && params.value !== '-') {
              return (
                <Box
                  sx={{
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    textAlign: 'right',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => handleCellClick(params)}>

                  {params.formattedValue}
                </Box>);

            }
            return params.formattedValue;
          }
        });
      });

      return enableTotalCellDrilldown(deferredColumns, handleCellClick);
    }

    if (stateInputs.length === 0 && !hasCalculationRows) {
      return [
      { field: 'AdjustmentCode', headerName: 'Adjustment Code', minWidth: 140 },
      { field: 'AdjustmentName', headerName: 'Adjustment Name', width: 200 }];

    }

    // Build unique filing codes from stateInputs for the selected scenario
    const scenarioSIs = stateInputs.filter(
      (si) => si.ScenarioCode === selectedScenarioCode
    );
    // Helper: resolve a composite key back to its StateInput record
    const scenarioSIMapByCompositeKey = new Map(
      scenarioSIs.map((si) => [`${si.FilingCode}__${si.EntityCode}`, si])
    );
    const siForKey = (key) => scenarioSIMapByCompositeKey.get(key);
    const masterStateEntityCountByFilingCode = new Map();
    masterStates.forEach((ms) => {
      if (!ms.FilingCode) return;
      const entitySet = masterStateEntityCountByFilingCode.get(ms.FilingCode) || new Set();
      (ms.entityList || []).forEach((entityId) => entitySet.add(entityId));
      masterStateEntityCountByFilingCode.set(ms.FilingCode, entitySet);
    });
    // Unique keys: FilingCode__EntityCode composites to handle same filing code across multiple entities
    let uniqueEntities = scenarioSIs.
    map((si) => `${si.FilingCode}__${si.EntityCode}`).
    filter((k, i, arr) => k && arr.indexOf(k) === i);

    // No fallback: if the scenario has no StateInputs, no columns should be generated.
    // The table will be empty, signaling that state inputs need to be configured.


    // Apply entity filter: filter filing code columns to only those belonging to the selected entities
    const _colEntityArr = Array.isArray(selectedEntity) ? selectedEntity : [];
    const _colPlainEntities = _colEntityArr.filter((e) => !e.startsWith('[Group] '));
    const _colGroupSelections = _colEntityArr.filter((e) => e.startsWith('[Group] '));
    if (_colEntityArr.length > 0) {
      let allowedEntityCodes = [..._colPlainEntities];
      _colGroupSelections.forEach((sel) => {
        const groupName = sel.replace('[Group] ', '');
        const selectedGroup = entityGroups.find((g) => g.name === groupName);
        if (selectedGroup?.entityList?.length > 0) {
          const groupEntityCodes = selectedGroup.entityList.
          map((entityId) => masterEntityCodeById.get(entityId)).
          filter((code) => code);
          allowedEntityCodes = [...allowedEntityCodes, ...groupEntityCodes];
        }
      });
      if (allowedEntityCodes.length > 0) {
        uniqueEntities = uniqueEntities.filter((fc) => {
          const si = siForKey(fc);
          return si && allowedEntityCodes.includes(si.EntityCode);
        });
      }
    }

    // Apply state filter: filter filing code columns to only those belonging to selected states
    const _colStateArr = Array.isArray(selectedState) ? selectedState : [];
    if (_colStateArr.length > 0) {
      uniqueEntities = uniqueEntities.filter((fc) => {
        const si = siForKey(fc);
        return si && _colStateArr.includes(si.StateCode);
      });
    }

    // Apply filing code filter: further restrict columns to selected filing codes
    const _colFilingArr = Array.isArray(selectedFilingCode) ? selectedFilingCode : [];
    if (_colFilingArr.length > 0) {
      uniqueEntities = uniqueEntities.filter((fc) => {
        const si = siForKey(fc);
        return si && _colFilingArr.includes(si.FilingCode);
      });
    }

    // Create base columns for adjustment info
    const baseColumns = [
    {
      field: 'AdjustmentName',
      headerName: 'Adjustment Name',
      minWidth: 400,
      maxWidth: 400,
      flex: 1,
      renderCell: (params) => {
        if (params.row.isSection) {
          return (
            <Box sx={{
              fontWeight: 'bold',
              backgroundColor: '#424242',
              color: 'white',
              padding: '8px',
              width: '100%',
              borderRadius: '4px'
            }}>
              {params.formattedValue}
            </Box>);

        }
        if (params.row.isSubFooter) {
          return (
            <Box sx={{
              fontWeight: 'bold',
              backgroundColor: '#f5f5f5',
              padding: '8px',
              width: '100%',
              borderRadius: '4px',
              borderTop: '1px solid #ccc',
              textAlign: 'right'
            }}>
              {params.formattedValue}
            </Box>);

        }
        if (params.row.isCalculatedTotal) {
          const isTaxableIncomRow = calculationType === 'current-provision' && params.row.id === 'calculated-taxable-income-before-adjustments';
          return (
            <Box
              onClick={isTaxableIncomRow ? () => setTopRowsCollapsed((prev) => !prev) : undefined}
              sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '8px',
                width: '100%',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: params.row.AdjustmentName === 'CURRENT TAX RATE PERCENTAGE' ? 'left' : 'right',
                ...(isTaxableIncomRow && {
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 1,
                  '&:hover': { backgroundColor: '#e8e8e8' }
                })
              }}>

              {isTaxableIncomRow &&
              <Box component="span" sx={{ fontSize: '12px', color: '#666', userSelect: 'none' }}>
                  {topRowsCollapsed ? '▶' : '▼'}
                </Box>
              }
              {params.formattedValue}
            </Box>);

        }

        // For regular adjustment rows in current provision mode, show code + name with custom layout
        if (calculationType === 'current-provision' && params.row.AdjustmentCode) {
          return (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              gap: 1
            }}>
              <Box sx={{
                textAlign: 'left',
                fontWeight: 'bold',
                minWidth: 'fit-content',
                flexShrink: 0
              }}>
                {params.row.AdjustmentCode}
              </Box>
              <Box sx={{
                textAlign: 'left',
                flex: 1
              }}>
                {params.value}
              </Box>
            </Box>);

        }

        return params.value;
      }
    }];

    // Add Total Consolidated column before entity columns (only if there are multiple entities)
    if (uniqueEntities.length > 1) {
      baseColumns.push({
        field: 'TotalConsolidated',
        drilldownKeys: uniqueEntities,
        headerName: 'Total Consolidated',
        minWidth: 180,
        type: 'number',
        valueGetter: (params) => {
          // Don't calculate for section headers
          if (params.row.isSection) {
            return null;
          }

          // Special handling for apportionment rate row:
          // compute weighted rate as sum(StatePostApportionedIncome) / sum(StatePreAppIncomeAfterNOL)
          if (params.row && params.row.AdjustmentName === 'CURRENT APPORTIONMENT RATE PERCENTAGE') {
            let preTotal = 0;
            let postTotal = 0;
            const scale = parseFloat(params.row[`${uniqueEntities[0]}_Scale`]) || 1;
            uniqueEntities.forEach((fc) => {
              preTotal += parseFloat(params.row[`${fc}_PreApp`]) || 0;
              postTotal += parseFloat(params.row[`${fc}_Post`]) || 0;
            });
            if (preTotal === 0) return null;
            return postTotal / preTotal * scale;
          }

          // Calculate total of all entity values for this row
          let total = 0;
          uniqueEntities.forEach((colKey) => {
            const entityValue = params.row[colKey];
            if (entityValue && typeof entityValue === 'number') {
              total += entityValue;
            }
          });

          return total === 0 ? null : total;
        },
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined) return '-';

          // Apportionment Rate row — display as raw decimal exactly as input (xx.xxxxxx)
          if (params.row && params.row.AdjustmentName === 'CURRENT APPORTIONMENT RATE PERCENTAGE') {
            const numValue = parseFloat(params.value) || 0;
            return numValue.toFixed(6);
          }

          // Special formatting for Current Tax Rate row - already formatted as string
          if (params.row && params.row.AdjustmentName === 'CURRENT TAX RATE PERCENTAGE') {
            // If it's already a string with %, return as is
            if (typeof params.value === 'string' && params.value.includes('%')) {
              return params.value;
            }
            // Otherwise format it
            const numValue = parseFloat(params.value) || 0;
            return `${numValue.toFixed(2)}%`;
          }

          // General fallback: if value is already a percentage string, return as-is
          if (typeof params.value === 'string' && params.value.includes('%')) {
            return params.value;
          }

          if (params.value === 0) return '-';

          return formatRoundedNumberWithParentheses(params.value);
        },
        renderCell: (params) => {
          // Don't show values for section headers
          if (params.row.isSection) {
            return null;
          }
          // Style sub-footer totals
          if (params.row.isSubFooter) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
              {params.formattedValue}
            </Box>);

          }
          // Style calculated totals
          if (params.row.isCalculatedTotal) {
            const isApportionmentRate = params.row.AdjustmentName === 'CURRENT APPORTIONMENT RATE PERCENTAGE';
            const displayValue = isApportionmentRate && params.value != null ?
            (parseFloat(params.value) || 0).toFixed(6) :
            params.formattedValue;
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: params.row.AdjustmentName === 'CURRENT TAX RATE PERCENTAGE' ? 'left' : 'right',
                width: '100%'
              }}>
              {displayValue}
            </Box>);

          }
          // Regular cells - not clickable for Total Consolidated column
          return (
            <Box sx={{
              padding: '4px 8px',
              borderRadius: '4px',
              textAlign: 'right',
              width: '100%'
            }}>
            {params.formattedValue}
          </Box>);

        }
      });
    }

    // Handler to toggle group expansion
    const toggleGroupExpansion = (groupKey) => {
      setExpandedGroups((prev) => ({
        ...prev,
        [groupKey]: !prev[groupKey]
      }));
    };

    // Derive effective grouping mode from the two selectors
    // all+total → 'all-total', all+state → 'state', all+state-filing → 'fcode-entity'
    // entity+total → 'entity', entity+state → (future), entity+state-filing → 'filing-code'
    const groupingMode = (() => {
      if (calculationType !== 'current-provision' && calculationType !== 'effective-tax-rate') return 'filing-code';
      if (etrEntityGroup === 'all') {
        if (etrStateGroup === 'total') return 'all-total';
        if (etrStateGroup === 'state') return 'state';
        return 'fcode-entity'; // state-filing
      } else {
        // entity
        if (etrStateGroup === 'total') return 'entity';
        if (etrStateGroup === 'state') return 'entity-state';
        return 'filing-code'; // state-filing
      }
    })();

    // Determine grouping based on groupingMode for current-provision and effective-tax-rate
    let columnGroups = [];
    if ((calculationType === 'current-provision' || calculationType === 'effective-tax-rate') && groupingMode === 'entity-state') {
      // Entity + State: columns are State-Entity pairs (e.g. "PA - 101"), expandable to filing codes
      const entityStateMap = {};
      uniqueEntities.forEach((colKey) => {
        const si = siForKey(colKey);
        const stateCode = si?.StateCode || 'Unknown';
        const entityCode = si?.EntityCode || 'Unknown';
        const pairKey = `${stateCode} - ${entityCode}`;
        if (!entityStateMap[pairKey]) entityStateMap[pairKey] = [];
        entityStateMap[pairKey].push(colKey);
      });

      Object.entries(entityStateMap).forEach(([pairLabel, colKeys]) => {
        const pairParentKey = `es-parent-${pairLabel}`;
        const isExpanded = expandedGroups[pairParentKey];
        const isExpandable = colKeys.length > 1;

        if (isExpandable) {
          columnGroups.push({
            groupKey: pairParentKey,
            groupLabel: pairLabel,
            filingCodes: colKeys,
            level: 'entityState',
            isExpandable: true,
            isExpanded
          });
        }

        if (!isExpandable || isExpanded) {
          colKeys.forEach((colKey) => {
            const si = siForKey(colKey);
            const filingCode = si?.FilingCode || colKey;
            const label = isExpandable ? `  ${filingCode}` : pairLabel;
            columnGroups.push({
              groupKey: colKey,
              groupLabel: label,
              filingCodes: [colKey],
              level: 'filingCode',
              parentKey: isExpandable ? pairParentKey : null,
              isExpandable: false
            });
          });
        }
      });
    } else if ((calculationType === 'current-provision' || calculationType === 'effective-tax-rate') && groupingMode === 'all-total') {
      // All + Total: single consolidated column summing all filing codes
      columnGroups.push({
        groupKey: 'consolidated-total',
        groupLabel: 'Total',
        filingCodes: uniqueEntities,
        level: 'total',
        isExpandable: false
      });
    } else if ((calculationType === 'current-provision' || calculationType === 'effective-tax-rate') && groupingMode === 'state') {
      // State Breakout: State → FilingCode (expandable if ≥2 entities) → EntityCode
      // Group composite keys by StateCode → FilingCode
      const stateGroups = {};
      uniqueEntities.forEach((colKey) => {
        const si = siForKey(colKey);
        const stateCode = si?.StateCode || 'Unknown';
        const filingCode = si?.FilingCode || colKey;
        if (!stateGroups[stateCode]) stateGroups[stateCode] = {};
        if (!stateGroups[stateCode][filingCode]) stateGroups[stateCode][filingCode] = [];
        stateGroups[stateCode][filingCode].push(colKey);
      });

      Object.entries(stateGroups).forEach(([stateCode, filingCodesMap]) => {
        const allStateColKeys = Object.values(filingCodesMap).flat();
        const stateParentKey = `state-parent-${stateCode}`;
        const stateExpanded = expandedGroups[stateParentKey];

        // State summary column (always expandable — states always have at least one filing code)
        columnGroups.push({
          groupKey: stateParentKey,
          groupLabel: stateCode,
          filingCodes: allStateColKeys,
          level: 'state',
          isExpandable: true,
          isExpanded: stateExpanded
        });

        if (stateExpanded) {
          Object.entries(filingCodesMap).forEach(([filingCode, colKeys]) => {
            // Determine if this filing code is expandable (same logic as fcode-entity)
            const entityListLength = Math.max(
              masterStateEntityCountByFilingCode.get(filingCode)?.size || 0,
              colKeys.length
            );
            const fcodeExpandable = entityListLength >= 2;
            const fcodeParentKey = `state-fce-parent-${stateCode}-${filingCode}`;
            const fcodeExpanded = expandedGroups[fcodeParentKey];

            if (fcodeExpandable) {
              // FilingCode summary column
              columnGroups.push({
                groupKey: fcodeParentKey,
                groupLabel: `  ${filingCode}`,
                filingCodes: colKeys,
                level: 'fcodeParent',
                parentKey: stateParentKey,
                isExpandable: true,
                isExpanded: fcodeExpanded
              });
            }

            // Individual entity columns — shown when not expandable, or when expanded
            if (!fcodeExpandable || fcodeExpanded) {
              colKeys.forEach((colKey) => {
                const si = siForKey(colKey);
                const entityCode = si?.EntityCode || '';
                const label = fcodeExpandable ?
                `    ${entityCode}` :
                `  ${filingCode} (${entityCode})`;
                columnGroups.push({
                  groupKey: colKey,
                  groupLabel: label,
                  filingCodes: [colKey],
                  level: 'filingCode',
                  parentKey: fcodeExpandable ? fcodeParentKey : stateParentKey,
                  isExpandable: false
                });
              });
            }
          });
        }
      });
    } else if ((calculationType === 'current-provision' || calculationType === 'effective-tax-rate') && groupingMode === 'fcode-entity') {
      // Fcode Entity Breakout: same individual columns as Filing Code breakout, but filing codes
      // whose MasterState has ≥2 entities get a collapsible parent summary column inserted before them.
      // Single-entity filing codes appear flat (no parent, not expandable).

      // Group composite keys by their raw FilingCode
      const fcodeGroups = {};
      uniqueEntities.forEach((colKey) => {
        const si = siForKey(colKey);
        const filingCode = si?.FilingCode || colKey;
        if (!fcodeGroups[filingCode]) fcodeGroups[filingCode] = [];
        fcodeGroups[filingCode].push(colKey);
      });

      Object.entries(fcodeGroups).forEach(([filingCode, colKeys]) => {
        // Determine expandability: union entityLists across ALL MasterState records with this FilingCode,
        // then also consider the actual number of entity columns present in the data.
        const entityListLength = Math.max(
          masterStateEntityCountByFilingCode.get(filingCode)?.size || 0,
          colKeys.length
        );
        const isExpandable = entityListLength >= 2;
        const parentKey = `fce-parent-${filingCode}`;
        const isExpanded = expandedGroups[parentKey];

        if (isExpandable) {
          // Insert a parent summary column (summing all entity columns for this filing code)
          columnGroups.push({
            groupKey: parentKey,
            groupLabel: filingCode,
            filingCodes: colKeys,
            level: 'fcodeParent',
            isExpandable: true,
            isExpanded
          });
        }

        // Always add individual entity columns (hidden when parent is collapsed and expandable)
        if (!isExpandable || isExpanded) {
          colKeys.forEach((colKey) => {
            const si = siForKey(colKey);
            const stateCode = si?.StateCode || '';
            const entityCode = si?.EntityCode || '';
            const label = isExpandable ?
            `  ${entityCode}` // indented under parent
            : stateCode ? `${filingCode} (${stateCode} - ${entityCode})` : filingCode;
            columnGroups.push({
              groupKey: colKey,
              groupLabel: label,
              filingCodes: [colKey],
              level: 'filingCode',
              parentKey: isExpandable ? parentKey : null,
              isExpandable: false
            });
          });
        }
      });
    } else if ((calculationType === 'current-provision' || calculationType === 'effective-tax-rate') && groupingMode === 'entity') {
      // Entity Breakout: Entity → State → Filing Code hierarchy
      const entityGroupsMap = {};
      uniqueEntities.forEach((colKey) => {
        const si = siForKey(colKey);
        const entityCode = si?.EntityCode || 'Unknown';
        const stateCode = si?.StateCode || 'Unknown';

        if (!entityGroupsMap[entityCode]) {
          entityGroupsMap[entityCode] = {};
        }
        if (!entityGroupsMap[entityCode][stateCode]) {
          entityGroupsMap[entityCode][stateCode] = [];
        }
        entityGroupsMap[entityCode][stateCode].push(colKey);
      });

      // Build hierarchical column structure
      Object.entries(entityGroupsMap).forEach(([entity, states]) => {
        const entityFilingCodes = Object.values(states).flat();

        // Add entity-level column
        columnGroups.push({
          groupKey: entity,
          groupLabel: entity,
          filingCodes: entityFilingCodes,
          level: 'entity',
          isExpandable: true,
          isExpanded: expandedGroups[entity]
        });

        // If entity is expanded, add state-level columns
        if (expandedGroups[entity]) {
          Object.entries(states).forEach(([state, colKeys]) => {
            const stateKey = `${entity}-${state}`;

            columnGroups.push({
              groupKey: stateKey,
              groupLabel: `  ${state}`,
              filingCodes: colKeys,
              level: 'state',
              parentKey: entity,
              isExpandable: true,
              isExpanded: expandedGroups[stateKey]
            });

            // If state is expanded, add filing code columns
            if (expandedGroups[stateKey]) {
              colKeys.forEach((colKey) => {
                const si = siForKey(colKey);
                columnGroups.push({
                  groupKey: colKey,
                  groupLabel: `    ${si?.FilingCode || colKey}`,
                  filingCodes: [colKey],
                  level: 'filingCode',
                  parentKey: stateKey,
                  isExpandable: false
                });
              });
            }
          });
        }
      });
    } else {
      // Default: individual filing code columns
      columnGroups = uniqueEntities.map((colKey) => ({
        groupKey: colKey,
        groupLabel: (() => {
          const si = siForKey(colKey);
          const stateCode = si?.StateCode || '';
          const entityCode = si?.EntityCode || '';
          const filingCode = si?.FilingCode || colKey;
          return stateCode ? `${filingCode} (${stateCode} - ${entityCode})` : filingCode;
        })(),
        filingCodes: [colKey],
        level: 'filingCode',
        isExpandable: false
      }));
    }

    // Add columns based on grouping
    columnGroups.forEach((group) => {
      baseColumns.push({
        field: group.groupKey,
        drilldownKeys: group.filingCodes,
        headerName: group.isExpandable ?
        `${group.isExpanded ? '▼' : '▶'} ${group.groupLabel}` :
        group.groupLabel,
        minWidth: 150,
        type: 'number',
        headerClassName: group.isExpandable ? 'expandable-header' : '',
        renderHeader: (params) => {
          if (group.isExpandable) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  '&:hover': {
                    color: '#1976d2'
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleGroupExpansion(group.groupKey);
                }}>

                <span style={{ marginRight: '4px' }}>
                  {group.isExpanded ? '▼' : '▶'}
                </span>
                {group.groupLabel}
              </Box>);

          }
          return <span>{group.groupLabel}</span>;
        },
        valueGetter: (params) => {
          // Special handling for apportionment rate row when column groups multiple filing codes:
          // compute weighted rate as sum(StatePostApportionedIncome) / sum(StatePreAppIncomeAfterNOL)
          if (
          params.row &&
          params.row.AdjustmentName === 'CURRENT APPORTIONMENT RATE PERCENTAGE' &&
          group.filingCodes.length > 1)
          {
            let preTotal = 0;
            let postTotal = 0;
            const scale = parseFloat(params.row[`${group.filingCodes[0]}_Scale`]) || 1;
            group.filingCodes.forEach((fc) => {
              preTotal += parseFloat(params.row[`${fc}_PreApp`]) || 0;
              postTotal += parseFloat(params.row[`${fc}_Post`]) || 0;
            });
            if (preTotal === 0) return null;
            return postTotal / preTotal * scale;
          }
          // For grouped columns (state or entity), sum all filing codes in the group
          if (group.filingCodes.length > 1) {
            let total = 0;
            group.filingCodes.forEach((fc) => {
              const value = params.row[fc];
              if (value && typeof value === 'number') {
                total += value;
              }
            });
            return total === 0 ? null : total;
          } else {
            // Single filing code, return its value directly
            return params.row[group.filingCodes[0]];
          }
        },
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined) return '-';

          // Special formatting for Current Tax Rate row - don't show for grouped columns
          if (params.row && params.row.AdjustmentName === 'CURRENT TAX RATE PERCENTAGE') {
            // For grouped columns (state or entity), don't show rate
            if (group.filingCodes.length > 1) {
              return '-';
            }
            // If it's already a string with %, return as is
            if (typeof params.value === 'string' && params.value.includes('%')) {
              return params.value;
            }
            // Otherwise format it
            const numValue = parseFloat(params.value) || 0;
            return `${numValue.toFixed(2)}%`;
          }

          // Apportionment Rate row — display as raw decimal exactly as input (xx.xxxxxx)
          if (params.row && params.row.AdjustmentName === 'CURRENT APPORTIONMENT RATE PERCENTAGE') {
            if (params.value === null || params.value === undefined) return '-';
            const numValue = parseFloat(params.value) || 0;
            return numValue.toFixed(6);
          }

          // Special formatting for State Tax Rate row - don't show for grouped columns
          if (params.row && params.row.AdjustmentName === 'CURRENT STATE TAX RATE PERCENTAGE') {
            // For grouped columns (state or entity), don't show rate
            if (group.filingCodes.length > 1) {
              return '-';
            }
            // If it's already a string with %, return as is
            if (typeof params.value === 'string' && params.value.includes('%')) {
              return params.value;
            }
            // Otherwise format it
            const numValue = parseFloat(params.value) || 0;
            return `${numValue.toFixed(2)}%`;
          }

          // General fallback: if value is already a percentage string, return as-is
          if (typeof params.value === 'string' && params.value.includes('%')) {
            return params.value;
          }

          if (params.value === 0) return '-';

          return formatRoundedNumberWithParentheses(params.value);
        },
        renderCell: (params) => {
          // Don't show values for section headers
          if (params.row.isSection) {
            return null;
          }
          // Style sub-footer totals
          if (params.row.isSubFooter) {
            // In effective tax rate view, make sub-footers clickable if they have values
            if (calculationType === 'effective-tax-rate' && params.value && params.value !== 0 && params.value !== '-') {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }
                }}
                onClick={() => handleCellClick(params)}>

                  {params.formattedValue}
                </Box>);

            }
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Style calculated totals
          if (params.row.isCalculatedTotal) {
            const isApportionmentRate = params.row.AdjustmentName === 'CURRENT APPORTIONMENT RATE PERCENTAGE';
            // params.row is not available in valueFormatter (MUI DataGrid v6), so apply
            // the apportionment rate decimal formatting here where params.row is accessible.
            const displayValue = isApportionmentRate && params.value != null ?
            (parseFloat(params.value) || 0).toFixed(6) :
            params.formattedValue;
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: params.row.AdjustmentName === 'CURRENT TAX RATE PERCENTAGE' ? 'left' : 'right',
                width: '100%'
              }}>
                {displayValue}
              </Box>);

          }
          // Make non-zero values clickable for editing (only for individual filing codes, not grouped)
          if (params.value && params.value !== 0 && params.value !== '-' && group.filingCodes.length === 1) {
            return (
              <Box
                sx={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  textAlign: 'right',
                  width: '100%',
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }
                }}
                onClick={() => handleCellClick(params)}>

                {params.formattedValue}
              </Box>);

          }
          // For grouped columns, show non-clickable values
          if (params.value && params.value !== 0 && params.value !== '-') {
            return (
              <Box sx={{
                padding: '4px 8px',
                borderRadius: '4px',
                textAlign: 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          return params.formattedValue;
        }
      });
    });

    return enableTotalCellDrilldown(baseColumns, handleCellClick);
  }, [hasCalculationRows, masterEntityCodeById, entityGroups, masterStates, stateInputs, selectedCurrency, calculationType, selectedScenarioCode, selectedEntity, etrEntityGroup, etrStateGroup, expandedGroups, deferredBreakout1, deferredBreakout2, selectedState, selectedFilingCode, topRowsCollapsed, handleCellClick]);

  return (
    <div className="entity-inputs-container">
      <div className="main-content">
        <div className="toolbar-container">
          <Typography className="page-title" variant="h6">Tax Calculations</Typography>
          <div className="button-group">
            {(calculationType === 'current-provision' || calculationType === 'effective-tax-rate') &&
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: '8px 16px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                border: '1px solid #e0e0e0'
              }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5, color: '#666' }}>
                    Entity Group
                  </Typography>
                  <RadioGroup
                  row
                  value={etrEntityGroup}
                  onChange={(e) => setEtrEntityGroup(e.target.value)}>

                    <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
                    <FormControlLabel value="entity" control={<Radio size="small" />} label="Entity" />
                  </RadioGroup>
                </Box>
                <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: '8px 16px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                border: '1px solid #e0e0e0'
              }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5, color: '#666' }}>
                    State Group
                  </Typography>
                  <RadioGroup
                  row
                  value={etrStateGroup}
                  onChange={(e) => setEtrStateGroup(e.target.value)}>

                    <FormControlLabel value="total" control={<Radio size="small" />} label="Total" />
                    <FormControlLabel value="state" control={<Radio size="small" />} label="State" />
                    <FormControlLabel value="state-filing" control={<Radio size="small" />} label="State Filing" />
                  </RadioGroup>
                </Box>
              </Box>
            }
            {calculationType === 'deferred-rollforward' &&
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: '8px 16px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                border: '1px solid #e0e0e0'
              }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5, color: '#666' }}>
                    Entity Grouping
                  </Typography>
                  <RadioGroup
                  row
                  value={deferredBreakout1}
                  onChange={(e) => setDeferredBreakout1(e.target.value)}>

                    <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
                    <FormControlLabel value="entity" control={<Radio size="small" />} label="Entity Breakout" />
                    <FormControlLabel value="group" control={<Radio size="small" />} label="Group" />
                  </RadioGroup>
                </Box>
                <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: '8px 16px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                border: '1px solid #e0e0e0'
              }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5, color: '#666' }}>
                    State Grouping
                  </Typography>
                  <RadioGroup
                  row
                  value={deferredBreakout2}
                  onChange={(e) => setDeferredBreakout2(e.target.value)}>

                    <FormControlLabel value="total" control={<Radio size="small" />} label="Total" />
                    <FormControlLabel value="state" control={<Radio size="small" />} label="State Breakout" />
                    <FormControlLabel value="filing" control={<Radio size="small" />} label="Filing Breakout" />
                  </RadioGroup>
                </Box>
              </Box>
            }
            {/* Export 
              <Button
               variant="outlined"
               size="small"
               startIcon={<DownloadIcon />}
               className="action-button"
               onClick={exportToExcel}
               disabled={rows.length === 0}
              >
               Export
              </Button>
              */}
          </div>
        </div>
        <div className="table-container">
          {loading && <LinearProgress />}
          <EnhancedDataTable
            rows={displayRows}
            columns={columns}
            loading={loading}
            tableName="taxCalculations"
            height="100%"
            className="custom-datagrid"
            disableColumnSorting={true}
            columnHeaderHeight={calculationType === 'current-provision' || calculationType === 'effective-tax-rate' ? 50 : 90}
            pinnedColumns={{ right: ['actions'], left: ['AdjustmentName'] }}
            getRowClassName={(params) => {
              return params.row.isSection ? 'section-header' : '';
            }} />

        </div>
      </div>
      
      {/* Edit Adjustment Dialog */}
      <UnsavedChangesDialog
        open={confirmCloseOpen}
        onConfirm={handleClose}
        onCancel={() => setConfirmCloseOpen(false)} />

      <Dialog
        open={openDialog}
        onClose={handleRequestClose}
        maxWidth="md"
        fullWidth>

        <DialogTitle>{editMode ? 'Edit Adjustment Input' : 'Add New Adjustment Input'}</DialogTitle>
        <DialogContent className="dialog-content">
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={[...masterEntities].sort((a, b) => (a.EntityCode || '').localeCompare(b.EntityCode || ''))}
                getOptionLabel={(option) => option.EntityCode || ''}
                loading={entityLoading}
                value={masterEntities.find((e) => e.EntityCode === currentAdjustment.EntityCode) || null}
                onChange={(event, newValue) => {
                  setCurrentAdjustment({
                    ...currentAdjustment,
                    EntityCode: newValue?.EntityCode || '',
                    EntityName: newValue?.EntityName || ''
                  });
                  setEntityName(newValue?.EntityName || '');
                }}
                renderInput={(params) =>
                <TextField
                  {...params}
                  label="Entity Code"
                  required
                  autoComplete="off" />

                } />

            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Entity Name"
                fullWidth
                value={entityName}
                InputProps={{ readOnly: true }}
                autoComplete="off"
                disabled />

            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sub Entity"
                fullWidth
                value={currentAdjustment.SubEntity}
                onChange={(e) => setCurrentAdjustment({ ...currentAdjustment, SubEntity: e.target.value })}
                autoComplete="off" />

            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={[...masterAdjustments].sort((a, b) => (a.AdjustmentCode || '').localeCompare(b.AdjustmentCode || ''))}
                getOptionLabel={(option) => option.AdjustmentCode || ''}
                loading={adjustmentLoading}
                value={masterAdjustments.find((a) => a.AdjustmentCode === currentAdjustment.AdjustmentCode) || null}
                onChange={(event, newValue) => {
                  setCurrentAdjustment({
                    ...currentAdjustment,
                    AdjustmentCode: newValue?.AdjustmentCode || '',
                    AdjustmentName: newValue?.AdjustmentName || ''
                  });
                  setAdjustmentName(newValue?.AdjustmentName || '');
                }}
                renderInput={(params) =>
                <TextField
                  {...params}
                  label="Adjustment Code"
                  required
                  autoComplete="off" />

                } />

            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Adjustment Name"
                fullWidth
                value={adjustmentName}
                InputProps={{ readOnly: true }}
                disabled
                autoComplete="off" />

            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Activity Type</InputLabel>
                <Select
                  value={currentAdjustment.ActivityType}
                  label="Activity Type"
                  onChange={(e) => setCurrentAdjustment({ ...currentAdjustment, ActivityType: e.target.value })}>

                  {getActivityTypeOptions(currentAdjustment.AdjustmentType).map((option) =>
                  <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Local Currency Amount"
                type="text"
                fullWidth
                autoComplete="off"
                value={formatNumberWithCommas(currentAdjustment.Amount)}
                onChange={(e) => handleAmountChange(e.target.value)}
                required
                helperText={(() => {
                  const fxRate = getFXRateForEntity(currentAdjustment.EntityCode, currentAdjustment.ActivityType);
                  const isPretax = currentAdjustment.AdjustmentType === 'Pretax' || currentAdjustment.AdjustmentType === 'PretaxAdjustment';
                  if (isPretax) {
                    return 'Pretax adjustments: Manual entry only (no auto-conversion)';
                  }
                  return fxRate ? `FX Rate: ${fxRate.toFixed(6)} (USD auto-calculated)` : 'Select entity to enable auto-conversion';
                })()} />

            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="USD Amount"
                type="text"
                fullWidth
                autoComplete="off"
                value={formatNumberWithCommas(currentAdjustment.USDAmount)}
                onChange={(e) => handleUSDAmountChange(e.target.value)}
                helperText={(() => {
                  const fxRate = getFXRateForEntity(currentAdjustment.EntityCode, currentAdjustment.ActivityType);
                  const isPretax = currentAdjustment.AdjustmentType === 'Pretax' || currentAdjustment.AdjustmentType === 'PretaxAdjustment';
                  if (isPretax) {
                    return 'Pretax adjustments: Manual entry only (no auto-conversion)';
                  }
                  return fxRate ? `FX Rate: ${fxRate.toFixed(6)} (Local auto-calculated)` : 'Select entity to enable auto-conversion';
                })()} />

            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="tag-label">Tag</InputLabel>
                <Select
                  labelId="tag-label"
                  value={currentAdjustment.Tag || ''}
                  label="Tag"
                  onChange={(e) => setCurrentAdjustment({ ...currentAdjustment, Tag: e.target.value })}>

                  {/* Show current value if it exists but isn't in options */}
                  {currentAdjustment.Tag && !TAG_OPTIONS.includes(currentAdjustment.Tag) &&
                  <MenuItem key={currentAdjustment.Tag} value={currentAdjustment.Tag}>
                      {currentAdjustment.Tag} (Current)
                    </MenuItem>
                  }
                  {TAG_OPTIONS.map((option) =>
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="subtag-label">Sub Tag</InputLabel>
                <Select
                  labelId="subtag-label"
                  value={currentAdjustment.SubTag || ''}
                  label="Sub Tag"
                  onChange={(e) => setCurrentAdjustment({ ...currentAdjustment, SubTag: e.target.value })}>

                  {/* Show current value if it exists but isn't in options */}
                  {currentAdjustment.SubTag && !SUBTAG_OPTIONS.includes(currentAdjustment.SubTag) &&
                  <MenuItem key={currentAdjustment.SubTag} value={currentAdjustment.SubTag}>
                      {currentAdjustment.SubTag} (Current)
                    </MenuItem>
                  }
                  {SUBTAG_OPTIONS.map((option) =>
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Point In Time</InputLabel>
                <Select
                  value={currentAdjustment.PointInTime}
                  label="Point In Time"
                  onChange={(e) => setCurrentAdjustment({ ...currentAdjustment, PointInTime: e.target.value })}>

                  {pointInTimes.map((pit) =>
                  <MenuItem key={pit.PointInTime} value={pit.PointInTime}>
                      {pit.PointInTimeName}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Scenario Code"
                fullWidth
                value={currentAdjustment.ScenarioCode}
                autoComplete="off"
                InputProps={{ readOnly: true }}
                disabled />

            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!currentAdjustment.EntityCode || !currentAdjustment.AdjustmentCode}>

            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Selection Dialog for Multiple Adjustments */}
      <Dialog
        open={selectionDialogOpen}
        onClose={() => setSelectionDialogOpen(false)}
        maxWidth="xl"
        fullWidth>

        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
          Select Adjustment to Edit or Delete
          <IconButton
            onClick={() => setSelectionDialogOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}>

            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Multiple adjustments exist for this cell. Click on a row to edit, or use the action buttons:
          </Typography>
          <Box sx={{ height: 500, width: '100%' }}>
            <SearchableDataTable
              searchPlaceholder="Search adjustments..."
              rows={matchingAdjustments.map((adj, index) => ({
                ...adj,
                id: adj.id || `adj-${index}`
              }))}
              columns={getSelectionDialogColumns(
                (row) => {
                  setSelectionDialogOpen(false);
                  handleEditAdjustment(row);
                },
                (row) => {
                  handleDeleteAdjustment(row);
                }
              )}
              loading={false}
              pageSize={10}
              tableName="adjustment-selection-dialog"
              height="100%"
              onRowClick={(params) => {
                setSelectionDialogOpen(false);
                handleEditAdjustment(params.row);
              }} />

          </Box>
        </DialogContent>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar open={notification.open} autoHideDuration={4000} onClose={() => setNotification({ ...notification, open: false })}>
        <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </div>);

};

export default StateCalculations;
